import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { trialService } from '../../services/trialService';
import { cropService } from '../../services/cropService';
import { seasonService } from '../../services/seasonService';
import { officerService } from '../../services/officerService';
import { locationService } from '../../services/locationService';
import { locationManagementService } from '../../services/locationManagementService';
import { observationService } from '../../services/observationService';
import { trialCentresService } from '../../services/trialCentresService';
import { trialAssignmentService } from '../../services/trialAssignmentService';
import officerAssignmentsService from '../../services/officerAssignmentsService';
import { trialDataEntryApiService } from '../../services/trialDataEntryApiService';
import { locationTrialDetailsService } from '../../services/locationTrialDetailsService';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  FileText,
  Filter,
  Calendar,
  Sprout,
  MapPin,
  Eye,
  Users,
  Minus,
  CheckCircle,
  X,
  Camera,
  Upload,
  Database
} from 'lucide-react';

const TrialCreation = ({ officerSource }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDetailView = (searchParams.get('view') === 'detail') && !!searchParams.get('id');
  const isLocationsView = (searchParams.get('view') === 'locations') && !!searchParams.get('id');
  const detailId = searchParams.get('id') ? Number(searchParams.get('id')) : null;
  const [trials, setTrials] = useState([]);
  const [currentTrial, setCurrentTrial] = useState(null);
  const [isLoadingTrial, setIsLoadingTrial] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Dynamic observations state
  const [dynamicObservations, setDynamicObservations] = useState([]);
  const [isLoadingObservations, setIsLoadingObservations] = useState(false);


  // Load crops from API
  const loadCrops = async () => {
    try {
      setIsLoadingCrops(true);
      const response = await cropService.getCrops();
      if (response.success && response.data) {
        setDynamicCrops(response.data);
        // toast.success('Crops loaded successfully');
      } else {
        throw new Error(response.message || 'Failed to load crops');
      }
    } catch (error) {
      console.warn('Failed to load crops from API:', error.message);
      toast.error('Failed to load crops from API, using fallback data');
      // Fallback to hardcoded crops
      setDynamicCrops(crops);
    } finally {
      setIsLoadingCrops(false);
    }
  };

  // Load seasons from API
  const loadSeasons = async () => {
    try {
      setIsLoadingSeasons(true);
      const response = await seasonService.getSeasons();
      if (response.success && response.data) {
        setDynamicSeasons(response.data);
        // toast.success('Seasons loaded successfully');
      } else {
        throw new Error(response.message || 'Failed to load seasons');
      }
    } catch (error) {
      console.warn('Failed to load seasons from API:', error.message);
      toast.error('Failed to load seasons from API, using fallback data');
      // Fallback to hardcoded seasons
      setDynamicSeasons(seasons);
    } finally {
      setIsLoadingSeasons(false);
    }
  };

  // Load scientists/officers from API
  const loadScientists = async () => {
    try {
      setIsLoadingScientists(true);
      const response = await officerService.getOfficers();
      if (response.success && response.data) {
        setDynamicScientists(response.data);
        // toast.success('Scientists loaded successfully');
      } else {
        throw new Error(response.message || 'Failed to load scientists');
      }
    } catch (error) {
      console.warn('Failed to load scientists from API:', error.message);
      toast.error('Failed to load scientists from API, using fallback data');
      // Fallback to hardcoded scientists
      setDynamicScientists(scientists.map(name => ({ id: name, name: name })));
    } finally {
      setIsLoadingScientists(false);
    }
  };

  // Load centres from API
  const loadCentres = async () => {
    try {
      setIsLoadingCentres(true);
      const response = await locationService.getCentres();
      if (response.success && response.data) {
        setDynamicCentres(response.data);
        // toast.success('Centres loaded successfully');
      } else {
        throw new Error(response.message || 'Failed to load centres');
      }
    } catch (error) {
      console.warn('Failed to load centres from API:', error.message);
      toast.error('Failed to load centres from API, using fallback data');
      // Fallback to hardcoded centres
      setDynamicCentres(centres.map(name => ({ id: name, name: name })));
    } finally {
      setIsLoadingCentres(false);
    }
  };

  // Load officers from API (for centre incharge)
  const loadOfficers = async () => {
    try {
      setIsLoadingOfficers(true);
      const response = await officerService.getOfficers();
      if (response.success && response.data) {
        setDynamicOfficers(response.data);
        // toast.success('Officers loaded successfully');
      } else {
        throw new Error(response.message || 'Failed to load officers');
      }
    } catch (error) {
      console.warn('Failed to load officers from API:', error.message);
      toast.error('Failed to load officers from API, using fallback data');
      // Fallback to hardcoded officers
      setDynamicOfficers(officers.map(name => ({ id: name, name: name })));
    } finally {
      setIsLoadingOfficers(false);
    }
  };

  // Load observations from API
  const loadObservations = async () => {
    try {
      setIsLoadingObservations(true);
      const response = await observationService.getObservations();
      if (response.success) {
        // Extract observation types from the response
        const observationTypes = (response.data || []).map(obs => obs.observation_type || obs.name || obs);
        setDynamicObservations(observationTypes);
        // toast.success('Observations loaded successfully');
      } else {
        throw new Error(response.message || 'Failed to load observations');
      }
    } catch (error) {
      console.warn('Failed to load observations from API:', error.message);
      toast.error('Failed to load observations from API, using fallback data');
      // Fallback to hardcoded observations
      setDynamicObservations([
        'Germination %',
        'Germination Vigour',
        'Population',
        'Vigour',
        'Germination DAS',
        'Plant height',
        'Plant width',
        'No. of branches'
      ]);
    } finally {
      setIsLoadingObservations(false);
    }
  };

  // Load trial assignments from API
  const loadTrialAssignments = async (trialId) => {
    if (!trialId) return;
    
    try {
      setIsLoadingAssignments(true);
      const response = await trialAssignmentService.getTrialAssignments(trialId);
      if (response.success) {
        setTrialAssignments(response.data || []);
        console.log('✅ Trial assignments loaded:', response.data);
      } else {
        throw new Error(response.message || 'Failed to load trial assignments');
      }
    } catch (error) {
      console.warn('Failed to load trial assignments:', error.message);
      setTrialAssignments([]);
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  // Create trial assignment
  const createTrialAssignment = async (trialId, assignmentData) => {
    try {
      const response = await trialAssignmentService.createTrialAssignment(trialId, assignmentData);
      if (response.success) {
        await loadTrialAssignments(trialId);
        toast.success('Trial assignment created successfully');
        return true;
      } else {
        throw new Error(response.message || 'Failed to create trial assignment');
      }
    } catch (error) {
      console.error('Error creating trial assignment:', error);
      toast.error(error.message || 'Failed to create trial assignment');
      return false;
    }
  };

  // Delete trial assignment
  const deleteTrialAssignment = async (trialId, assignmentId) => {
    try {
      const response = await trialAssignmentService.deleteTrialAssignment(trialId, assignmentId);
      if (response.success) {
        await loadTrialAssignments(trialId);
        toast.success('Trial assignment deleted successfully');
        return true;
      } else {
        throw new Error(response.message || 'Failed to delete trial assignment');
      }
    } catch (error) {
      console.error('Error deleting trial assignment:', error);
      toast.error(error.message || 'Failed to delete trial assignment');
      return false;
    }
  };

  // Load centre assignments for a specific centre
  const loadCentreAssignments = async (centreId) => {
    try {
      setIsLoadingAssignments(true);
      
      const response = await trialCentresService.getTrialCentreAssignments(centreId);
      
      if (response.success && response.data) {
        setCentreAssignments(response.data);
        console.log('✅ Centre assignments loaded:', response.data);
      } else {
        setCentreAssignments([]);
      }
    } catch (error) {
      console.error('❌ Failed to load centre assignments:', error);
      setCentreAssignments([]);
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  // Load single trial centre by ID
  const loadTrialCentreById = async (centreId) => {
    try {
      const response = await trialCentresService.getTrialCentre(centreId);
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to load centre');
      }
    } catch (error) {
      console.error('Error loading trial centre:', error);
      throw error;
    }
  };

  // Load trial centres from API
  const loadTrialCentres = async (page = 1, limit = 10) => {
    try {
      setIsLoadingTrialCentres(true);
      
      // If in detail view, filter by trial ID
      const params = {
        page,
        limit
      };
      
      if (isDetailView && detailId) {
        params.trial_id = detailId;
      }
      
      const response = await trialCentresService.getTrialCentres(params);
      
      if (response.success) {
        setTrialCentres(response.data || []);
        // Also refresh global centres list for unified counter
        try {
          const allResponse = await trialCentresService.getTrialCentres({ page: 1, limit: 100000 });
          if (allResponse.success) {
            setAllCentresForCounter(allResponse.data || []);
          }
        } catch (e) {
          // Non-blocking: ignore errors in global fetch
        }
        setTrialCentresPagination(response.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0
        });
        console.log('✅ Trial centres loaded from API:', response);
        // toast.success(`Loaded ${response.data?.length || 0} trial centres from API`);
      } else {
        throw new Error(response.message || 'Failed to load trial centres');
      }
    } catch (error) {
      console.warn('Failed to load trial centres from API:', error.message);
      // Fallback to mock data
      const mockTrialCentres = [
        {
          id: 1,
          trial_id: null,
          centre_id: "TNMDU13-TE",
          centre_name: "TEST",
          centre_code: "TC-COI-01",
          centre_incharge: "Dr. S. Kumar",
          from_date: "2025-01-15T00:00:00.000Z",
          status: "active",
          created_at: "2025-09-08T10:31:46.863Z",
          updated_at: "2025-09-08T10:31:46.863Z"
        },
        {
          id: 2,
          trial_id: 101,
          centre_id: "TNMDU14-RE",
          centre_name: "RESEARCH CENTRE",
          centre_code: "RC-SAL-02",
          centre_incharge: "Dr. Priya Sharma",
          from_date: "2025-02-01T00:00:00.000Z",
          status: "active",
          created_at: "2025-09-08T10:31:46.863Z",
          updated_at: "2025-09-08T10:31:46.863Z"
        },
        {
          id: 3,
          trial_id: 102,
          centre_id: "TNMDU15-DE",
          centre_name: "DEMO CENTRE",
          centre_code: "DC-ERO-03",
          centre_incharge: "Dr. Amit Patel",
          from_date: "2025-01-20T00:00:00.000Z",
          status: "inactive",
          created_at: "2025-09-08T10:31:46.863Z",
          updated_at: "2025-09-08T10:31:46.863Z"
        }
      ];
      setTrialCentres(mockTrialCentres);
      setTrialCentresPagination({
        page: 1,
        limit: 10,
        total: mockTrialCentres.length,
        pages: 1
      });
      console.log('📋 Using mock trial centres data');
      toast.info(`Using mock data: ${mockTrialCentres.length} trial centres`);
    } finally {
      setIsLoadingTrialCentres(false);
    }
  };

  // Load specific trial by ID
  const loadCurrentTrial = async (trialId) => {
    if (!trialId) return;
    
    try {
      setIsLoadingTrial(true);
      const response = await trialService.getTrial(trialId);
      if (response.success) {
        setCurrentTrial(response.data);
        console.log('Loaded trial details:', response.data);
      } else {
        throw new Error(response.message || 'Failed to load trial details');
      }
    } catch (error) {
      console.error('Failed to load trial details:', error.message);
      toast.error('Failed to load trial details');
      setCurrentTrial(null);
    } finally {
      setIsLoadingTrial(false);
    }
  };

  // Load location management data for a specific trial centre
  const loadLocationManagement = async (trialCentreId, page = 1, limit = 10) => {
    if (!trialCentreId) return;
    
    try {
      setIsLoadingLocationManagement(true);
      const response = await locationManagementService.getLocationManagement(trialCentreId, {
        page,
        limit,
        officer_id:searchParams.get('officer_id')
      });
      
      if (response.success && response.data) {
        setLocationManagementData(response.data);
        if (response.pagination) {
          setLocationManagementPagination(response.pagination);
        }
        console.log('✅ Location management data loaded:', response.data);
        console.log('✅ Pagination info:', response.pagination);
      } else {
        console.warn('⚠️ Failed to load location management data:', response.message);
        setLocationManagementData([]);
        setLocationManagementPagination({ page: 1, limit: 10, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('❌ Error loading location management data:', error);
      setLocationManagementData([]);
      setLocationManagementPagination({ page: 1, limit: 10, total: 0, pages: 0 });
    } finally {
      setIsLoadingLocationManagement(false);
    }
  };

  // Load trials from API with fallback to localStorage
  const loadTrials = async (page = currentPage, limit = pageSize) => {
    try {
        let user = localStorage.getItem('user');
        if(user){
            user = JSON.parse(user);
        }
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      const params = {
        page: page,
        limit: limit
      };
      
      const response = await officerAssignmentsService.getTrialAssignments(user.id, params);
      if (response.success) {
        // Use the trials data directly from the response structure
        const trialsData = response.data.trials || [];
        
        setTrials(trialsData);
        
        // Update pagination state from API response
        if (response.data.pagination) {
          setCurrentPage(response.data.pagination.page || 1);
          setPageSize(response.data.pagination.limit || 10);
          setTotalRecords(response.data.pagination.total || 0);
          setTotalPages(response.data.pagination.pages || 1);
        } else {
          // Fallback if no pagination data
          setCurrentPage(1);
          setPageSize(trialsData.length);
          setTotalRecords(trialsData.length);
          setTotalPages(1);
        }
      } else {
        throw new Error(response.message || 'Failed to load trial assignments');
      }
    } catch (error) {
      console.warn('API failed, falling back to localStorage:', error.message);
      // Fallback to localStorage
    try {
      const savedTrials = window.localStorage.getItem('trials');
      if (savedTrials) {
        const parsed = JSON.parse(savedTrials);
        if (Array.isArray(parsed)) {
          setTrials(parsed);
          // Reset pagination for fallback data
          setCurrentPage(1);
          setTotalRecords(parsed.length);
          setTotalPages(1);
        }
      }
      } catch {
        // If no saved trials, use empty array
        setTrials([]);
        setCurrentPage(1);
        setTotalRecords(0);
        setTotalPages(0);
      }
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadTrials(newPage, pageSize);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    loadTrials(1, newPageSize);
  };

  // Persist trials so opening detail view in a new tab can access them
  useEffect(() => {
    try {
      window.localStorage.setItem('trials', JSON.stringify(trials));
    } catch {}
  }, [trials]);

  // On mount, load trials from API with fallback to localStorage
  useEffect(() => {
    loadTrials();
    loadCrops();
    loadSeasons();
    loadScientists();
    loadObservations();
    
    // Load centres and officers when detail view or locations view is active
    if (isDetailView || isLocationsView) {
      loadCentres();
      loadOfficers();
      loadTrialCentres(); // Load trial centres data
    }
  }, [isDetailView, isLocationsView]);


// Load officer trial centres when component mounts
useEffect(() => {
  let user = localStorage.getItem('user');
  if(user){
    user = JSON.parse(user);
  }
  if (user?.id) {
    loadOfficerTrialCentres();
  }
}, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentCulture, setAssignmentCulture] = useState('');
  const [assignmentReplications, setAssignmentReplications] = useState(1);
  const [assignmentObservations, setAssignmentObservations] = useState([]);
  const [observationQuery, setObservationQuery] = useState('');
  const [assignmentObservationDays, setAssignmentObservationDays] = useState({});
  const [assignmentCentre, setAssignmentCentre] = useState('');
  const [assignmentCentreCode, setAssignmentCentreCode] = useState('');
  const [assignmentCentreIncharge, setAssignmentCentreIncharge] = useState('');
  const [assignmentFromDate, setAssignmentFromDate] = useState('');
  const [cultureReplicationList, setCultureReplicationList] = useState([]);
  const [isObservationDropdownOpen, setIsObservationDropdownOpen] = useState(false);
  const observationDropdownRef = useRef(null);
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const [showIndividualDetails, setShowIndividualDetails] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignmentDetailModal, setShowAssignmentDetailModal] = useState(false);
  const [showEditAssignmentModal, setShowEditAssignmentModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [addLocationContext, setAddLocationContext] = useState({ trial: null, centre: '' });
  const [showAddLocLocationDetails, setShowAddLocLocationDetails] = useState(false);
  const [showAddLocIndividualDetails, setShowAddLocIndividualDetails] = useState(false);
  const [editingTrial, setEditingTrial] = useState(null);
  const [selectedTrial, setSelectedTrial] = useState(null);

  // Load trial assignments when editing a trial
  useEffect(() => {
    if (editingTrial && editingTrial.id) {
      loadTrialAssignments(editingTrial.id);
    }
  }, [editingTrial]);

  // Load trial centres when in detail view
  useEffect(() => {
    if (isDetailView && detailId) {
      loadTrialCentres();
    }
  }, [isDetailView, detailId]);

  // Load current trial details when detailId changes
  useEffect(() => {
    if (detailId) {
      loadCurrentTrial(detailId);
    }
  }, [detailId]);

  // Load location management data when in locations view with centre parameter
  useEffect(() => {
    if (isLocationsView && searchParams.get('centre')) {
      const trialCentreId = searchParams.get('centre');
      loadLocationManagement(trialCentreId);
    }
  }, [isLocationsView, searchParams.get('centre')]);

  // Load trial centre assignments when user is available
  useEffect(() => {
    let user = localStorage.getItem('user');
    if(user){
        user = JSON.parse(user);
    }
    if (user?.id) {
      loadTrialCentreAssignments(user.id);
    }
  }, []);

  // Load location assignments when user is available
  useEffect(() => {
    let user = localStorage.getItem('user');
    if(user){
        user = JSON.parse(user);
    }
    if (user?.id) {
      loadLocationAssignmentsForOfficer(user.id);
    }
  }, []);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationDetailsModal, setShowLocationDetailsModal] = useState(false);
  const [selectedLocationForTrialDetails, setSelectedLocationForTrialDetails] = useState(null);
  const [showLocationTrialDetailsModal, setShowLocationTrialDetailsModal] = useState(false);
  const [selectedCentre, setSelectedCentre] = useState(null);
  
  // Dynamic data state
  const [dynamicCrops, setDynamicCrops] = useState([]);
  const [dynamicSeasons, setDynamicSeasons] = useState([]);
  const [dynamicScientists, setDynamicScientists] = useState([]);
  const [dynamicCentres, setDynamicCentres] = useState([]);
  const [dynamicOfficers, setDynamicOfficers] = useState([]);
  const [isLoadingCrops, setIsLoadingCrops] = useState(false);
  const [isLoadingSeasons, setIsLoadingSeasons] = useState(false);
  const [isLoadingScientists, setIsLoadingScientists] = useState(false);
  const [isLoadingCentres, setIsLoadingCentres] = useState(false);
  const [isLoadingOfficers, setIsLoadingOfficers] = useState(false);
  
  // Trial centres state
  const [trialCentres, setTrialCentres] = useState([]);
  // Holds ALL centres across ALL trials for global code generation
  const [allCentresForCounter, setAllCentresForCounter] = useState([]);
  const [isLoadingTrialCentres, setIsLoadingTrialCentres] = useState(false);
  const [isCreatingTrialCentre, setIsCreatingTrialCentre] = useState(false);
  const [deletingTrialCentre, setDeletingTrialCentre] = useState(null);
  const [trialCentresPagination, setTrialCentresPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  // New state for officer trial centres
  const [officerTrialCentres, setOfficerTrialCentres] = useState([]);
  const [isLoadingOfficerTrialCentres, setIsLoadingOfficerTrialCentres] = useState(false);
  const [officerTrialCentresPagination, setOfficerTrialCentresPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [showEditCentreModal, setShowEditCentreModal] = useState(false);
  const [locationIncharge, setLocationIncharge] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCrop, setFilterCrop] = useState('all');
  const [filterSeason, setFilterSeason] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // State for edit trial culture entries
  const [editTrialCulture, setEditTrialCulture] = useState('');
  const [editTrialReplications, setEditTrialReplications] = useState(1);
  const [editTrialObservations, setEditTrialObservations] = useState([]);
  const [editTrialObservationDays, setEditTrialObservationDays] = useState({});
  const [editTrialCultureList, setEditTrialCultureList] = useState([]);
  const [editTrialObservationsList, setEditTrialObservationsList] = useState([]);

  // Trial Incharge Validity state variables
  const [trialInchargeValidityFrom, setTrialInchargeValidityFrom] = useState('');
  const [trialInchargeValidityTo, setTrialInchargeValidityTo] = useState('');
  const [trialInchargeValidityList, setTrialInchargeValidityList] = useState([]);

  // Centre Incharge Validity state variables
  const [centreInchargeValidityFrom, setCentreInchargeValidityFrom] = useState('');
  const [centreInchargeValidityTo, setCentreInchargeValidityTo] = useState('');
  const [centreInchargeValidityList, setCentreInchargeValidityList] = useState([]);
  
  // Centre Incharge Assignments state variables
  const [centreInchargeAssignments, setCentreInchargeAssignments] = useState([]);
  const [isLoadingCentreInchargeAssignments, setIsLoadingCentreInchargeAssignments] = useState(false);

  // Location Incharge Validity state variables
  const [locationInchargeValidityFrom, setLocationInchargeValidityFrom] = useState('');
  const [locationInchargeValidityTo, setLocationInchargeValidityTo] = useState('');
  const [locationInchargeValidityList, setLocationInchargeValidityList] = useState([]);

  // Location Management state variables
  const [locationManagementData, setLocationManagementData] = useState([]);
  const [isLoadingLocationManagement, setIsLoadingLocationManagement] = useState(false);
  const [isCreatingLocation, setIsCreatingLocation] = useState(false);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [selectedLocationForForm, setSelectedLocationForForm] = useState(null);
  const [showLocationForm, setShowLocationForm] = useState(false);
  
  // Trial Data Entry Modal state variables
  const [currentEntry, setCurrentEntry] = useState({
    observation: '',
    replication: '',
    culture: '',
    value: ''
  });
  const [farmerFeedback, setFarmerFeedback] = useState('');
  const [locationInchargeFeedback, setLocationInchargeFeedback] = useState('');
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [isSavingEntry, setIsSavingEntry] = useState(false);
  const [currentTrialData, setCurrentTrialData] = useState(null);
  const [savedEntries, setSavedEntries] = useState([]);
  const [isLoadingSavedEntries, setIsLoadingSavedEntries] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [locationManagementPagination, setLocationManagementPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editLocationData, setEditLocationData] = useState({
    location_name: '',
    sf_number: '',
    latitude: '',
    longitude: '',
    address: '',
    location_incharge_id: '',
    location_incharge: '',
    from_date: '',
    status: 'active'
  });
  const [locationAssignments, setLocationAssignments] = useState([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  
  // Trial Details Modal state variables
  const [showTrialDetailsModal, setShowTrialDetailsModal] = useState(false);
  const [selectedLocationForDetails, setSelectedLocationForDetails] = useState(null);
  const [trialDetailsData, setTrialDetailsData] = useState(null);
  const [isLoadingTrialDetails, setIsLoadingTrialDetails] = useState(false);
  const [isSavingTrialDetails, setIsSavingTrialDetails] = useState(false);
  const [trialDetailsFormData, setTrialDetailsFormData] = useState({
    experimental_area_ha: '',
    plot_size_sqm: '',
    soil_type: '',
    soil_test_date: '',
    soil_test_details: '',
    irrigation_source: '',
    previous_crop: '',
    seed_treatment: '',
    ploughing: '',
    sowing_date: '',
    planting_date: '',
    basal_organic: '',
    basal_organic_date: '',
    basal_organic_quantity_kg_ha: '',
    basal_fertilizer_date: '',
    basal_fertilizer_name: '',
    basal_fertilizer_quantity_kg_ha: '',
    top_dressing_date: '',
    top_dressing_name: '',
    top_dressing_quantity_kg_ha: '',
    weed_management: '',
    plant_protection: '',
    harvest_date: '',
    monitoring_visit_date: '',
    monitoring_comments: '',
    status: 'active'
  });

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm();
  const { user } = useAuth();

  // Trial assignments data
  const [trialAssignments, setTrialAssignments] = useState([]);
  const [centreAssignments, setCentreAssignments] = useState([]);
  
  // Trial centre assignments data
  const [trialCentreAssignments, setTrialCentreAssignments] = useState([]);
  const [isLoadingTrialCentreAssignments, setIsLoadingTrialCentreAssignments] = useState(false);

  // Location assignments data
  const [isLoadingLocationAssignments, setIsLoadingLocationAssignments] = useState(false);
  const [locationAssignmentsPagination, setLocationAssignmentsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Load centre incharge data when edit modal opens
  useEffect(() => {
    if (showEditCentreModal && selectedCentre) {
      console.log('🔍 Modal opened with selectedCentre:', selectedCentre);
      
      // Set the current centre incharge from the selected centre data
      const currentIncharge = selectedCentre.centre_incharge || selectedCentre.assignment_centre_incharge || selectedCentre.centreIncharge;
      if (currentIncharge) {
        console.log('✅ Setting centre incharge:', currentIncharge);
        setSelectedCentre(prev => ({ 
          ...prev, 
          centreIncharge: currentIncharge,
          centre_incharge: currentIncharge
        }));
      }
    }
  }, [showEditCentreModal, selectedCentre]);

  // Current centre assignment for Add Location modal (read-only summary)
  const centreAssignmentForAddLocation = useMemo(() => {
    if (!showAddLocationModal || !addLocationContext.trial) return null;
    try {
      return (
        trialAssignments.find(
          (a) => a.trialId === addLocationContext.trial.id && a.locationName === addLocationContext.centre
        ) || null
      );
    } catch {
      return null;
    }
  }, [showAddLocationModal, addLocationContext, trialAssignments]);

  // Mock data for dropdowns
  const scientists = [];

  // Officers are managed via OfficerIncharge and stored in localStorage
  const officers = [];

  const centres = [];

  // Use dynamic observations from API, fallback to hardcoded if not loaded yet
  const observations = dynamicObservations;

  const soilTypes = [];
  const irrigationSources = [];

  // Trial types
  const trialTypes = ['ART', 'MLT'];

  // Crops (from crop management)
  const crops = [];

  // Seasons
  const seasons = [];

  // Years (current year and next 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => (currentYear + i).toString());

  // Watch form values for code generation
  const watchedType = watch('type');
  const watchedCrop = watch('crop');
  const watchedYear = watch('year');
  const watchedSeason = watch('season');

  // Auto-generate trial code
  const generateTrialCode = () => {
    if (!watchedType || !watchedCrop || !watchedYear || !watchedSeason) {
      return '';
    }

    // Get trial type (full name)
    const trialType = watchedType;
    
    // Get first letter of crop name (handle both ID and name)
    const cropName = dynamicCrops.find(crop => crop.id == watchedCrop)?.name || watchedCrop;
    const cropFirstLetter = cropName.charAt(0).toUpperCase();
    
    // Get last 2 digits of year
    const yearLastTwoDigits = watchedYear.toString().slice(-2);
    
    // Get first letter of season name (handle both ID and name)
    const seasonName = dynamicSeasons.find(season => season.id == watchedSeason)?.name || watchedSeason;
    const seasonFirstLetter = seasonName.charAt(0).toUpperCase();
    
    // Get auto number (count all existing trials + 1) with 3 digit number for unified sequence
    const allExistingTrials = trials.filter(trial => trial && (trial.trial_type || trial.type));
    const autoNumber = (allExistingTrials.length + 1).toString().padStart(3, '0');
    
    return `${trialType}${cropFirstLetter}${yearLastTwoDigits}${seasonFirstLetter}${autoNumber}`;
  };

  const filteredTrials = (Array.isArray(trials) ? trials : []).filter(trial => {
    // Ensure trial is a valid object before processing
    if (!trial || typeof trial !== 'object') {
      return false;
    }
    
    const matchesSearch = (trial.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (trial.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ((trial.crop_name || trial.crop) || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || (trial.trial_type || trial.type) === filterType;
    
    const matchesCrop = filterCrop === 'all' || (trial.crop_name || trial.crop) === filterCrop;
    const matchesSeason = filterSeason === 'all' || (trial.season_name || trial.season) === filterSeason;
    
    const matchesDetail = !isDetailView || trial.id === detailId;
    // Officer access scoping
    let matchesAccess = true;
    if (user && (user.role === 'officer' || user.role === 'plot-officer')) {
      const lowerName = (user.name || '').toLowerCase();
      const isTrialIncharge = ((trial.trial_incharge_officer || trial.trialInchargeOfficer) || '').toLowerCase() === lowerName;
      const hasAssignments = trial.id ? getTrialAssignments(trial.id).some(a =>
        (a.assignedOfficer || '').toLowerCase() === lowerName ||
        (a.centreIncharge || '').toLowerCase() === lowerName
      ) : false;
      matchesAccess = isTrialIncharge || hasAssignments;
    }
    return matchesSearch && matchesType && matchesCrop && matchesSeason && matchesDetail && matchesAccess;
  });

  // Generate next centre code number using unified global counter across all trials
  const generateNextCentreCode = () => {
    if (!filteredTrials[0]?.code) return '';
    
    // Prefer the global centres list if available; fallback to currently loaded centres
    const sourceCentres = (allCentresForCounter && allCentresForCounter.length > 0) ? allCentresForCounter : trialCentres;
    
    // Find ALL existing centres across ALL trials that have centre codes with -C pattern
    const allExistingCentres = sourceCentres.filter(centre => 
      centre && centre.centre_code && centre.centre_code.includes('-C')
    );
    
    // Extract existing numbers and find the next one
    const existingNumbers = allExistingCentres
      .map(centre => {
        const match = centre.centre_code?.match(/-C(\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(num => !isNaN(num))
      .sort((a, b) => b - a);
    
    const nextNumber = existingNumbers.length > 0 ? existingNumbers[0] + 1 : 1;
    const generatedCode = `${filteredTrials[0].code}-C${nextNumber.toString().padStart(3, '0')}`;
    
    console.log(`🔢 Generating centre code (unified): ${generatedCode} (Found ${existingNumbers.length} existing centres across all trials: [${existingNumbers.join(', ')}])`);
    
    return generatedCode;
  };

  // Update centre code when trial changes or trial centres change
  useEffect(() => {
    if (filteredTrials[0]?.code) {
      const nextCode = generateNextCentreCode();
      setAssignmentCentreCode(nextCode);
    }
  }, [filteredTrials, trialCentres, detailId]);

  const handleAdd = async (data) => {
    setIsLoading(true);
    try {
      // Culture is now optional - no validation required
      
      // Check if crops, seasons, and scientists are loaded
      if (dynamicCrops.length === 0 && !isLoadingCrops) {
        toast.error('Please wait for crops to load or refresh the data');
        setIsLoading(false);
        return;
      }

      if (dynamicSeasons.length === 0 && !isLoadingSeasons) {
        toast.error('Please wait for seasons to load or refresh the data');
        setIsLoading(false);
        return;
      }
      
      if (dynamicScientists.length === 0 && !isLoadingScientists) {
        toast.error('Please wait for scientists to load or refresh the data');
        setIsLoading(false);
        return;
      }
      
      // Get culture data from the list (optional)
      const cultureData = editTrialCultureList.length > 0 ? editTrialCultureList[0] : null;
      const observationsData = editTrialObservationsList.length > 0 ? editTrialObservationsList[0] : null;
      
      // Convert trial incharge ID to name for API submission
      const selectedScientist = dynamicScientists.find(scientist => scientist.id == data.trialIncharge) || { name: data.trialIncharge };
      
      // Convert crop ID to name for API submission
      const selectedCrop = dynamicCrops.find(crop => crop.id == data.crop) || { name: data.crop };
      
      // Convert season ID to name for API submission
      const selectedSeason = dynamicSeasons.find(season => season.id == data.season) || { name: data.season };
      
      // Create assignments array - include selected trial incharge and any existing validity list
      const assignments = [];
      
      // Add the selected trial incharge as the primary assignment
      const primaryAssignment = {
        trial_id: null, // Will be set by API response
        officer_id: selectedScientist.id || null,
        centre_incharge: selectedScientist.name || data.trialIncharge,
        assigned_officer: selectedScientist.name || data.trialIncharge,
        assignment_date: data.startDate || new Date().toISOString().split('T')[0],
        status: 'active'
      };
      assignments.push(primaryAssignment);
      
      // Add any additional assignments from trial incharge validity list
      const additionalAssignments = trialInchargeValidityList.map(validity => {
        // Find officer ID for the trial incharge
        const officer = dynamicScientists.find(scientist => scientist.name === validity.trialIncharge);
        const assignment = {
          trial_id: null, // Will be set by API response
          officer_id: officer?.id || null,
          centre_incharge: validity.trialIncharge,
          assigned_officer: validity.trialIncharge,
          assignment_date: validity.startDate || new Date().toISOString().split('T')[0],
          status: validity.status || 'active'
        };
        console.log('Creating additional assignment:', assignment);
        return assignment;
      });
      assignments.push(...additionalAssignments);
      
      console.log('Selected Trial Incharge:', selectedScientist);
      console.log('Trial Incharge Validity List:', trialInchargeValidityList);
      console.log('Generated Assignments:', assignments);

      const trialData = {
        code: generateTrialCode(),
        type: data.type,
        name: data.name,
        crop: selectedCrop.name || data.crop,
        year: data.year,
        season: selectedSeason.name || data.season,
        date: new Date().toISOString().split('T')[0],
        status: 'active',
        trialIncharge: selectedScientist.name || data.trialIncharge,
        trialInchargeOfficer: selectedScientist.name || data.trialIncharge,
        startDate: data.startDate,
        culture: cultureData?.culture || '',
        replications: cultureData?.replications || 1,
        observations: editTrialObservationsList.map(obs => obs.observation),
        observationDays: editTrialObservationsList.reduce((acc, obs) => {
          acc[obs.observation] = obs.days;
          return acc;
        }, {}),
        assignments: assignments
      };

      console.log('Complete Trial Data with Assignments:', trialData);
      const response = await trialService.createTrial(trialData);
      if (response.success) {
        await loadTrials(currentPage, pageSize); // Reload data from API with current pagination
      toast.success('Trial created successfully!');
      setShowAddModal(false);
      setEditTrialCultureList([]);
      setEditTrialCulture('');
      setEditTrialObservations([]);
      setEditTrialObservationsList([]);
      setEditTrialObservationDays('');
      setTrialInchargeValidityFrom('');
      setTrialInchargeValidityTo('');
      setTrialInchargeValidityList([]);
      reset();
      } else {
        throw new Error(response.message || 'Failed to create trial');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to create trial');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (data) => {
    setIsLoading(true);
    try {
      // Validate observations
      if (!editTrialObservationsList || editTrialObservationsList.length === 0) {
        toast.error('Please select at least one observation');
        setIsLoading(false);
        return;
      }
      
      // Get culture data from the list
      const cultureData = editTrialCultureList.length > 0 ? editTrialCultureList[0] : null;
      const observationsData = editTrialObservationsList.length > 0 ? editTrialObservationsList[0] : null;
      
      // Convert trial incharge ID to name for API submission
      const selectedScientist = dynamicScientists.find(scientist => scientist.id == data.trialIncharge) || { name: data.trialIncharge };
      
      // Convert crop ID to name for API submission
      const selectedCrop = dynamicCrops.find(crop => crop.id == data.crop) || { name: data.crop };
      
      // Convert season ID to name for API submission
      const selectedSeason = dynamicSeasons.find(season => season.id == data.season) || { name: data.season };
      
      // Map trial incharge validity list to assignments format
      const assignments = trialInchargeValidityList.map(validity => {
        // Find officer ID for the trial incharge
        const officer = dynamicScientists.find(scientist => scientist.name === validity.trialIncharge);
        const assignment = {
          trial_id: editingTrial.id, // Use existing trial ID for edit
          officer_id: officer?.id || null,
          centre_incharge: validity.trialIncharge,
          assigned_officer: validity.trialIncharge,
          assignment_date: validity.startDate || new Date().toISOString().split('T')[0],
          status: validity.status || 'active'
        };
        console.log('Editing assignment:', assignment);
        return assignment;
      });
      
      console.log('Edit - Trial Incharge Validity List:', trialInchargeValidityList);
      console.log('Edit - Generated Assignments:', assignments);
      console.log('🔍 Edit - Current editTrialReplications value:', editTrialReplications);
      
      const trialData = { 
        code: editingTrial.code, // Keep existing trial code when editing
        type: data.type,
        name: data.name,
        crop: selectedCrop.name || data.crop,
        year: data.year,
        season: selectedSeason.name || data.season,
        trialIncharge: selectedScientist.name || data.trialIncharge,
        replications: editTrialReplications || 1, // Add replications field from Trials section
        trials: editTrialCultureList.map(culture => ({
          culture: culture.culture || '',
          replications: culture.replications || 1
        })),
        observations: editTrialObservationsList.map(obs => ({
          observation: obs.observation,
          days: obs.days
        })),
        assignments: assignments
      };

      console.log('Complete Edit Trial Data with Assignments:', trialData);
      const response = await trialService.updateTrial(editingTrial.id, trialData);
      if (response.success) {
        await loadTrials(currentPage, pageSize); // Reload data from API with current pagination
      toast.success('Trial updated successfully!');
      setShowAddModal(false);
      setEditingTrial(null);
      setEditTrialCultureList([]);
      setEditTrialCulture('');
      setEditTrialObservations([]);
      setEditTrialObservationsList([]);
      setEditTrialObservationDays('');
      setTrialInchargeValidityFrom('');
      setTrialInchargeValidityTo('');
      setTrialInchargeValidityList([]);
      reset();
      } else {
        throw new Error(response.message || 'Failed to update trial');
      }
    } catch (error) {
      toast.error('Failed to update trial');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (trialId) => {
    if (!window.confirm('Are you sure you want to delete this trial?')) return;

    setIsLoading(true);
    try {
      const response = await trialService.deleteTrial(trialId);
      if (response.success) {
        await loadTrials(currentPage, pageSize); // Reload data from API with current pagination
      toast.success('Trial deleted successfully!');
      } else {
        throw new Error(response.message || 'Failed to delete trial');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to delete trial');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (trial) => {
    console.log('🔍 Opening edit modal for trial:', trial);
    console.log('🔍 Trial replications field:', trial.replications);
    console.log('🔍 Trial trials array:', trial.trials);
    setEditingTrial(trial);
    setValue('type', trial.trial_type || trial.type);
    setValue('name', trial.name);
    
    // Find crop and season IDs for the form
    const cropId = dynamicCrops.find(crop => crop.name === (trial.crop_name || trial.crop))?.id || (trial.crop_name || trial.crop);
    const seasonId = dynamicSeasons.find(season => season.name === (trial.season_name || trial.season))?.id || (trial.season_name || trial.season);
    
    // Find scientist ID for the form
    const scientistId = dynamicScientists.find(scientist => scientist.name === (trial.trial_incharge || trial.trialIncharge))?.id || (trial.trial_incharge || trial.trialIncharge);
    
    setValue('crop', cropId);
    setValue('year', trial.year);
    setValue('season', seasonId);
    setValue('trialIncharge', scientistId);
    
    // Set replications for the Trials section
    // Try to get replications from trial.replications first, then from trials array
    let replicationsValue = trial.replications || 1;
    if (trial.trials && Array.isArray(trial.trials) && trial.trials.length > 0) {
      replicationsValue = trial.trials[0].replications || trial.replications || 1;
    }
    console.log('🔍 Setting editTrialReplications to:', replicationsValue);
    setEditTrialReplications(replicationsValue);
    
    // Set culture list if trial has culture data
    if (trial.trials && Array.isArray(trial.trials) && trial.trials.length > 0) {
      // New format: array of objects with {culture, replications}
      const cultureEntries = trial.trials.map((culture, index) => ({
        id: Date.now() + index,
        culture: culture.culture || '',
        replications: culture.replications || 1
      }));
      setEditTrialCultureList(cultureEntries);
    } else if (trial.culture && trial.replications) {
      // Old format: individual culture and replications fields
      setEditTrialCultureList([{
        id: Date.now(),
        culture: trial.culture,
        replications: trial.replications
      }]);
    } else {
      setEditTrialCultureList([]);
    }
    
    // Set observations list (normalize to array safely before mapping)
    try {
      const rawObservations = trial?.observations;
      let observationsArray = [];
      
      if (Array.isArray(rawObservations)) {
        // Check if observations are objects with {observation, days} structure
        if (rawObservations.length > 0 && typeof rawObservations[0] === 'object' && rawObservations[0].observation) {
          // New format: array of objects with {observation, days}
          observationsArray = rawObservations;
        } else {
          // Old format: array of strings
          observationsArray = rawObservations;
        }
      } else if (rawObservations == null) {
        observationsArray = [];
      } else if (typeof rawObservations === 'string') {
        observationsArray = rawObservations
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
      } else if (typeof rawObservations === 'object') {
        observationsArray = Object.values(rawObservations).filter(Boolean);
      }

      if (observationsArray.length > 0) {
        const observationEntries = observationsArray.map((obs, index) => {
          // Handle both old and new formats
          if (typeof obs === 'object' && obs.observation) {
            // New format: {observation, days}
            return {
              id: Date.now() + index,
              observation: obs.observation,
              days: obs.days || ''
            };
          } else {
            // Old format: string
            return {
              id: Date.now() + index,
              observation: obs,
              days: (trial?.observationDays && typeof trial.observationDays === 'object')
                ? (trial.observationDays?.[obs] ?? '')
                : (trial.observationDays ?? '')
            };
          }
        });
        setEditTrialObservationsList(observationEntries);
      } else {
        setEditTrialObservationsList([]);
      }
    } catch {
      setEditTrialObservationsList([]);
    }
    
    setShowAddModal(true);
  };

  const openAssignmentModal = (trial) => {
    setSelectedTrial(trial);
    setShowAssignmentModal(true);
    setAssignmentCulture('');
    setAssignmentReplications(1);
    setAssignmentObservations([]);
    setAssignmentCentre('');
    setCultureReplicationList([]);
    setIsObservationDropdownOpen(false);
  };

  // Persist assignments to localStorage whenever they change so ViewAssignment can read them
  useEffect(() => {
    try {
      window.localStorage.setItem('trialAssignments', JSON.stringify(trialAssignments));
    } catch {}
  }, [trialAssignments]);

  // Keep replications in sync across all culture rows
  useEffect(() => {
    if (cultureReplicationList.length === 0) return;
    setCultureReplicationList(prev => prev.map(row => ({
      ...row,
      replications: assignmentReplications
    })));
  }, [assignmentReplications]);

  // Keep observations in sync across all culture rows
  useEffect(() => {
    if (cultureReplicationList.length === 0) return;
    setCultureReplicationList(prev => prev.map(row => ({
      ...row,
      observations: assignmentObservations
    })));
  }, [assignmentObservations]);

  // Close the observations dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        observationDropdownRef.current &&
        !observationDropdownRef.current.contains(event.target)
      ) {
        setIsObservationDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleObservationSelection = (value) => {
    setAssignmentObservations((prev) => {
      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      // if removed, also remove any days value
      if (!next.includes(value)) {
        setAssignmentObservationDays((d) => {
          const { [value]: _omit, ...rest } = d || {};
          return rest;
        });
      }
      return next;
    });
  };

  const openViewModal = (trial) => {
    setSelectedTrial(trial);
    setShowViewModal(true);
  };

  const handleAddCultureReplication = () => {
    if (!assignmentCulture || !assignmentCentre) return;
    
    // Convert centre ID to name for storage
    const centreName = dynamicCentres.length > 0 
      ? (dynamicCentres.find(centre => centre.id == assignmentCentre)?.name || assignmentCentre)
      : (centres[assignmentCentre] || assignmentCentre);
    
    setCultureReplicationList(prev => {
      const isFirst = prev.length === 0;
      const next = [
        ...prev,
        {
          centre: centreName,
          culture: assignmentCulture,
          replications: isFirst ? assignmentReplications : (prev[0]?.replications ?? assignmentReplications),
          observations: isFirst ? assignmentObservations : (prev[0]?.observations ?? assignmentObservations)
        }
      ];
      return next;
    });
    // After first row, keep the same replications/observations for the rest
    setAssignmentCulture('');
  };

  const handleSaveAssignments = (e) => {
    e.preventDefault();
    if (!selectedTrial || cultureReplicationList.length === 0) return;
    const today = new Date().toISOString().split('T')[0];
    setTrialAssignments(prev => {
      const next = [...prev];
      
      // Convert centre ID to name
      const centreName = dynamicCentres.length > 0 
        ? (dynamicCentres.find(centre => centre.id == assignmentCentre)?.name || assignmentCentre)
        : (centres[assignmentCentre] || assignmentCentre);
      
      // Convert officer ID to name
      const officerName = dynamicOfficers.length > 0 
        ? (dynamicOfficers.find(officer => officer.id == assignmentCentreIncharge)?.name || assignmentCentreIncharge)
        : (officers[assignmentCentreIncharge] || assignmentCentreIncharge);
      
      cultureReplicationList.forEach((row, index) => {
        next.push({
          id: Date.now() + index,
          trialId: selectedTrial.id,
          locationCode: generateLocationCode(selectedTrial.code),
          locationName: row.centre || centreName || 'Centre',
          assignedOfficer: officerName,
          culture: row.culture,
          treatment: '',
          replications: row.replications,
          observations: row.observations || [],
          centreIncharge: officerName,
          status: 'active',
          assignedDate: today
        });
      });
      return next;
    });
    setShowAssignmentModal(false);
    setCultureReplicationList([]);
    toast.success('Centre(s) added');
  };

  const openAssignmentDetailModal = (assignment) => {
    setSelectedAssignment(assignment);
    setShowAssignmentDetailModal(true);
  };

  const openLocationDetailsModal = (location) => {
    setSelectedLocation(location);
    setShowLocationDetailsModal(true);
    
    // Load existing location incharge validity periods if they exist
    if (location.locationInchargeValidityList && location.locationInchargeValidityList.length > 0) {
      setLocationInchargeValidityList(location.locationInchargeValidityList);
    } else {
      setLocationInchargeValidityList([]);
    }
    
    // Reset the date inputs
    setLocationInchargeValidityFrom('');
    setLocationInchargeValidityTo('');
  };

  const openLocationTrialDetailsModal = (location) => {
    setSelectedLocationForTrialDetails(location);
    setShowLocationTrialDetailsModal(true);
  };

  const openAddLocationModal = (trial, centre) => {
    setAddLocationContext({ trial, centre });
    setShowAddLocationModal(true);
    // Reset form data
    setNewLocationData({
      locationName: '',
      locationIncharge: '',
      sfNumber: '',
      latitude: '',
      longitude: '',
      locationAddress: '',
      experimentalArea: '',
      plotSize: '',
      soilType: '',
      soilTestDate: '',
      soilTestDetails: '',
      irrigationSource: '',
      previousCrop: '',
      seedTreatment: '',
      ploughing: '',
      sowingDate: '',
      plantingDate: '',
      basalOrganic: '',
      basalOrganicDate: '',
      basalOrganicQuantity: '',
      basalFertilizerDate: '',
      basalFertilizerName: '',
      basalFertilizerQuantity: '',
      topDressingDate: '',
      topDressingName: '',
      topDressingQuantity: '',
      weedManagement: '',
      plantProtection: '',
      harvestDate: '',
      monitoringVisitDate: '',
      monitoringComments: '',
      plotOfficerVisitDate: '',
      plotOfficerComments: '',
      inspectionVisitDate: '',
      inspectionComments: '',
      fieldDayDate: '',
      farmersFeedback: ''
    });
  };

  const handleSaveLocation = () => {
    if (!newLocationData.locationName || !newLocationData.locationIncharge) {
      toast.error('Please fill in Location Name and Location Incharge');
      return;
    }

    // Create new location assignment
    const newLocation = {
      id: Date.now(),
      trialId: addLocationContext.trial.id,
      locationCode: generateLocationCode(addLocationContext.trial.code),
      locationName: newLocationData.locationName,
      assignedOfficer: newLocationData.locationIncharge,
      culture: centreAssignmentForAddLocation?.culture || '',
      treatment: '',
      replications: centreAssignmentForAddLocation?.replications || 1,
      observations: centreAssignmentForAddLocation?.observations || [],
      centreIncharge: addLocationContext.centre,
      locationIncharge: newLocationData.locationIncharge,
      status: 'active',
      assignedDate: new Date().toISOString().split('T')[0],
      // Add all the detailed location data
      ...newLocationData
    };

    // Add to trial assignments
    setTrialAssignments(prev => [...prev, newLocation]);
    
    toast.success('Location added successfully!');
    setShowAddLocationModal(false);
    
    // Reset form data
    setNewLocationData({
      locationName: '',
      locationIncharge: '',
      sfNumber: '',
      latitude: '',
      longitude: '',
      locationAddress: '',
      experimentalArea: '',
      plotSize: '',
      soilType: '',
      soilTestDate: '',
      soilTestDetails: '',
      irrigationSource: '',
      previousCrop: '',
      seedTreatment: '',
      ploughing: '',
      sowingDate: '',
      plantingDate: '',
      basalOrganic: '',
      basalOrganicDate: '',
      basalOrganicQuantity: '',
      basalFertilizerDate: '',
      basalFertilizerName: '',
      basalFertilizerQuantity: '',
      topDressingDate: '',
      topDressingName: '',
      topDressingQuantity: '',
      weedManagement: '',
      plantProtection: '',
      harvestDate: '',
      monitoringVisitDate: '',
      monitoringComments: '',
      plotOfficerVisitDate: '',
      plotOfficerComments: '',
      inspectionVisitDate: '',
      inspectionComments: '',
      fieldDayDate: '',
      farmersFeedback: ''
    });
  };

  const openEditAssignmentModal = (assignment) => {
    setEditingAssignment(assignment);
    setShowEditAssignmentModal(true);
  };

  const handleEditAssignment = async (data) => {
    try {
      setIsLoading(true);
      
      // Update the assignment in the state
      setTrialAssignments(prev => 
        prev.map(assignment => 
          assignment.id === editingAssignment.id 
            ? { ...assignment, ...data }
            : assignment
        )
      );

      toast.success('Assignment updated successfully');
      setShowEditAssignmentModal(false);
      setEditingAssignment(null);
      reset();
    } catch (error) {
      toast.error('Failed to update assignment');
      console.error('Error updating assignment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Location Management CRUD functions
  const handleCreateLocation = async (locationData) => {
    try {
      setIsCreatingLocation(true);
      const trialCentreId = searchParams.get('centre');
      
      // First create the location
      const locationResponse = await locationManagementService.createLocationManagement({
        ...locationData,
        trial_centre_id: trialCentreId
      });
      
      if (locationResponse.success) {
        // Then create the assignment if we have officer data
        if (locationData.location_incharge_id && locationData.location_incharge) {
          const assignmentData = {
            officer_id: parseInt(locationData.location_incharge_id),
            location_incharge: locationData.location_incharge,
            assignment_date: locationData.from_date,
            status: 'active',
            notes: 'Primary location incharge'
          };
          
          try {
            await locationManagementService.createLocationAssignment(locationResponse.data.id, assignmentData);
          } catch (assignmentError) {
            console.warn('Location created but assignment failed:', assignmentError);
            // Don't fail the entire operation if assignment fails
          }
        }
        
        toast.success('Location created successfully');
        // Reload location management data
        await loadLocationManagement(trialCentreId);
        return true;
      } else {
        throw new Error(locationResponse.message || 'Failed to create location');
      }
    } catch (error) {
      console.error('Error creating location:', error);
      toast.error(error.message || 'Failed to create location');
      return false;
    } finally {
      setIsCreatingLocation(false);
    }
  };

  const handleUpdateLocation = async (id, locationData) => {
    try {
      setIsUpdatingLocation(true);
      
      const response = await locationManagementService.updateLocationManagement(id, locationData);
      
      if (response.success) {
        toast.success('Location updated successfully');
        // Reload location management data
        const trialCentreId = searchParams.get('centre');
        await loadLocationManagement(trialCentreId, locationManagementPagination.page, locationManagementPagination.limit);
        // Close the modal
        closeEditLocationModal();
        return true;
      } else {
        throw new Error(response.message || 'Failed to update location');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error(error.message || 'Failed to update location');
      return false;
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  const handleCreateAssignmentOnly = async (id, locationData) => {
    try {
      setIsUpdatingLocation(true);
      
      // Only create assignment if officer data has changed - NO location update API call
      if (locationData.location_incharge_id && locationData.location_incharge) {
        const assignmentData = {
          officer_id: parseInt(locationData.location_incharge_id),
          location_incharge: locationData.location_incharge,
          assignment_date: locationData.from_date,
          status: 'active',
          notes: 'Primary location incharge'
        };
        
        try {
          await locationManagementService.createLocationAssignment(id, assignmentData);
          toast.success('Assignment created successfully');
          // Reload assignments to show the new one
          await loadLocationAssignments(id);
        } catch (assignmentError) {
          console.error('Assignment creation failed:', assignmentError);
          toast.error('Failed to create assignment');
        }
      } else {
        toast.info('No assignment data provided');
      }
    } catch (error) {
      console.error('Error in handleCreateAssignmentOnly:', error);
      toast.error(error.message || 'Operation failed');
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    
    try {
      setDeletingLocation(id);
      
      const response = await locationManagementService.deleteLocationManagement(id);
      
      if (response.success) {
        toast.success('Location deleted successfully');
        // Reload location management data
        const trialCentreId = searchParams.get('centre');
        await loadLocationManagement(trialCentreId, locationManagementPagination.page, locationManagementPagination.limit);
      } else {
        throw new Error(response.message || 'Failed to delete location');
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.message || 'Failed to delete location');
    } finally {
      setDeletingLocation(null);
    }
  };

  // Location Management pagination handlers
  const handleLocationPageChange = (newPage) => {
    const trialCentreId = searchParams.get('centre');
    if (trialCentreId) {
      loadLocationManagement(trialCentreId, newPage, locationManagementPagination.limit);
    }
  };

  const handleLocationPageSizeChange = (newLimit) => {
    const trialCentreId = searchParams.get('centre');
    if (trialCentreId) {
      loadLocationManagement(trialCentreId, 1, newLimit);
    }
  };

  // Load location assignments
  const loadLocationAssignments = async (locationId) => {
    if (!locationId) return;
    
    try {
      setIsLoadingAssignments(true);
      const response = await locationManagementService.getLocationAssignments(locationId);
      
      if (response.success && response.data) {
        setLocationAssignments(response.data);
        console.log('✅ Location assignments loaded:', response.data);
      } else {
        console.warn('⚠️ Failed to load location assignments:', response.message);
        setLocationAssignments([]);
      }
    } catch (error) {
      console.error('❌ Error loading location assignments:', error);
      setLocationAssignments([]);
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  // Load trial centre assignments for a specific officer
  const loadTrialCentreAssignments = async (officerId) => {
    if (!officerId) return;
    
    try {
      setIsLoadingTrialCentreAssignments(true);
      const response = await officerAssignmentsService.getTrialCentreAssignments(officerId);
      
      if (response.success && response.data) {
        setTrialCentreAssignments(response.data);
        console.log('✅ Trial centre assignments loaded:', response.data);
      } else {
        console.warn('⚠️ Failed to load trial centre assignments:', response.message);
        setTrialCentreAssignments([]);
      }
    } catch (error) {
      console.error('❌ Error loading trial centre assignments:', error);
      setTrialCentreAssignments([]);
    } finally {
      setIsLoadingTrialCentreAssignments(false);
    }
  };

  // Location Management modal functions
  const openEditLocationModal = async (location) => {
    setEditingLocation(location);
    setEditLocationData({
      location_name: location.location_name || '',
      sf_number: location.sf_number || '',
      latitude: location.latitude ? location.latitude.toString() : '',
      longitude: location.longitude ? location.longitude.toString() : '',
      address: location.address || '',
      location_incharge_id: location.location_incharge_id || '',
      location_incharge: location.location_incharge || location.incharge_name || '',
      from_date: location.from_date || '',
      status: location.status || 'active'
    });
    setShowEditLocationModal(true);
    
    // Load assignments for this location
    await loadLocationAssignments(location.id);
  };

  const closeEditLocationModal = () => {
    setShowEditLocationModal(false);
    setEditingLocation(null);
    setEditLocationData({
      location_name: '',
      sf_number: '',
      latitude: '',
      longitude: '',
      address: '',
      location_incharge_id: '',
      location_incharge: '',
      from_date: '',
      status: 'active'
    });
    setLocationAssignments([]);
  };

  // Handle location row click to show form
  const handleLocationRowClick = (location) => {
    setSelectedLocationForForm(location);
    setShowLocationForm(true);
    // Load trial data for the selected location
    loadTrialDataForLocation(location);
    // Load saved entries for the selected location
    loadSavedEntries(location.id);
  };

  // Trial Details Modal functions
  const closeTrialDetailsModal = () => {
    setShowTrialDetailsModal(false);
    setSelectedLocationForDetails(null);
    setTrialDetailsData(null);
    setTrialDetailsFormData({
      experimental_area_ha: '',
      plot_size_sqm: '',
      soil_type: '',
      soil_test_date: '',
      soil_test_details: '',
      irrigation_source: '',
      previous_crop: '',
      seed_treatment: '',
      ploughing: '',
      sowing_date: '',
      planting_date: '',
      basal_organic: '',
      basal_organic_date: '',
      basal_organic_quantity_kg_ha: '',
      basal_fertilizer_date: '',
      basal_fertilizer_name: '',
      basal_fertilizer_quantity_kg_ha: '',
      top_dressing_date: '',
      top_dressing_name: '',
      top_dressing_quantity_kg_ha: '',
      weed_management: '',
      plant_protection: '',
      harvest_date: '',
      monitoring_visit_date: '',
      monitoring_comments: '',
      status: 'active'
    });
  };

  const loadTrialDetails = async (locationId) => {
    try {
      setIsLoadingTrialDetails(true);
      const response = await locationTrialDetailsService.getLocationTrialDetails(locationId);
      
      if (response.success && response.data.details && response.data.details.length > 0) {
        const details = response.data.details[0]; // Get the first detail record
        setTrialDetailsData(details);
        
        // Populate form with existing data
        setTrialDetailsFormData({
          experimental_area_ha: details.experimental_area_ha || '',
          plot_size_sqm: details.plot_size_sqm || '',
          soil_type: details.soil_type || '',
          soil_test_date: details.soil_test_date ? details.soil_test_date.split('T')[0] : '',
          soil_test_details: details.soil_test_details || '',
          irrigation_source: details.irrigation_source || '',
          previous_crop: details.previous_crop || '',
          seed_treatment: details.seed_treatment || '',
          ploughing: details.ploughing || '',
          sowing_date: details.sowing_date ? details.sowing_date.split('T')[0] : '',
          planting_date: details.planting_date ? details.planting_date.split('T')[0] : '',
          basal_organic: details.basal_organic || '',
          basal_organic_date: details.basal_organic_date ? details.basal_organic_date.split('T')[0] : '',
          basal_organic_quantity_kg_ha: details.basal_organic_quantity_kg_ha || '',
          basal_fertilizer_date: details.basal_fertilizer_date ? details.basal_fertilizer_date.split('T')[0] : '',
          basal_fertilizer_name: details.basal_fertilizer_name || '',
          basal_fertilizer_quantity_kg_ha: details.basal_fertilizer_quantity_kg_ha || '',
          top_dressing_date: details.top_dressing_date ? details.top_dressing_date.split('T')[0] : '',
          top_dressing_name: details.top_dressing_name || '',
          top_dressing_quantity_kg_ha: details.top_dressing_quantity_kg_ha || '',
          weed_management: details.weed_management || '',
          plant_protection: details.plant_protection || '',
          harvest_date: details.harvest_date ? details.harvest_date.split('T')[0] : '',
          monitoring_visit_date: details.monitoring_visit_date ? details.monitoring_visit_date.split('T')[0] : '',
          monitoring_comments: details.monitoring_comments || '',
          status: details.status || 'active'
        });
      } else {
        // Reset form if no data
        setTrialDetailsData(null);
        setTrialDetailsFormData({
          experimental_area_ha: '',
          plot_size_sqm: '',
          soil_type: '',
          soil_test_date: '',
          soil_test_details: '',
          irrigation_source: '',
          previous_crop: '',
          seed_treatment: '',
          ploughing: '',
          sowing_date: '',
          planting_date: '',
          basal_organic: '',
          basal_organic_date: '',
          basal_organic_quantity_kg_ha: '',
          basal_fertilizer_date: '',
          basal_fertilizer_name: '',
          basal_fertilizer_quantity_kg_ha: '',
          top_dressing_date: '',
          top_dressing_name: '',
          top_dressing_quantity_kg_ha: '',
          weed_management: '',
          plant_protection: '',
          harvest_date: '',
          monitoring_visit_date: '',
          monitoring_comments: '',
          status: 'active'
        });
      }
    } catch (error) {
      console.error('Error loading trial details:', error);
      toast.error('Failed to load trial details');
    } finally {
      setIsLoadingTrialDetails(false);
    }
  };

  const saveTrialDetails = async () => {
    try {
      setIsSavingTrialDetails(true);
      
      const payload = {
        location_id: selectedLocationForDetails.id,
        trial_id: currentTrial?.id || detailId,
        ...trialDetailsFormData
      };

      let response;
      if (trialDetailsData) {
        // Update existing record
        response = await locationTrialDetailsService.updateLocationTrialDetails(trialDetailsData.id, payload);
      } else {
        // Create new record
        response = await locationTrialDetailsService.createLocationTrialDetails(payload);
      }

      if (response.success) {
        toast.success('Trial details saved successfully');
        // Reload the data
        await loadTrialDetails(selectedLocationForDetails.id);
      } else {
        throw new Error(response.message || 'Failed to save trial details');
      }
    } catch (error) {
      console.error('Error saving trial details:', error);
      toast.error(error.message || 'Failed to save trial details');
    } finally {
      setIsSavingTrialDetails(false);
    }
  };

  const handleTrialDetailsChange = (field, value) => {
    setTrialDetailsFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Close location form
  const closeLocationForm = () => {
    setShowLocationForm(false);
    setSelectedLocationForForm(null);
    // Clear form data when closing
    setCurrentEntry({
      observation: '',
      replication: '',
      culture: '',
      value: ''
    });
    setFarmerFeedback('');
    setLocationInchargeFeedback('');
    setUploadedPhotos([]);
    setCurrentTrialData(null);
    setSavedEntries([]);
    setEditingEntry(null);
    setIsEditMode(false);
  };

  // Load trial data for the selected location
  const loadTrialDataForLocation = async (location) => {
    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Get trial data from officer assignments
      const response = await officerAssignmentsService.getTrialAssignments(user.id);
      if (response.success && response.data.trials) {
        // Find the trial that matches the current trial context
        const trialData = response.data.trials.find(trial => 
          trial.id === currentTrial?.id || 
          trial.code === currentTrial?.code
        );
        
        if (trialData) {
          setCurrentTrialData(trialData);
        }
      }
    } catch (error) {
      console.error('Error loading trial data for location:', error);
      toast.error('Failed to load trial data');
    }
  };

  // Handle form field changes
  const handleEntryChange = (field, value) => {
    setCurrentEntry(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save/update entry
  const handleSaveEntry = async () => {
    if (!currentEntry.observation || !currentEntry.replication || !currentEntry.culture || !currentEntry.value) {
      toast.error('Please fill all fields: Observation, Replication, Culture, and Value');
      return;
    }

    if (isNaN(currentEntry.value) || currentEntry.value <= 0) {
      toast.error('Please enter a valid positive number for the value');
      return;
    }

    try {
      setIsSavingEntry(true);

      // Prepare images data for JSON stringify (only IDs)
      const imagesData = uploadedPhotos.map(photo => ({
        id: photo.serverId || photo.id
      }));

      // Prepare payload in the required format
      const payload = {
        officer_id: user?.id || 5, // Use current user ID or default to 5
        entry_date: new Date().toISOString().split('T')[0], // Format: YYYY-MM-DD
        observation: currentEntry.observation,
        replication: currentEntry.replication,
        culture: currentEntry.culture,
        value: parseFloat(currentEntry.value),
        status: 'active',
        location_id: selectedLocationForForm?.id, // Add location_id to the payload
        image: JSON.stringify(imagesData), // Add images as JSON string
        farmer_feedback: farmerFeedback || '', // Add farmer feedback
        incharge_feedback: locationInchargeFeedback || '' // Add location incharge feedback
      };

      let response;
      if (isEditMode && editingEntry) {
        // Update existing entry
        response = await trialDataEntryApiService.updateTrialDataEntry(editingEntry.id, payload);
      } else {
        // Create new entry
        response = await trialDataEntryApiService.createTrialDataEntry(payload);
      }

      if (response.success) {
        toast.success(isEditMode ? 'Entry updated successfully!' : 'Entry saved successfully!');
        
        // Clear all fields after successful save/update
        setCurrentEntry({
          observation: '',
          replication: '',
          culture: '',
          value: ''
        });
        setUploadedPhotos([]);
        setFarmerFeedback('');
        setLocationInchargeFeedback('');
        setEditingEntry(null);
        setIsEditMode(false);
        
        // Reload saved entries
        if (selectedLocationForForm?.id) {
          await loadSavedEntries(selectedLocationForForm.id);
        }
      } else {
        throw new Error(response.message || 'Failed to save entry');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error(error.message || 'Failed to save entry');
    } finally {
      setIsSavingEntry(false);
    }
  };

  // Load saved entries for the selected location
  const loadSavedEntries = async (locationId) => {
    try {
      setIsLoadingSavedEntries(true);
      const response = await trialDataEntryApiService.getTrialDataEntries({
        location_id: locationId,
        page: 1,
        limit: 100
      });
      
      if (response.success) {
        setSavedEntries(response.data || []);
      } else {
        throw new Error(response.message || 'Failed to load saved entries');
      }
    } catch (error) {
      console.error('Error loading saved entries:', error);
      toast.error('Failed to load saved entries');
      setSavedEntries([]);
    } finally {
      setIsLoadingSavedEntries(false);
    }
  };

  // Handle edit entry
  const handleEditEntry = async (entry) => {
    try {
      // Fetch full entry details
      const response = await trialDataEntryApiService.getTrialDataEntry(entry.id);
      if (response.success) {
        const entryData = response.data;
        
        // Populate form fields
        setCurrentEntry({
          observation: entryData.observation || '',
          replication: entryData.replication || '',
          culture: entryData.culture || '',
          value: entryData.value || ''
        });
        
        setFarmerFeedback(entryData.farmer_feedback || '');
        setLocationInchargeFeedback(entryData.incharge_feedback || '');
        
        // Handle images if they exist
        if (entryData.image) {
          try {
            const imageData = JSON.parse(entryData.image);
            if (Array.isArray(imageData) && imageData.length > 0) {
              // Load images from the server
              const loadedPhotos = await Promise.all(
                imageData.map(async (img, index) => {
                  try {
                    // Download image from server
                    const imageBlob = await trialDataEntryApiService.downloadDocument(img.id);
                    const imageUrl = URL.createObjectURL(imageBlob);
                    
                    return {
                      id: img.id || Date.now() + index,
                      serverId: img.id,
                      name: `Image ${index + 1}`,
                      size: 0, // Unknown size from server
                      type: 'image/jpeg', // Default type
                      preview: imageUrl,
                      uploaded: true
                    };
                  } catch (error) {
                    console.error(`Error loading image ${img.id}:`, error);
                    // Return a placeholder for failed images
                    return {
                      id: img.id || Date.now() + index,
                      serverId: img.id,
                      name: `Image ${index + 1} (Failed to load)`,
                      size: 0,
                      type: 'image/jpeg',
                      preview: '', // No preview for failed images
                      uploaded: true,
                      error: true
                    };
                  }
                })
              );
              setUploadedPhotos(loadedPhotos);
            } else {
              setUploadedPhotos([]);
            }
          } catch (e) {
            console.error('Error parsing image data:', e);
            setUploadedPhotos([]);
          }
        } else {
          setUploadedPhotos([]);
        }
        
        setEditingEntry(entryData);
        setIsEditMode(true);
        
        toast.success('Entry loaded for editing');
      } else {
        throw new Error(response.message || 'Failed to load entry for editing');
      }
    } catch (error) {
      console.error('Error loading entry for editing:', error);
      toast.error('Failed to load entry for editing');
    }
  };

  // Handle delete entry
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      const response = await trialDataEntryApiService.deleteTrialDataEntry(entryId);
      if (response.success) {
        toast.success('Entry deleted successfully!');
        
        // Reload saved entries
        if (selectedLocationForForm?.id) {
          await loadSavedEntries(selectedLocationForForm.id);
        }
      } else {
        throw new Error(response.message || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.message || 'Failed to delete entry');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setCurrentEntry({
      observation: '',
      replication: '',
      culture: '',
      value: ''
    });
    setFarmerFeedback('');
    setLocationInchargeFeedback('');
    setUploadedPhotos([]);
    setEditingEntry(null);
    setIsEditMode(false);
  };

  // Photo upload functions
  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    try {
      setIsSavingEntry(true);
      
      // Upload photos to API using the new endpoint
      const uploadPromises = files.map(async (file) => {
        const response = await trialDataEntryApiService.uploadImage(file);
        
        if (response.success) {
          return {
            id: response.data.id || Date.now() + Math.random(),
            serverId: response.data.id,
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            preview: URL.createObjectURL(file),
            uploaded: true
          };
        } else {
          throw new Error(response.message || 'Failed to upload photo');
        }
      });
      
      const newPhotos = await Promise.all(uploadPromises);
      setUploadedPhotos(prev => [...prev, ...newPhotos]);
      toast.success(`${files.length} photo(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload photos');
      
      // Fallback to local preview only
      const newPhotos = files.map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file),
        uploaded: false
      }));
      setUploadedPhotos(prev => [...prev, ...newPhotos]);
    } finally {
      setIsSavingEntry(false);
    }
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo?.preview) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.id !== photoId);
    });
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Create a fake event object to reuse the upload function
      const fakeEvent = {
        target: {
          files: files
        }
      };
      handlePhotoUpload(fakeEvent);
    }
  };

  // Cleanup object URLs when component unmounts or photos change
  useEffect(() => {
    return () => {
      uploadedPhotos.forEach(photo => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    };
  }, [uploadedPhotos]);

  // Assignment management functions

  // Handle update centre assignment
  const handleUpdateCentreAssignment = async (assignmentId, assignmentData) => {
    try {
      if (!selectedCentre?.id) {
        throw new Error('No centre selected');
      }

      const response = await trialCentresService.updateTrialCentreAssignment(
        selectedCentre.id, 
        assignmentId, 
        assignmentData
      );
      
      if (response.success) {
        toast.success('Assignment updated successfully');
        // Reload centre assignments
        await loadCentreAssignments(selectedCentre.id);
      } else {
        throw new Error(response.message || 'Failed to update assignment');
      }
    } catch (error) {
      console.error('Error updating centre assignment:', error);
      toast.error(error.message || 'Failed to update assignment');
    }
  };

  // Handle create centre assignment
  const handleCreateCentreAssignment = async (assignmentData) => {
    try {
      if (!selectedCentre?.id) {
        throw new Error('No centre selected');
      }

      const response = await trialCentresService.createTrialCentreAssignment(
        selectedCentre.id, 
        assignmentData
      );
      
      if (response.success) {
        toast.success('Assignment created successfully');
        // Reload centre assignments
        await loadCentreAssignments(selectedCentre.id);
      } else {
        throw new Error(response.message || 'Failed to create assignment');
      }
    } catch (error) {
      console.error('Error creating centre assignment:', error);
      toast.error(error.message || 'Failed to create assignment');
    }
  };

  // Handle delete centre assignment
  const handleDeleteCentreAssignment = async (assignmentId) => {
    try {
      if (!selectedCentre?.id) {
        throw new Error('No centre selected');
      }

      const response = await trialCentresService.deleteTrialCentreAssignment(
        selectedCentre.id, 
        assignmentId
      );
      
      if (response.success) {
        toast.success('Assignment deleted successfully');
        // Reload centre assignments
        await loadCentreAssignments(selectedCentre.id);
      } else {
        throw new Error(response.message || 'Failed to delete assignment');
      }
    } catch (error) {
      console.error('Error deleting centre assignment:', error);
      toast.error(error.message || 'Failed to delete assignment');
    }
  };

  // Load centre incharge assignments
  const loadCentreInchargeAssignments = async (centreId) => {
    if (!centreId) return;

    try {
      setIsLoadingCentreInchargeAssignments(true);
      const response = await trialCentresService.getTrialCentreAssignments(centreId);

      if (response.success && response.data) {
        setCentreInchargeAssignments(response.data);
        console.log('✅ Centre incharge assignments loaded:', response.data);
      } else {
        console.warn('⚠️ Failed to load centre incharge assignments:', response.message);
        setCentreInchargeAssignments([]);
      }
    } catch (error) {
      console.error('❌ Error loading centre incharge assignments:', error);
      setCentreInchargeAssignments([]);
    } finally {
      setIsLoadingCentreInchargeAssignments(false);
    }
  };

  // Add centre incharge assignment
  const handleAddCentreInchargeAssignment = async () => {
    if (!selectedCentre?.id) {
      toast.error('No centre selected');
      return;
    }

    if (!selectedCentre.centreIncharge) {
      toast.error('Please select a Centre Incharge');
      return;
    }

    if (!centreInchargeValidityFrom) {
      toast.error('Please select a From Date');
      return;
    }

    try {
      const assignmentData = {
        centre_incharge: selectedCentre.centreIncharge,
        officer_id: selectedCentre.centreInchargeId,
        assignment_date: centreInchargeValidityFrom,
        status: 'active'
      };

      const response = await trialCentresService.createTrialCentreAssignment(selectedCentre.id, assignmentData);
      
      if (response.success) {
        toast.success('Centre Incharge assignment added successfully!');
        
        // Reset form
        setCentreInchargeValidityFrom('');
        setSelectedCentre(prev => ({ 
          ...prev, 
          centreIncharge: '',
          centre_incharge: '',
          centreInchargeId: ''
        }));
        
        // Reload assignments
        await loadCentreInchargeAssignments(selectedCentre.id);
      } else {
        throw new Error(response.message || 'Failed to add centre incharge assignment');
      }
    } catch (error) {
      console.error('Error adding centre incharge assignment:', error);
      toast.error(error.message || 'Failed to add centre incharge assignment');
    }
  };

  // Delete centre incharge assignment
  const handleDeleteCentreInchargeAssignment = async (assignmentId) => {
    if (!selectedCentre?.id) {
      toast.error('No centre selected');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      const response = await trialCentresService.deleteTrialCentreAssignment(selectedCentre.id, assignmentId);
      
      if (response.success) {
        toast.success('Assignment deleted successfully!');
        
        // Reload assignments
        await loadCentreInchargeAssignments(selectedCentre.id);
      } else {
        throw new Error(response.message || 'Failed to delete assignment');
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error(error.message || 'Failed to delete assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    
    try {
      const response = await locationManagementService.deleteLocationAssignment(editingLocation.id, assignmentId);
      
      if (response.success) {
        toast.success('Assignment deleted successfully');
        await loadLocationAssignments(editingLocation.id);
      } else {
        throw new Error(response.message || 'Failed to delete assignment');
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error(error.message || 'Failed to delete assignment');
    }
  };

  const generateLocationCode = (trialCode) => {
    const existingAssignments = trialAssignments.filter(assignment => assignment.trialId === detailId);
    const nextNumber = (existingAssignments.length + 1).toString().padStart(3, '0');
    return `${trialCode}L${nextNumber}`;
  };

  // Generate unique centre code for new centres
  const generateCentreCode = (trialCode) => {
    const existingCentres = trialAssignments.filter(assignment => 
      assignment.trialId === detailId && assignment.locationName && !assignment.locationIncharge
    );
    const nextNumber = (existingCentres.length + 1).toString().padStart(3, '0');
    return `CENTRE${nextNumber}`;
  };

  // Handle create centre
  const handleCreateCentre = async () => {
    try {
      setIsCreatingTrialCentre(true);
      
      // Prepare centre data
      const centreData = {
        centre_name: assignmentCentre,
        centre_code: assignmentCentreCode || generateCentreCode('CENTRE'),
        centre_incharge: assignmentCentreIncharge,
        from_date: assignmentFromDate,
        status: 'active'
      };
      
      // Call API to create trial centre
      const response = await trialCentresService.createTrialCentre(centreData);
      
      if (response.success) {
        toast.success('Centre created successfully!');
        
        // Clear form fields
        setAssignmentCentre('');
        setAssignmentCentreCode('');
        setAssignmentCentreIncharge('');
        setAssignmentFromDate('');
        
        // Refresh trial centres data
        await loadTrialCentres();
      } else {
        throw new Error(response.message || 'Failed to create centre');
      }
    } catch (error) {
      console.error('Error creating centre:', error);
      toast.error(error.message || 'Failed to create centre');
    } finally {
      setIsCreatingTrialCentre(false);
    }
  };

  // Load officer trial centres
  const loadOfficerTrialCentres = async (page = 1, limit = 10) => {
    if (!user?.id) return;
    
    try {
      setIsLoadingOfficerTrialCentres(true);
      const response = await officerAssignmentsService.getTrialCentres(user.id, {
        page,
        limit
      });
      
      if (response.success && response.data) {
        setOfficerTrialCentres(response.data.trial_centres || []);
        setOfficerTrialCentresPagination(response.data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0
        });
        console.log('✅ Officer trial centres loaded:', response.data);
      } else {
        console.warn('⚠️ Failed to load officer trial centres:', response.message);
        setOfficerTrialCentres([]);
        setOfficerTrialCentresPagination({
          page: 1,
          limit: 10,
          total: 0,
          pages: 0
        });
      }
    } catch (error) {
      console.error('❌ Error loading officer trial centres:', error);
      setOfficerTrialCentres([]);
      setOfficerTrialCentresPagination({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      });
    } finally {
      setIsLoadingOfficerTrialCentres(false);
    }
  };

  // Handle officer trial centres page change
  const handleOfficerTrialCentresPageChange = (newPage) => {
    setOfficerTrialCentresPagination(prev => ({ ...prev, page: newPage }));
    loadOfficerTrialCentres(newPage, officerTrialCentresPagination.limit);
  };

  // Handle officer trial centres page size change
  const handleOfficerTrialCentresPageSizeChange = (newLimit) => {
    setOfficerTrialCentresPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    loadOfficerTrialCentres(1, newLimit);
  };

  // Handle delete trial centre
  const handleDeleteTrialCentre = async (centreId) => {
    try {
      setDeletingTrialCentre(centreId);
      
      // Call API to delete trial centre
      const response = await trialCentresService.deleteTrialCentre(centreId);
      
      if (response.success) {
        toast.success('Centre deleted successfully!');
        
        // Refresh trial centres data
        await loadOfficerTrialCentres(officerTrialCentresPagination.page, officerTrialCentresPagination.limit);
      } else {
        throw new Error(response.message || 'Failed to delete centre');
      }
    } catch (error) {
      console.error('Error deleting centre:', error);
      toast.error(error.message || 'Failed to delete centre');
    } finally {
      setDeletingTrialCentre(null);
    }
  };

  // Load location assignments for a specific officer
  const loadLocationAssignmentsForOfficer = async (officerId, page = 1, limit = 10) => {
    if (!officerId) return;
    
    try {
      setIsLoadingLocationAssignments(true);
      const response = await officerAssignmentsService.getLocationAssignments(officerId, { page, limit });
      
      if (response.success && response.data) {
        // Handle the nested structure: response.data.locations
        const assignments = Array.isArray(response.data.locations) ? response.data.locations : [];
        setLocationAssignments(assignments);
        
        // Handle pagination
        if (response.data.pagination) {
          setLocationAssignmentsPagination(response.data.pagination);
        }
        
        console.log('✅ Location assignments loaded:', assignments);
        console.log('✅ Pagination info:', response.data.pagination);
      } else {
        console.warn('⚠️ Failed to load location assignments:', response.message);
        setLocationAssignments([]);
        setLocationAssignmentsPagination({ page: 1, limit: 10, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('❌ Error loading location assignments:', error);
      setLocationAssignments([]);
      setLocationAssignmentsPagination({ page: 1, limit: 10, total: 0, pages: 0 });
    } finally {
      setIsLoadingLocationAssignments(false);
    }
  };

  // Pagination handlers for location assignments
  const handleLocationAssignmentsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= locationAssignmentsPagination.pages) {
      let user = localStorage.getItem('user');
      if(user){
          user = JSON.parse(user);
      }
      if (user?.id) {
        loadLocationAssignmentsForOfficer(user.id, newPage, locationAssignmentsPagination.limit);
      }
    }
  };

  const handleLocationAssignmentsPageSizeChange = (newPageSize) => {
    let user = localStorage.getItem('user');
    if(user){
        user = JSON.parse(user);
    }
    if (user?.id) {
      loadLocationAssignmentsForOfficer(user.id, 1, newPageSize);
    }
  };

  // Load centre incharge data for edit modal
  const loadCentreInchargeData = async (centreId) => {
    try {
      setIsLoadingOfficers(true);
      
      // Load officers for dropdown
      const officersResponse = await officerService.getOfficers();
      if (officersResponse.success) {
        setDynamicOfficers(officersResponse.data || []);
        console.log('✅ Officers loaded:', officersResponse.data);
      }
      
      // Load centre assignments to get current incharge
      await loadCentreAssignments(centreId);
      
      // Load centre incharge assignments
      await loadCentreInchargeAssignments(centreId);
      
      // Also load the specific centre data to get current incharge
      try {
        const centreResponse = await trialCentresService.getTrialCentre(centreId);
        if (centreResponse.success && centreResponse.data) {
          const centreData = centreResponse.data;
          console.log('✅ Centre data loaded:', centreData);
          
          // Update selectedCentre with the loaded data
          setSelectedCentre(prev => ({
            ...prev,
            centre_incharge: centreData.centre_incharge || centreData.assignment_centre_incharge,
            centreIncharge: centreData.centre_incharge || centreData.assignment_centre_incharge
          }));
        }
      } catch (centreError) {
        console.warn('Could not load specific centre data:', centreError);
      }
      
    } catch (error) {
      console.error('Error loading centre incharge data:', error);
      toast.error('Failed to load centre incharge data');
    } finally {
      setIsLoadingOfficers(false);
    }
  };



  function getTrialAssignments(trialId) {
    if (!Array.isArray(trialAssignments) || !trialId) {
      return [];
    }
    return trialAssignments.filter((assignment) => assignment.trialId === trialId);
  }

  // For the centre-creation tab, show a compact list of centre names per trial
  const getCentreNames = (trialId) => {
    if (!trialId) return '';
    try {
      const names = getTrialAssignments(trialId)
        .map(a => a.locationName)
        .filter(Boolean);
      return Array.from(new Set(names)).join(', ');
    } catch {
      return '';
    }
  };

  // Unique list for linking
  const getCentreList = (trialId) => {
    if (!trialId) return [];
    try {
      const names = getTrialAssignments(trialId)
        .map(a => a.locationName)
        .filter(Boolean);
      return Array.from(new Set(names));
    } catch {
      return [];
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const getTrialTypeColor = (type) => {
    return type === 'ART' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600';
  };

  const getSeasonColor = (season) => {
    switch (season) {
      case 'Kharif':
        return 'bg-green-100 text-green-600';
      case 'Rabi':
        return 'bg-blue-100 text-blue-600';
      case 'Winter':
        return 'bg-gray-100 text-gray-600';
      case 'Summer':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const filteredObservations = useMemo(
    () => (Array.isArray(observations) ? observations : []).filter((o) => 
      typeof o === 'string' && o.toLowerCase().includes((observationQuery || '').toLowerCase())
    ),
    [observations, observationQuery]
  );

  const formatObservationsWithDays = useCallback(
    (list) => (list || []).map((obs) => {
      const d = assignmentObservationDays?.[obs];
      return d ? `${obs} (${d} days)` : obs;
    }).join(', '),
    [assignmentObservationDays]
  );

  // Add Location Modal State
  const [newLocationData, setNewLocationData] = useState({
    locationName: '',
    locationIncharge: '',
    sfNumber: '',
    latitude: '',
    longitude: '',
    locationAddress: '',
    experimentalArea: '',
    plotSize: '',
    soilType: '',
    soilTestDate: '',
    soilTestDetails: '',
    irrigationSource: '',
    previousCrop: '',
    seedTreatment: '',
    ploughing: '',
    sowingDate: '',
    plantingDate: '',
    basalOrganic: '',
    basalOrganicDate: '',
    basalOrganicQuantity: '',
    basalFertilizerDate: '',
    basalFertilizerName: '',
    basalFertilizerQuantity: '',
    topDressingDate: '',
    topDressingName: '',
    topDressingQuantity: '',
    weedManagement: '',
    plantProtection: '',
    harvestDate: '',
    monitoringVisitDate: '',
    monitoringComments: '',
    plotOfficerVisitDate: '',
    plotOfficerComments: '',
    inspectionVisitDate: '',
    inspectionComments: '',
    fieldDayDate: '',
    farmersFeedback: ''
  });

  return (
    <div className="space-y-6">
      {(isDetailView || isLocationsView) ? (
        <div className="rounded-xl shadow-soft border border-gray-100 p-6">
          <div className="space-y-6">
            {/* Trial Details Section - Moved above Centre Management title */}
            {isDetailView && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-primary-600" />
                  Trial Details
                  {isLoadingTrial && (
                    <div className="ml-2 w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </h3>
                
                {isLoadingTrial ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500">Loading trial details...</div>
                  </div>
                ) : currentTrial ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Trial Code</label>
                        <div className="text-gray-900 font-medium">{currentTrial.code || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Trial Type</label>
                        <div className="text-gray-900 font-medium">{currentTrial.trial_type || currentTrial.type || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Crop</label>
                        <div className="text-gray-900 font-medium">{currentTrial.crop_name || currentTrial.crop || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Season</label>
                        <div className="text-gray-900 font-medium">{currentTrial.season_name || currentTrial.season || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                        <div className="text-gray-900 font-medium">{currentTrial.year || 'N/A'}</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Replications</label>
                        <div className="text-gray-900 font-medium">{currentTrial.replications || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <div className="text-gray-900 font-medium">{currentTrial.status || 'Active'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Created Date</label>
                        <div className="text-gray-900 font-medium">
                          {currentTrial.created_at ? new Date(currentTrial.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Observations and Trials Culture - Side by Side */}
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Observations Section */}
                      {currentTrial.observations && currentTrial.observations.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Observations</label>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-gray-900">
                              {currentTrial.observations.map(obs => `${obs.observation} (${obs.days} days)`).join(', ')}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Trials Culture Section */}
                      {currentTrial.trials && currentTrial.trials.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Trials Culture</label>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-gray-900">
                              {currentTrial.trials.map(trial => `${trial.culture}`).join(', ')}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-500">Failed to load trial details</div>
                  </div>
                )}
              </div>
            )}

            {/* Trial Details Section - Moved above Location Management title */}
            {isLocationsView && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-primary-600" />
                  Trial Details
                  {isLoadingTrial && (
                    <div className="ml-2 w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </h3>
                
                {isLoadingTrial ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500">Loading trial details...</div>
                  </div>
                ) : currentTrial ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Trial Code</label>
                        <div className="text-gray-900 font-medium">{currentTrial.code || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Trial Type</label>
                        <div className="text-gray-900 font-medium">{currentTrial.trial_type || currentTrial.type || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Crop</label>
                        <div className="text-gray-900 font-medium">{currentTrial.crop_name || currentTrial.crop || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Season</label>
                        <div className="text-gray-900 font-medium">{currentTrial.season_name || currentTrial.season || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                        <div className="text-gray-900 font-medium">{currentTrial.year || 'N/A'}</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Replications</label>
                        <div className="text-gray-900 font-medium">{currentTrial.replications || 'N/A'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <div className="text-gray-900 font-medium">{currentTrial.status || 'Active'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Created Date</label>
                        <div className="text-gray-900 font-medium">
                          {currentTrial.created_at ? new Date(currentTrial.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Observations and Trials Culture - Side by Side */}
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Observations Section */}
                      {currentTrial.observations && currentTrial.observations.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Observations</label>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-gray-900">
                              {currentTrial.observations.map(obs => `${obs.observation} (${obs.days} days)`).join(', ')}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Trials Culture Section */}
                      {currentTrial.trials && currentTrial.trials.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Trials Culture</label>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-gray-900">
                              {currentTrial.trials.map(trial => `${trial.culture}`).join(', ')}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-500">Failed to load trial details</div>
                  </div>
                )}
              </div>
            )}

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                {!(user?.role === 'officer' && officerSource === 'centre' && !isLocationsView) && (
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {isLocationsView ? 'Location Management' : 'Centre Management'}
                    </h1>
                    <p className="text-gray-600">
                      {isLocationsView ? (() => {
                        const centreId = searchParams.get('centre');
                        if (!centreId || centreId === 'undefined') return 'All Centres';
                        // Find the centre name by ID
                        const centre = trialCentres.find(c => c.id == centreId || c.trial_centre_id === centreId);
                        return centre ? centre.centre_name : '';
                      })() : `${filteredTrials[0]?.name || ''}`}
                    </p>
                  </div>
                )}
              </div>
              {!isLocationsView && null}
            </div>









            {/* Location Management Form - Combined Add Location and Incharge Validity */}
            {isLocationsView && !searchParams.get('location') && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-green-600" />
                  Add New Location
                </h3>
                
                {/* Step 1: Basic Location Details */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-green-600" />
                    Location Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Location Name *</label>
                      <input 
                        type="text" 
                        className="input-field border-2 focus:border-green-500" 
                        placeholder="e.g., Research Station"
                        value={newLocationData.locationName}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, locationName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">SF Number</label>
                      <input 
                        type="text" 
                        className="input-field border-2 focus:border-green-500" 
                        placeholder="SF001"
                        value={newLocationData.sfNumber}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, sfNumber: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                      <input 
                        type="text" 
                        className="input-field border-2 focus:border-green-500" 
                        placeholder="11.0168"
                        value={newLocationData.latitude}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, latitude: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                      <input 
                        type="text" 
                        className="input-field border-2 focus:border-green-500" 
                        placeholder="76.9558"
                        value={newLocationData.longitude}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, longitude: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Location Address</label>
                      <textarea 
                        rows="3" 
                        className="input-field border-2 focus:border-green-500" 
                        placeholder="Complete address of the location"
                        value={newLocationData.locationAddress}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, locationAddress: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Step 2: Location Incharge Validity */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-green-600" />
                    Location Incharge Assignment
                  </h4>
                  
                  {/* Add New Location Incharge Validity Period */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location Incharge *</label>
                      <select
                        value={newLocationData.locationIncharge}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, locationIncharge: e.target.value }))}
                        className="input-field border-2 focus:border-green-500"
                        disabled={isLoadingOfficers}
                      >
                        <option value="">
                          {isLoadingOfficers ? 'Loading officers...' : 'Select Location Incharge'}
                        </option>
                        {dynamicOfficers.length > 0 ? (
                          dynamicOfficers.map((officer) => (
                            <option key={officer.id || officer} value={officer.id || officer}>
                              {officer.name || officer}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No officers available</option>
                        )}
                      </select>
                      {!isLoadingOfficers && (
                        <p className="text-xs text-gray-500 mt-1">
                          {dynamicOfficers.length > 0 
                            ? ``
                            : `No officers available`
                          }
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                      <input
                        type="date"
                        value={locationInchargeValidityFrom}
                        onChange={(e) => setLocationInchargeValidityFrom(e.target.value)}
                        className="input-field border-2 focus:border-green-500"
                        placeholder="Start date"
                      />
                    </div>
                  </div>
                  

                  

                </div>

                {/* Step 3: Add Location Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={async () => {
                      if (!newLocationData.locationName || !newLocationData.locationIncharge) {
                        toast.error('Please fill in Location Name and Location Incharge');
                        return;
                      }
                      if (!locationInchargeValidityFrom) {
                        toast.error('Please select a From Date');
                        return;
                      }
                      
                      // Prepare location data for API
                      const locationPayload = {
                        trial_centre_id: parseInt(searchParams.get('centre')),
                        location_name: newLocationData.locationName,
                        sf_number: newLocationData.sfNumber || '',
                        latitude: newLocationData.latitude ? parseFloat(newLocationData.latitude) : null,
                        longitude: newLocationData.longitude ? parseFloat(newLocationData.longitude) : null,
                        address: newLocationData.locationAddress || '',
                        location_incharge_id: parseInt(newLocationData.locationIncharge),
                        location_incharge: dynamicOfficers.find(o => o.id == newLocationData.locationIncharge)?.name || '',
                        from_date: locationInchargeValidityFrom,
                        status: 'active'
                      };

                      // Call API to create location
                      const success = await handleCreateLocation(locationPayload);
                      
                      if (success) {
                        // Reset form data
                        setNewLocationData({
                          locationName: '',
                          locationIncharge: '',
                          sfNumber: '',
                          latitude: '',
                          longitude: '',
                          locationAddress: '',
                          experimentalArea: '',
                          plotSize: '',
                          soilType: '',
                          soilTestDate: '',
                          soilTestDetails: '',
                          irrigationSource: '',
                          previousCrop: '',
                          seedTreatment: '',
                          ploughing: '',
                          sowingDate: '',
                          plantingDate: '',
                          basalOrganic: '',
                          basalOrganicDate: '',
                          basalOrganicQuantity: '',
                          basalFertilizerDate: '',
                          basalFertilizerName: '',
                          basalFertilizerQuantity: '',
                          topDressingDate: '',
                          topDressingName: '',
                          topDressingQuantity: '',
                          weedManagement: '',
                          plantProtection: '',
                          harvestDate: '',
                          monitoringVisitDate: '',
                          monitoringComments: '',
                          plotOfficerVisitDate: '',
                          plotOfficerComments: '',
                          inspectionVisitDate: '',
                          inspectionComments: '',
                          fieldDayDate: '',
                          farmersFeedback: ''
                        });
                        
                        // Reset date field
                        setLocationInchargeValidityFrom('');
                      }
                    }}
                    disabled={isCreatingLocation}
                    className="btn-primary bg-green-600 hover:bg-green-700 text-white px-6 py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreatingLocation ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Saving...
                      </div>
                    ) : (
                      'Save Location'
                    )}
                  </button>
                </div>
              </div>
            )}




            {/* Trials Table - Hidden in Location Management view and when no data */}
            {!isLocationsView && (() => {
              // Hide table if in Centre Management view but no trial centres data
              if (isDetailView) {
                return trialCentres.length > 0 || (detailId && getTrialAssignments(detailId).length > 0);
              }
              // Show table for other views
              return true;
            })() && (
            <div className="rounded-xl shadow-soft border border-gray-100 overflow-hidden">
              {/* Trial Centres Header with Refresh Button */}
              {isDetailView && (
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Trial Centres</h3>
                  <button
                    onClick={() => loadTrialCentres()}
                    disabled={isLoadingTrialCentres}
                    className="btn-outline flex items-center text-sm"
                  >
                    <div className={`w-4 h-4 mr-2 ${isLoadingTrialCentres ? 'animate-spin' : ''}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    {isLoadingTrialCentres ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {isDetailView ? 'Centre Code' : (searchParams.get('view') === 'locations' ? 'Location Code' : 'Trial Code')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trial Type
                      </th>
                      {searchParams.get('view') === 'locations' && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location Name
                        </th>
                      )}
                      {searchParams.get('view') === 'locations' && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location Incharge
                        </th>
                      )}
                      {searchParams.get('view') === 'locations' && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Incharge Number
                        </th>
                      )}

                      {isDetailView && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Centre Name
                        </th>
                      )}
                      {isDetailView && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Centre Incharge
                        </th>
                      )}
                      {isDetailView && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Incharge Number
                        </th>
                      )}


                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {isLoadingTrialCentres && isDetailView ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-2"></div>
                            <span className="text-gray-500">Loading trial centres...</span>
                          </div>
                        </td>
                      </tr>
                    ) : (searchParams.get('view') === 'locations' ? (detailId ? getTrialAssignments(detailId).filter(a => {
                      const paramCentre = searchParams.get('centre');
                      return !paramCentre || paramCentre === 'undefined' || a.trial_centre_id === paramCentre || a.id === paramCentre;
                    }) : []) : isDetailView ? (trialCentres.length > 0 ? trialCentres : (detailId ? getTrialAssignments(detailId) : [])) : filteredTrials).map((item) => {
                      const isLoc = searchParams.get('view') === 'locations';
                      const isDetail = isDetailView && !isLoc;
                      const trial = isLoc ? (trials.find(t => t.id === item.trialId) || {}) : (isDetail ? trials.find(t => t.id === item.trialId) || {} : item);
                      const locationName = isLoc ? item.locationName : null;
                      return (
                      <tr 
                        key={isDetail ? item.id : trial.id} 
                        className="bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => {
                          if (isLoc) {
                            // Open location trial details modal for viewing
                            openLocationTrialDetailsModal(item);
                          } else if (isDetail) {
                            // Navigate to Location Management for this centre
                            const centreId = item.id || item.trial_centre_id;
                            const qs = `?view=locations&id=${detailId}&centre=${centreId}`;
                            if (user?.role === 'officer') {
                              navigate({ search: qs });
                            } else {
                              navigate(`/trial-creation${qs}`);
                            }
                          }
                        }}
                      >

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono font-semibold text-blue-600">
                            {isDetail ? (trialCentres.length > 0 ? item.centre_code : (item.locationCode || '-')) : trial.code}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getTrialTypeColor(trial.trial_type || trial.type)}`}>
                            {trialCentres.length > 0 ? 'Trial Centre' : (trial.trial_type || trial.type)}
                          </span>
                        </td>
                        {isLoc && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(() => {
                                const centreId = searchParams.get('centre');
                                if (!centreId || centreId === 'undefined') return 'All Centres';
                                // Find the centre name by ID
                                const centre = trialCentres.find(c => c.id == centreId || c.trial_centre_id === centreId);
                                return centre ? centre.centre_name : '';
                              })()}
                            </div>
                          </td>
                        )}
                        {isLoc && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{item.assignedOfficer || '-'}</div>
                          </td>
                        )}
                        {isLoc && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {item.inchargeFromDate ? new Date(item.inchargeFromDate).getFullYear() : '-'}
                            </div>
                          </td>
                        )}

                        {isDetailView && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isDetail ? (
                              <div className="text-sm text-gray-900">
                                {trialCentres.length > 0 ? item.centre_name : (item.locationName || '-')}
                              </div>
                            ) : (
                              getCentreList(trial.id).length > 0 ? (
                              (() => {
                                const centreData = getCentreList(trial.id)[0];
                                const centreId = centreData?.id || centreData?.trial_centre_id;
                                const centreName = centreData?.centre_name || centreData?.locationName || centreData;
                                if (!centreId) return <div className="text-sm text-gray-500">-</div>;
                                return (
                                  <button
                                    onClick={() => {
                                      const qs = `?view=locations&id=${trial.id}&centre=${centreId}`;
                                      if (user?.role === 'officer') {
                                        navigate({ search: qs });
                                      } else {
                                        const newUrl = `/trial-creation${qs}`;
                                        navigate(newUrl);
                                      }
                                    }}
                                    className="text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors truncate max-w-[420px]"
                                    title={`View locations for ${centreName}`}
                                  >
                                    {centreName}
                                  </button>
                                );
                              })()
                            ) : (
                              <div className="text-sm text-gray-500">-</div>
                              )
                            )}
                          </td>
                        )}
                        {isDetailView && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {isDetail ? (trialCentres.length > 0 ? item.centre_incharge : (item.centreIncharge || '-')) : (trial.id && getTrialAssignments(trial.id).length > 0 ? getTrialAssignments(trial.id)[0].centreIncharge || '-' : '-')}
                            </div>
                          </td>
                        )}
                        {isDetailView && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(() => {
                                if (isDetail) {
                                  if (trialCentres.length > 0) {
                                    return item.from_date ? new Date(item.from_date).getFullYear() : '-';
                                  }
                                  return item.inchargeFromDate ? new Date(item.inchargeFromDate).getFullYear() : '-';
                                } else if (trial.id && getTrialAssignments(trial.id).length > 0) {
                                  return getTrialAssignments(trial.id)[0].inchargeFromDate ? new Date(getTrialAssignments(trial.id)[0].inchargeFromDate).getFullYear() : '-';
                                } else {
                                  return '-';
                                }
                              })()}
                            </div>
                          </td>
                        )}


                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status || trial.status)}`}>
                            {item.status || trial.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {searchParams.get('view') === 'locations' ? (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedLocation(item);
                                    setShowLocationDetailsModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Edit Location"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Are you sure you want to delete the location "${item.locationName}"?`)) {
                                      // Remove the location from trialAssignments
                                      setTrialAssignments(prev => 
                                        prev.filter(assignment => assignment.id !== item.id)
                                      );
                                      toast.success('Location deleted successfully!');
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                  title="Delete Location"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            ) : isDetail ? (
                              <>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setSelectedCentre(item);
                                    // Load centre assignments before showing modal
                                    await loadCentreAssignments(item.id);
                                    setShowEditCentreModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Edit Centre"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const centreName = trialCentres.length > 0 ? item.centre_name : item.locationName;
                                    if (window.confirm(`Are you sure you want to delete the centre "${centreName}"?`)) {
                                      setDeletingTrialCentre(item.id);
                                      try {
                                        // Call API to delete trial centre
                                        await trialCentresService.deleteTrialCentre(item.id);
                                        
                                        // Refresh trial centres data
                                        await loadTrialCentres();
                                        
                                        toast.success('Trial centre deleted successfully!');
                                        console.log('✅ Trial centre deleted:', item.id);
                                      } catch (error) {
                                        console.error('❌ Failed to delete trial centre:', error);
                                        toast.error(`Failed to delete trial centre: ${error.message}`);
                                      } finally {
                                        setDeletingTrialCentre(null);
                                      }
                                    }
                                  }}
                                  disabled={deletingTrialCentre === item.id}
                                  className={`text-red-600 hover:text-red-800 ${deletingTrialCentre === item.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title={deletingTrialCentre === item.id ? 'Deleting...' : 'Delete Centre'}
                                >
                                  {deletingTrialCentre === item.id ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </button>
                              </>
                            ) : (
                            <button
                              onClick={() => handleDelete(trial.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete Trial"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {/* Location Management Table */}
            {isLocationsView && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-green-600" />
                    Location Management
                    {isLoadingLocationManagement && (
                      <div className="ml-2 w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <button
                    onClick={() => {
                      const trialCentreId = searchParams.get('centre');
                      if (trialCentreId) {
                        loadLocationManagement(trialCentreId, locationManagementPagination.page, locationManagementPagination.limit);
                      }
                    }}
                    disabled={isLoadingLocationManagement}
                    className="btn-outline flex items-center text-sm"
                  >
                    <div className={`w-4 h-4 mr-2 ${isLoadingLocationManagement ? 'animate-spin' : ''}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    {isLoadingLocationManagement ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SF Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Coordinates
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location Incharge
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          From Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoadingLocationManagement ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center">
                            <div className="flex items-center justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mr-2"></div>
                              <span className="text-gray-500">Loading locations...</span>
                            </div>
                          </td>
                        </tr>
                      ) : locationManagementData.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center">
                            <div className="text-center py-8">
                              <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                              <h3 className="text-lg font-medium text-gray-900 mb-2">No Locations Found</h3>
                              <p className="text-gray-500">No locations have been added to this centre yet.</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        locationManagementData.map((location) => (
                          <tr key={location.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleLocationRowClick(location)}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{location.location_name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 font-mono">{location.sf_number}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {location.latitude && location.longitude && 
                                 !isNaN(Number(location.latitude)) && !isNaN(Number(location.longitude))
                                  ? `${Number(location.latitude).toFixed(4)}, ${Number(location.longitude).toFixed(4)}`
                                  : '-'
                                }
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 max-w-xs truncate" title={location.address}>
                                {location.address || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{location.location_incharge || location.incharge_name || '-'}</div>
                              {location.incharge_email && (
                                <div className="text-xs text-gray-500">{location.incharge_email}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {location.from_date ? new Date(location.from_date).toLocaleDateString() : '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                location.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {location.status || 'Unknown'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={async () => {
                                    setSelectedLocationForDetails(location);
                                    setShowTrialDetailsModal(true);
                                    await loadTrialDetails(location.id);
                                  }}
                                  className="text-green-600 hover:text-green-900"
                                  title="View Trial Details"
                                >
                                  <Database className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openEditLocationModal(location)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit Location"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                {/* <button
                                  onClick={() => handleDeleteLocation(location.id)}
                                  disabled={deletingLocation === location.id}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                >
                                  {deletingLocation === location.id ? (
                                    <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </button> */}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {locationManagementPagination.pages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-700">
                          Showing {((locationManagementPagination.page - 1) * locationManagementPagination.limit) + 1} to{' '}
                          {Math.min(locationManagementPagination.page * locationManagementPagination.limit, locationManagementPagination.total)} of{' '}
                          {locationManagementPagination.total} results
                        </span>
                        <select
                          value={locationManagementPagination.limit}
                          onChange={(e) => handleLocationPageSizeChange(Number(e.target.value))}
                          className="ml-4 text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value={10}>10 per page</option>
                          <option value={25}>25 per page</option>
                          <option value={50}>50 per page</option>
                          <option value={100}>100 per page</option>
                        </select>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleLocationPageChange(locationManagementPagination.page - 1)}
                          disabled={locationManagementPagination.page <= 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-700">
                          Page {locationManagementPagination.page} of {locationManagementPagination.pages}
                        </span>
                        <button
                          onClick={() => handleLocationPageChange(locationManagementPagination.page + 1)}
                          disabled={locationManagementPagination.page >= locationManagementPagination.pages}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Location Data Entry Modal - Shows when row is clicked */}
            {showLocationForm && selectedLocationForForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Enter Trial Data - {selectedLocationForForm.location_name}
                    </h3>
                    <button
                      onClick={closeLocationForm}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="p-6">
                    {/* Trial Information */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Trial Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Location:</span> {selectedLocationForForm.location_name}
                        </div>
                        <div>
                          <span className="font-medium">SF Number:</span> {selectedLocationForForm.sf_number}
                        </div>
                        <div>
                          <span className="font-medium">Status:</span> 
                          <span className={`ml-1 px-2 py-1 text-xs rounded-full ${
                            selectedLocationForForm.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedLocationForForm.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Data Entry Form */}
                    <form className="space-y-6">
                      {/* Entry Date and Observation */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Entry Date
                          </label>
                          <input
                            type="date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            defaultValue={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Observation <span className="text-red-500">*</span>
                          </label>
                          <select 
                            value={currentEntry.observation}
                            onChange={(e) => handleEntryChange('observation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select Observation</option>
                            {currentTrialData?.observations?.map((obs, index) => (
                              <option key={index} value={`${obs.observation} (${obs.days} days)`}>
                                {obs.observation} ({obs.days} days)
                              </option>
                            )) || (
                              <>
                                <option value="germination">Germination %</option>
                                <option value="vigour">Germination Vigour</option>
                                <option value="population">Population</option>
                                <option value="plant_height">Plant height</option>
                                <option value="plant_width">Plant width</option>
                                <option value="branches">No. of branches</option>
                              </>
                            )}
                          </select>
                        </div>
                      </div>

                      {/* Replication and Culture */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Replication <span className="text-red-500">*</span>
                          </label>
                          <select 
                            value={currentEntry.replication}
                            onChange={(e) => handleEntryChange('replication', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select Replication</option>
                            {currentTrialData?.replications && Array.from({ length: parseInt(currentTrialData.replications) }, (_, i) => (
                              <option key={'R' + (i + 1)} value={'R' + (i + 1)}>
                                {'R' + (i + 1)}
                              </option>
                            )) || (
                              <>
                                <option value="R1">R1</option>
                                <option value="R2">R2</option>
                                <option value="R3">R3</option>
                                <option value="R4">R4</option>
                                <option value="R5">R5</option>
                              </>
                            )}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">Please select an observation first</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Culture <span className="text-red-500">*</span>
                          </label>
                          <select 
                            value={currentEntry.culture}
                            onChange={(e) => handleEntryChange('culture', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select Culture</option>
                            {currentTrialData?.trials?.map((trial, index) => (
                              <option key={index} value={trial.culture}>
                                {trial.culture}
                              </option>
                            )) || (
                              <>
                                <option value="v1">V1</option>
                                <option value="v2">V2</option>
                                <option value="v3">V3</option>
                                <option value="v4">V4</option>
                                <option value="v5">V5</option>
                              </>
                            )}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">Please select a replication first</p>
                        </div>
                      </div>

                      {/* Value Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter Value <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={currentEntry.value}
                          onChange={(e) => handleEntryChange('value', e.target.value)}
                          placeholder="Enter the measured value"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Please select a culture first</p>
                      </div>

                      {/* Field Photos & Documentation */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Field Photos & Documentation
                        </label>
                        <div 
                          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragEnter}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            className="hidden"
                            id="photo-upload"
                            disabled={isSavingEntry}
                          />
                          <label htmlFor="photo-upload" className="cursor-pointer">
                            <Camera className="w-12 h-12 text-gray-400 mb-4 mx-auto" />
                            <p className="text-gray-600 mb-2">Click to upload photos or drag and drop</p>
                            <p className="text-xs text-gray-500">Supports JPG, PNG, GIF (Max 5MB each)</p>
                          </label>
                        </div>

                        {/* Photo Previews */}
                        {uploadedPhotos.length > 0 && (
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Uploaded Photos ({uploadedPhotos.length})</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                              {uploadedPhotos.map((photo) => (
                                <div key={photo.id} className="relative group">
                                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                                    {photo.error ? (
                                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                        <X className="w-8 h-8 mb-2" />
                                        <span className="text-xs text-center">Failed to load</span>
                                      </div>
                                    ) : photo.preview ? (
                                      <img
                                        src={photo.preview}
                                        alt={photo.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => removePhoto(photo.id)}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    disabled={isSavingEntry}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                  <div className="mt-1 text-xs text-gray-500 truncate">{photo.name}</div>
                                  {photo.uploaded && !photo.error && (
                                    <div className="absolute top-1 left-1 bg-green-500 text-white rounded-full p-1">
                                      <CheckCircle className="w-3 h-3" />
                                    </div>
                                  )}
                                  {photo.error && (
                                    <div className="absolute top-1 left-1 bg-red-500 text-white rounded-full p-1">
                                      <X className="w-3 h-3" />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Feedback Sections */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Farmer Feedback & Observations
                          </label>
                          <textarea
                            rows={4}
                            value={farmerFeedback}
                            onChange={(e) => setFarmerFeedback(e.target.value)}
                            placeholder="Enter farmer's feedback, observations, concerns, or recommendations..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Location Incharge Feedback
                          </label>
                          <textarea
                            rows={4}
                            value={locationInchargeFeedback}
                            onChange={(e) => setLocationInchargeFeedback(e.target.value)}
                            placeholder="Enter location incharge's technical observations, issues encountered, or recommendations..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        {isEditMode && (
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                          >
                            Cancel Edit
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={closeLocationForm}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        >
                          {isEditMode ? 'Close' : 'Cancel'}
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveEntry}
                          disabled={isSavingEntry || !currentEntry.observation || !currentEntry.replication || !currentEntry.culture || !currentEntry.value}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSavingEntry ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              {isEditMode ? 'Updating...' : 'Saving...'}
                            </>
                          ) : (
                            <>
                              <FileText className="w-4 h-4 mr-2" />
                              {isEditMode ? 'Update Entry' : 'Save Entry'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>

                    {/* Saved Entries Table */}
                    <div className="mt-8">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">
                          Saved Entries ({savedEntries.length})
                        </h4>
                        {isLoadingSavedEntries && (
                          <div className="flex items-center text-sm text-gray-500">
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                            Loading...
                          </div>
                        )}
                      </div>
                      
                      {savedEntries.length > 0 ? (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    OBSERVATION
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    REPLICATION
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    CULTURE
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    VALUE
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    ENTRY DATE
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    OFFICER
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    ACTIONS
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {savedEntries.map((entry) => (
                                  <tr key={entry.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {entry.observation}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {entry.replication}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {entry.culture}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {entry.value}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {new Date(entry.entry_date).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      <div>
                                        <div className="font-medium">{entry.officer_name}</div>
                                        <div className="text-gray-500 text-xs">{entry.officer_email}</div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      <div className="flex space-x-2">
                                        <button
                                          onClick={() => handleEditEntry(entry)}
                                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                                          title="Edit Entry"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteEntry(entry.id)}
                                          className="text-red-600 hover:text-red-900 p-1 rounded"
                                          title="Delete Entry"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p>No saved entries found for this location.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Location Modal */}
            {showEditLocationModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Edit className="w-5 h-5 mr-2 text-blue-600" />
                        Edit Location
                      </h3>
                      <button
                        onClick={closeEditLocationModal}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      handleUpdateLocation(editingLocation.id, editLocationData);
                    }} className="space-y-6">
                      {/* Location Information */}
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-green-600" />
                          Location Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Location Name *</label>
                            <input 
                              type="text" 
                              className="input-field border-2 focus:border-green-500" 
                              placeholder="e.g., Research Station"
                              value={editLocationData.location_name}
                              onChange={(e) => setEditLocationData(prev => ({ ...prev, location_name: e.target.value }))}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">SF Number</label>
                            <input 
                              type="text" 
                              className="input-field border-2 focus:border-green-500" 
                              placeholder="SF001"
                              value={editLocationData.sf_number}
                              onChange={(e) => setEditLocationData(prev => ({ ...prev, sf_number: e.target.value }))}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                            <input 
                              type="text" 
                              className="input-field border-2 focus:border-green-500" 
                              placeholder="11.0168"
                              value={editLocationData.latitude}
                              onChange={(e) => setEditLocationData(prev => ({ ...prev, latitude: e.target.value }))}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                            <input 
                              type="text" 
                              className="input-field border-2 focus:border-green-500" 
                              placeholder="76.9558"
                              value={editLocationData.longitude}
                              onChange={(e) => setEditLocationData(prev => ({ ...prev, longitude: e.target.value }))}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Location Address</label>
                            <textarea 
                              rows="3" 
                              className="input-field border-2 focus:border-green-500" 
                              placeholder="Complete address of the location"
                              value={editLocationData.address}
                              onChange={(e) => setEditLocationData(prev => ({ ...prev, address: e.target.value }))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Location Incharge Assignment */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-medium text-gray-900 flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-green-600" />
                            Location Incharge Assignment
                          </h4>
                          <button
                            type="button"
                            onClick={() => handleCreateAssignmentOnly(editingLocation.id, editLocationData)}
                            className="btn-primary bg-green-600 hover:bg-green-700 text-sm flex items-center"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Save Assignment
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Location Incharge *</label>
                            <select
                              value={editLocationData.location_incharge_id}
                              onChange={(e) => {
                                const selectedOfficer = dynamicOfficers.find(o => o.id == e.target.value);
                                setEditLocationData(prev => ({ 
                                  ...prev, 
                                  location_incharge_id: e.target.value,
                                  location_incharge: selectedOfficer?.name || ''
                                }));
                              }}
                              className="input-field border-2 focus:border-green-500"
                              disabled={isLoadingOfficers}
                              required
                            >
                              <option value="">
                                {isLoadingOfficers ? 'Loading officers...' : 'Select Location Incharge'}
                              </option>
                              {dynamicOfficers.length > 0 ? (
                                dynamicOfficers.map((officer) => (
                                  <option key={officer.id || officer} value={officer.id || officer}>
                                    {officer.name || officer}
                                  </option>
                                ))
                              ) : (
                                <option value="" disabled>No officers available</option>
                              )}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                            <input
                              type="date"
                              value={editLocationData.from_date}
                              onChange={(e) => setEditLocationData(prev => ({ ...prev, from_date: e.target.value }))}
                              className="input-field border-2 focus:border-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                              value={editLocationData.status}
                              onChange={(e) => setEditLocationData(prev => ({ ...prev, status: e.target.value }))}
                              className="input-field border-2 focus:border-green-500"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Multiple Location Incharge Assignments */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-medium text-gray-900 flex items-center">
                            <Users className="w-4 h-4 mr-2 text-green-600" />
                            Location Incharge Assignments
                          </h4>
                        </div>

                        {/* Assignments Table */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          {isLoadingAssignments ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mr-2"></div>
                              <span className="text-gray-500">Loading assignments...</span>
                            </div>
                          ) : (Array.isArray(locationAssignments) ? locationAssignments : []).length === 0 ? (
                            <div className="text-center py-8">
                              <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-gray-500">No assignments found</p>
                              <p className="text-sm text-gray-400">Use the form above to create assignments</p>
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Officer
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Assignment Date
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Status
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {(Array.isArray(locationAssignments) ? locationAssignments : []).map((assignment) => (
                                    <tr key={assignment.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                          {assignment.location_incharge}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          ID: {assignment.officer_id}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                          {assignment.assignment_date ? new Date(assignment.assignment_date).toLocaleDateString() : '-'}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                          assignment.status === 'active' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                          {assignment.status || 'Unknown'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                          <button
                                            onClick={() => handleDeleteAssignment(assignment.id)}
                                            className="text-red-600 hover:text-red-900"
                                            title="Delete Assignment"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end pt-4 border-t border-gray-200 space-x-3">
                        <button
                          type="button"
                          onClick={closeEditLocationModal}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isUpdatingLocation}
                          className="btn-primary bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUpdatingLocation ? (
                            <div className="flex items-center">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                              Updating...
                            </div>
                          ) : (
                            'Update Location'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Location Management</h1>
              <p className="text-gray-600">Manage location assignments for this officer</p>
            </div>
          </div>



          {/* Location Assignments Table */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-green-600" />
              Location Assignments
              {isLoadingLocationAssignments && (
                <div className="ml-2 w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SF Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Centre Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trial Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location Incharge
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assignment Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoadingLocationAssignments ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mr-2"></div>
                          <span className="text-gray-500">Loading location assignments...</span>
                        </div>
                      </td>
                    </tr>
                  ) : (Array.isArray(locationAssignments) ? locationAssignments : []).length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="text-center py-8">
                          <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Location Assignments</h3>
                          <p className="text-gray-500">No location assignments found for this officer.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    (Array.isArray(locationAssignments) ? locationAssignments : []).map((assignment) => (
                      <tr 
                        key={assignment.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          // Navigate to location management for this location
                          const locationId = assignment.id;
                          const centreId = assignment.trial_centre_id;
                          const location_incharge_id = assignment.location_incharge_id;
                          if (locationId && centreId) {
                            navigate(`/trial-creation?view=locations&id=${assignment.trial_id}&centre=${centreId}&location=${locationId}&officer_id=${location_incharge_id}`);
                          }
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.location_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">
                            {assignment.sf_number || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {assignment.centre_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">
                            {assignment.trial_code || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {assignment.location_incharge || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {assignment.assignment_date 
                              ? new Date(assignment.assignment_date).toLocaleDateString()
                              : 'N/A'
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            assignment.assignment_status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {assignment.assignment_status || 'Unknown'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {locationAssignmentsPagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">
                      Showing {((locationAssignmentsPagination.page - 1) * locationAssignmentsPagination.limit) + 1} to{' '}
                      {Math.min(locationAssignmentsPagination.page * locationAssignmentsPagination.limit, locationAssignmentsPagination.total)} of{' '}
                      {locationAssignmentsPagination.total} results
                    </span>
                    <select
                      value={locationAssignmentsPagination.limit}
                      onChange={(e) => handleLocationAssignmentsPageSizeChange(Number(e.target.value))}
                      className="ml-4 text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value={10}>10 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleLocationAssignmentsPageChange(locationAssignmentsPagination.page - 1)}
                      disabled={locationAssignmentsPagination.page <= 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-700">
                      Page {locationAssignmentsPagination.page} of {locationAssignmentsPagination.pages}
                    </span>
                    <button
                      onClick={() => handleLocationAssignmentsPageChange(locationAssignmentsPagination.page + 1)}
                      disabled={locationAssignmentsPagination.page >= locationAssignmentsPagination.pages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

        </>
      )}

      {/* Location Details Modal */}
      {showLocationDetailsModal && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Location Details: {selectedLocation.locationName}
                </h2>
                <button
                  onClick={() => setShowLocationDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                    <input
                      type="text"
                      value={selectedLocation.locationName || ''}
                      onChange={(e) => setSelectedLocation(prev => ({ ...prev, locationName: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SF Number</label>
                    <input
                      type="text"
                      value={selectedLocation.sfNumber || ''}
                      onChange={(e) => setSelectedLocation(prev => ({ ...prev, sfNumber: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location Address</label>
                    <textarea
                      rows="3"
                      value={selectedLocation.locationAddress || ''}
                      onChange={(e) => setSelectedLocation(prev => ({ ...prev, locationAddress: e.target.value }))}
                      className="input-field"
                      placeholder="Complete address of the location"
                    />
                  </div>
                </div>
                
                {/* Coordinates */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Coordinates</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                    <input
                      type="text"
                      value={selectedLocation.latitude || ''}
                      onChange={(e) => setSelectedLocation(prev => ({ ...prev, latitude: e.target.value }))}
                      className="input-field"
                      placeholder="11.0168"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                    <input
                      type="text"
                      value={selectedLocation.longitude || ''}
                      onChange={(e) => setSelectedLocation(prev => ({ ...prev, longitude: e.target.value }))}
                      className="input-field"
                      placeholder="76.9558"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Altitude</label>
                    <input
                      type="text"
                      value={selectedLocation.altitude || ''}
                      onChange={(e) => setSelectedLocation(prev => ({ ...prev, altitude: e.target.value }))}
                      className="input-field"
                      placeholder="411"
                    />
                  </div>
                </div>
              </div>
              
              {/* Location Incharge Validity Details */}
              <div className="mt-6 bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-green-600" />
                  Location Incharge Validity Periods
                </h3>
                
                {/* Add New Validity Period */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location Incharge</label>
                    <select
                      value={selectedLocation.locationIncharge || ''}
                      onChange={(e) => setSelectedLocation(prev => ({ ...prev, locationIncharge: e.target.value }))}
                      className="input-field border-2 focus:border-green-500"
                      disabled={isLoadingOfficers}
                    >
                      <option value="">
                        {isLoadingOfficers ? 'Loading officers...' : 'Select Location Incharge'}
                      </option>
                      {dynamicOfficers.length > 0 ? (
                        dynamicOfficers.map((officer) => (
                          <option key={officer.id || officer} value={officer.id || officer}>
                            {officer.name || officer}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No officers available</option>
                      )}
                    </select>
                    {!isLoadingOfficers && (
                      <p className="text-xs text-gray-500 mt-1">
                        {dynamicOfficers.length > 0 
                          ? ``
                          : `No officers available`
                        }
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                      type="date"
                      value={locationInchargeValidityFrom}
                      onChange={(e) => setLocationInchargeValidityFrom(e.target.value)}
                      className="input-field border-2 focus:border-green-500"
                      placeholder="Start date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={locationInchargeValidityTo}
                      onChange={(e) => setLocationInchargeValidityTo(e.target.value)}
                      className="input-field border-2 focus:border-green-500"
                      placeholder="End date"
                    />
                  </div>
                </div>
                
                {/* Add Validity Period Button */}
                <div className="flex justify-end mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedLocation.locationIncharge) {
                        toast.error('Please select a Location Incharge');
                        return;
                      }
                      if (!locationInchargeValidityFrom || !locationInchargeValidityTo) {
                        toast.error('Please select both from and to dates');
                        return;
                      }
                      if (new Date(locationInchargeValidityFrom) >= new Date(locationInchargeValidityTo)) {
                        toast.error('From date must be before To date');
                        return;
                      }
                      
                      const newValidity = {
                        id: Date.now(),
                        locationIncharge: selectedLocation.locationIncharge,
                        fromDate: locationInchargeValidityFrom,
                        toDate: locationInchargeValidityTo,
                        status: 'active'
                      };
                      
                      setLocationInchargeValidityList(prev => [...prev, newValidity]);
                      setLocationInchargeValidityFrom('');
                      setLocationInchargeValidityTo('');
                      toast.success('Location Incharge validity period added successfully!');
                    }}
                    className="btn-primary px-6"
                  >
                    Add
                  </button>
                </div>
                
                {/* Validity Periods Table */}
                {locationInchargeValidityList.length > 0 && (
                  <div className="bg-white rounded-md border border-green-200 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-green-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-700">Location Incharge</th>
                          <th className="px-4 py-2 text-left text-gray-700">From Date</th>
                          <th className="px-4 py-2 text-left text-gray-700">To Date</th>
                          <th className="px-4 py-2 text-left text-gray-700">Status</th>
                          <th className="px-4 py-2 text-left text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locationInchargeValidityList.map((validity) => (
                          <tr key={validity.id} className="border-t">
                            <td className="px-4 py-2">{validity.locationIncharge}</td>
                            <td className="px-4 py-2">{validity.fromDate}</td>
                            <td className="px-4 py-2">{validity.toDate}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                validity.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {validity.status === 'active' ? 'Active' : 'inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setLocationInchargeValidityList(prev =>
                                    prev.filter(item => item.id !== validity.id)
                                  );
                                  toast.success('Location Incharge validity period removed successfully!');
                                }}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowLocationDetailsModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Validate that at least one validity period is added
                    if (locationInchargeValidityList.length === 0) {
                      toast.error('Please add at least one Location Incharge validity period');
                      return;
                    }
                    
                    // Update the location in trialAssignments with validity periods
                    const updatedLocation = {
                      ...selectedLocation,
                      locationInchargeValidityList: locationInchargeValidityList
                    };
                    
                    setTrialAssignments(prev => 
                      prev.map(assignment => 
                        assignment.id === selectedLocation.id 
                          ? { ...assignment, ...updatedLocation }
                          : assignment
                      )
                    );
                    toast.success('Location updated successfully!');
                    setShowLocationDetailsModal(false);
                    
                    // Reset validity list
                    setLocationInchargeValidityList([]);
                  }}
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Trial Details Modal */}
      {showLocationTrialDetailsModal && selectedLocationForTrialDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Location Trial Details: {selectedLocationForTrialDetails.locationName}
                </h2>
                <button
                  onClick={() => setShowLocationTrialDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Agricultural Practices */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Agricultural Practices</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Experimental Area (ha)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={selectedLocationForTrialDetails.experimentalArea || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, experimentalArea: e.target.value }))}
                      className="input-field"
                      placeholder="2.5"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plot Size (sq.m)</label>
                    <input
                      type="number"
                      value={selectedLocationForTrialDetails.plotSize || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, plotSize: e.target.value }))}
                      className="input-field"
                      placeholder="25"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Soil Type</label>
                    <select
                      value={selectedLocationForTrialDetails.soilType || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, soilType: e.target.value }))}
                      className="input-field"
                    >
                      <option value="">Select Soil Type</option>
                      {soilTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Soil Test Date</label>
                    <input
                      type="date"
                      value={selectedLocationForTrialDetails.soilTestDate || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, soilTestDate: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Soil Test Details</label>
                    <textarea
                      rows="3"
                      value={selectedLocationForTrialDetails.soilTestDetails || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, soilTestDetails: e.target.value }))}
                      className="input-field"
                      placeholder="pH: 6.8, N: 280 kg/ha, P: 45 kg/ha, K: 320 kg/ha"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Irrigation Source</label>
                    <select
                      value={selectedLocationForTrialDetails.irrigationSource || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, irrigationSource: e.target.value }))}
                      className="input-field"
                    >
                      <option value="">Select Irrigation Source</option>
                      {irrigationSources.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Previous Crop</label>
                    <input
                      type="text"
                      value={selectedLocationForTrialDetails.previousCrop || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, previousCrop: e.target.value }))}
                      className="input-field"
                      placeholder="Paddy"
                    />
                  </div>
                </div>
                
                {/* Crop Management */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Crop Management</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Seed Treatment</label>
                    <input
                      type="text"
                      value={selectedLocationForTrialDetails.seedTreatment || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, seedTreatment: e.target.value }))}
                      className="input-field"
                      placeholder="Thiram @ 2g/kg seed"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ploughing</label>
                    <input
                      type="text"
                      value={selectedLocationForTrialDetails.ploughing || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, ploughing: e.target.value }))}
                      className="input-field"
                      placeholder="2 times disc ploughing"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sowing Date</label>
                    <input
                      type="date"
                      value={selectedLocationForTrialDetails.sowingDate || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, sowingDate: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Planting Date</label>
                    <input
                      type="date"
                      value={selectedLocationForTrialDetails.plantingDate || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, plantingDate: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic</label>
                    <input
                      type="text"
                      value={selectedLocationForTrialDetails.basalOrganic || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, basalOrganic: e.target.value }))}
                      className="input-field"
                      placeholder="Farm Yard Manure"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic Date</label>
                    <input
                      type="date"
                      value={selectedLocationForTrialDetails.basalOrganicDate || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, basalOrganicDate: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic Quantity (kg/ha)</label>
                    <input
                      type="number"
                      value={selectedLocationForTrialDetails.basalOrganicQuantity || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, basalOrganicQuantity: e.target.value }))}
                      className="input-field"
                      placeholder="5000"
                    />
                  </div>
                </div>
              </div>
              
              {/* Additional Fields Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                {/* Fertilizer Management */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Fertilizer Management</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Date</label>
                    <input
                      type="date"
                      value={selectedLocationForTrialDetails.basalFertilizerDate || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, basalFertilizerDate: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Name</label>
                    <input
                      type="text"
                      value={selectedLocationForTrialDetails.basalFertilizerName || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, basalFertilizerName: e.target.value }))}
                      className="input-field"
                      placeholder="NPK 20:20:20"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Quantity (kg/ha)</label>
                    <input
                      type="number"
                      value={selectedLocationForTrialDetails.basalFertilizerQuantity || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, basalFertilizerQuantity: e.target.value }))}
                      className="input-field"
                      placeholder="120"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Date</label>
                    <input
                      type="date"
                      value={selectedLocationForTrialDetails.topDressingDate || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, topDressingDate: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Name</label>
                    <input
                      type="text"
                      value={selectedLocationForTrialDetails.topDressingName || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, topDressingName: e.target.value }))}
                      className="input-field"
                      placeholder="Urea"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Quantity (kg/ha)</label>
                    <input
                      type="number"
                      value={selectedLocationForTrialDetails.topDressingQuantity || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, topDressingQuantity: e.target.value }))}
                      className="input-field"
                      placeholder="60"
                    />
                  </div>
                </div>
                
                {/* Crop Protection & Monitoring */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Crop Protection & Monitoring</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Weed Management</label>
                    <textarea
                      rows="2"
                      value={selectedLocationForTrialDetails.weedManagement || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, weedManagement: e.target.value }))}
                      className="input-field"
                      placeholder="Hand weeding on 30 DAS, Chemical: 2,4-D @ 0.5 kg/ha"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plant Protection</label>
                    <textarea
                      rows="2"
                      value={selectedLocationForTrialDetails.plantProtection || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, plantProtection: e.target.value }))}
                      className="input-field"
                      placeholder="Carbendazim @ 1g/l for blast control"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Harvest Date</label>
                    <input
                      type="date"
                      value={selectedLocationForTrialDetails.harvestDate || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, harvestDate: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monitoring Visit Date</label>
                    <input
                      type="date"
                      value={selectedLocationForTrialDetails.monitoringVisitDate || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, monitoringVisitDate: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monitoring Comments</label>
                    <textarea
                      rows="2"
                      value={selectedLocationForTrialDetails.monitoringComments || ''}
                      onChange={(e) => setSelectedLocationForTrialDetails(prev => ({ ...prev, monitoringComments: e.target.value }))}
                      className="input-field"
                      placeholder="Crop growth satisfactory, no pest incidence"
                    />
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowLocationTrialDetailsModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Update the location in trialAssignments
                    setTrialAssignments(prev => 
                      prev.map(assignment => 
                        assignment.id === selectedLocationForTrialDetails.id 
                          ? { ...assignment, ...selectedLocationForTrialDetails }
                          : assignment
                      )
                    );
                    toast.success('Location trial details saved successfully!');
                    setShowLocationTrialDetailsModal(false);
                  }}
                  className="btn-primary"
                >
                  Save Trial Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                {editingTrial ? 'Edit' : 'Create'} Trial
              </h2>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={loadCrops}
                    disabled={isLoadingCrops}
                    className="p-2 text-blue-600 hover:text-blue-800 disabled:text-gray-400 rounded-md hover:bg-blue-50"
                    title="Refresh crops"
                  >
                    <div className={`w-4 h-4 ${isLoadingCrops ? 'animate-spin' : ''}`}>
                      {isLoadingCrops ? (
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <Sprout className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={loadSeasons}
                    disabled={isLoadingSeasons}
                    className="p-2 text-blue-600 hover:text-blue-800 disabled:text-gray-400 rounded-md hover:bg-blue-50"
                    title="Refresh seasons"
                  >
                    <div className={`w-4 h-4 ${isLoadingSeasons ? 'animate-spin' : ''}`}>
                      {isLoadingSeasons ? (
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <Calendar className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={loadScientists}
                    disabled={isLoadingScientists}
                    className="p-2 text-blue-600 hover:text-blue-800 disabled:text-gray-400 rounded-md hover:bg-blue-50"
                    title="Refresh scientists"
                  >
                    <div className={`w-4 h-4 ${isLoadingScientists ? 'animate-spin' : ''}`}>
                      {isLoadingScientists ? (
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <Users className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={loadObservations}
                    disabled={isLoadingObservations}
                    className="p-2 text-blue-600 hover:text-blue-800 disabled:text-gray-400 rounded-md hover:bg-blue-50"
                    title="Refresh observations"
                  >
                    <div className={`w-4 h-4 ${isLoadingObservations ? 'animate-spin' : ''}`}>
                      {isLoadingObservations ? (
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                </div>
              </div>
              <form onSubmit={handleSubmit(editingTrial ? handleEdit : handleAdd)} className="space-y-6">
                {/* Basic Trial Information - 2 Column Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {!editingTrial && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Trial Code</label>
                      <input
                        type="text"
                        value={generateTrialCode()}
                        className="input-field bg-gray-50 font-mono"
                        readOnly
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-generated: {watchedType || ''}{watchedCrop ? (dynamicCrops.find(crop => crop.id == watchedCrop)?.name || watchedCrop).charAt(0).toUpperCase() : ''}{watchedYear ? watchedYear.toString().slice(-2) : ''}{watchedSeason ? (dynamicSeasons.find(season => season.id == watchedSeason)?.name || watchedSeason).charAt(0).toUpperCase() : ''}{watchedType ? (trials.filter(t => t && (t.trial_type || t.type)).length + 1).toString().padStart(3, '0') : ''}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trial Type</label>
                    <select
                      {...register('type', { required: 'Trial type is required' })}
                      className="input-field"
                    >
                      <option value="">Select Trial Type</option>
                      {trialTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trial Name</label>
                    <input
                      type="text"
                      {...register('name', { required: 'Trial name is required' })}
                      placeholder="Enter trial name"
                      className="input-field"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Crop
                      {isLoadingCrops && (
                        <span className="ml-2 inline-flex items-center">
                          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </span>
                      )}
                      {!isLoadingCrops && dynamicCrops.length > 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          API
                        </span>
                      )}
                      {!isLoadingCrops && dynamicCrops.length === 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                          Fallback
                        </span>
                      )}
                    </label>
                    <select
                      {...register('crop', { required: 'Crop is required' })}
                      className="input-field"
                      disabled={isLoadingCrops}
                    >
                      <option value="">
                        {isLoadingCrops ? 'Loading crops...' : 'Select Crop'}
                      </option>
                      {dynamicCrops.length > 0 ? (
                        dynamicCrops.map(crop => (
                          <option key={crop.id || crop} value={crop.id || crop}>
                            {crop.name || crop}
                          </option>
                        ))
                      ) : (
                        ''
                      )}
                    </select>
                    {errors.crop && <p className="text-red-500 text-sm mt-1">{errors.crop.message}</p>}
                    {!isLoadingCrops && (
                      <p className="text-xs text-gray-500 mt-1">
                        {dynamicCrops.length > 0 
                          ? ``
                          : `No crops available`
                        }
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                      {...register('year', { required: 'Year is required' })}
                      className="input-field"
                    >
                      <option value="">Select Year</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    {errors.year && <p className="text-red-500 text-sm mt-1">{errors.year.message}</p>}
                  </div>



                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Season
                      {isLoadingSeasons && (
                        <span className="ml-2 inline-flex items-center">
                          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </span>
                      )}
                      {!isLoadingSeasons && dynamicSeasons.length > 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          API
                        </span>
                      )}
                      {!isLoadingSeasons && dynamicSeasons.length === 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                          Fallback
                        </span>
                      )}
                    </label>
                    <select
                      {...register('season', { required: 'Season is required' })}
                      className="input-field"
                      disabled={isLoadingSeasons}
                    >
                      <option value="">
                        {isLoadingSeasons ? 'Loading seasons...' : 'Select Season'}
                      </option>
                      {dynamicSeasons.length > 0 ? (
                        dynamicSeasons.map(season => (
                          <option key={season.id || season} value={season.id || season}>
                            {season.name || season}
                          </option>
                        ))
                      ) : (
                        ''
                      )}
                    </select>
                    {errors.season && <p className="text-red-500 text-sm mt-1">{errors.season.message}</p>}
                    {!isLoadingSeasons && (
                      <p className="text-xs text-gray-500 mt-1">
                        {dynamicSeasons.length > 0 
                          ? ``
                          : `No seasons available`
                        }
                      </p>
                    )}
                  </div>
                  {/* Removed duplicate Trial Incharge select from top section */}
                </div>

                {/* Trial Incharge Section - Create: simple select; Edit: allow multiple via add + table (no To Date) */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-md font-semibold text-purple-900 mb-3">Trial Incharge</h3>
                  <div className="space-y-4">
                    {/* Create mode: single incharge selection */}
                    {!editingTrial && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Trial Incharge
                            {isLoadingScientists && (
                              <span className="ml-2 inline-flex items-center">
                                <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                              </span>
                            )}
                            {!isLoadingScientists && dynamicScientists.length > 0 && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                API
                              </span>
                            )}
                            {!isLoadingScientists && dynamicScientists.length === 0 && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                                Fallback
                              </span>
                            )}
                          </label>
                        <select
                          {...register('trialIncharge', { required: 'Trial incharge is required' })}
                          className="input-field"
                            disabled={isLoadingScientists}
                          >
                            <option value="">
                              {isLoadingScientists ? 'Loading scientists...' : 'Select Trial Incharge'}
                            </option>
                            {dynamicScientists.length > 0 ? (
                              dynamicScientists.map(scientist => (
                                <option key={scientist.id || scientist} value={scientist.id || scientist}>
                                  {scientist.name || scientist}
                                </option>
                              ))
                            ) : (
                              ''
                            )}
                        </select>
                          {errors.trialIncharge && <p className="text-red-500 text-sm mt-1">{errors.trialIncharge.message}</p>}
                          {!isLoadingScientists && (
                            <p className="text-xs text-gray-500 mt-1">
                              {dynamicScientists.length > 0 
                                ? ``
                                : `No scientists available`
                              }
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            {...register('startDate', { required: 'Start date is required' })}
                            className="input-field"
                            placeholder="mm/dd/yyyy"
                          />
                          {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate.message}</p>}
                        </div>
                      </div>
                    )}

                    {/* Edit mode: add multiple incharge periods with Start Date only */}
                    {editingTrial && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Trial Incharge</label>
                            <select
                              value={editingTrial?.trialIncharge || ''}
                              onChange={(e) => setEditingTrial(prev => ({ ...prev, trialIncharge: e.target.value }))}
                              className="input-field"
                              disabled={isLoadingScientists}
                            >
                              <option value="">Select Trial Incharge</option>
                              {dynamicScientists.length > 0 ? (
                                dynamicScientists.map(scientist => (
                                  <option key={scientist.id || scientist} value={scientist.id || scientist}>
                                    {scientist.name || scientist}
                                  </option>
                                ))
                              ) : (
                                !isLoadingScientists && scientists.map(scientist => (
                                <option key={scientist} value={scientist}>{scientist}</option>
                                ))
                              )}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input
                              type="date"
                              value={trialInchargeValidityFrom}
                              onChange={(e) => setTrialInchargeValidityFrom(e.target.value)}
                              className="input-field"
                              placeholder="Start date"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={async () => {
                              const currentTrialIncharge = editingTrial?.trialIncharge;
                              if (!currentTrialIncharge) {
                                toast.error('Please select a Trial Incharge');
                                return;
                              }
                              if (!trialInchargeValidityFrom) {
                                toast.error('Please select from date');
                                return;
                              }
                              
                              // Convert trial incharge ID to name for storage
                              const selectedScientist = dynamicScientists.find(scientist => scientist.id == currentTrialIncharge) || { name: currentTrialIncharge };
                              
                              // Prepare assignment data for API
                              const assignmentData = {
                                officerId: currentTrialIncharge,
                                centreIncharge: selectedScientist.name || currentTrialIncharge,
                                assignedOfficer: selectedScientist.name || currentTrialIncharge,
                                assignmentDate: trialInchargeValidityFrom,
                                status: 'active'
                              };

                              // Create assignment via API
                              const success = await createTrialAssignment(editingTrial.id, assignmentData);
                              if (success) {
                                setTrialInchargeValidityFrom('');
                              }
                            }}
                            className="btn-primary px-6"
                          >
                            Add Incharge
                          </button>
                        </div>
                        {isLoadingAssignments ? (
                          <div className="flex justify-center py-8">
                            <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : trialAssignments.length > 0 ? (
                          <div className="bg-white rounded-md border border-purple-100 overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-purple-100/40">
                                <tr>
                                  <th className="px-4 py-2 text-left text-gray-700">Trial Incharge</th>
                                  <th className="px-4 py-2 text-left text-gray-700">Start Date</th>
                                  <th className="px-4 py-2 text-left text-gray-700">Status</th>
                                  <th className="px-4 py-2 text-left text-gray-700">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {trialAssignments.map((assignment) => (
                                  <tr key={assignment.id} className="border-t">
                                    <td className="px-4 py-2">{assignment.assigned_officer || assignment.officer_name || 'N/A'}</td>
                                    <td className="px-4 py-2">
                                      {assignment.assignment_date ? new Date(assignment.assignment_date).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="px-4 py-2">
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        assignment.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {assignment.status === 'active' ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2">
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (window.confirm('Are you sure you want to delete this assignment?')) {
                                            await deleteTrialAssignment(editingTrial.id, assignment.id);
                                          }
                                        }}
                                        className="text-red-600 hover:text-red-800 text-sm"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>No assignments found</p>
                            <p className="text-sm">Click "Add Incharge" to create one</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Top Row: Trials and Observations Input Sections - Hidden for create trial, shown for edit */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ display: editingTrial ? 'grid' : 'none' }}>
                  {/* Trials Section - Left Side */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-md font-semibold text-blue-900 mb-3">Trials</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Culture/Treatment (Optional)</label>
                          <input
                            type="text"
                            value={editTrialCulture}
                            onChange={(e) => setEditTrialCulture(e.target.value)}
                            placeholder="e.g., Hybrid Rice Variety (optional)"
                            className="input-field"
                          />
                        </div>
                        <div className="w-24">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Replications</label>
                          <select
                            value={editTrialReplications}
                            onChange={(e) => {
                              const newValue = Number(e.target.value);
                              console.log('🔍 Replications changed to:', newValue);
                              setEditTrialReplications(newValue);
                            }}
                            className="input-field"
                          >
                            {[1,2,3,4,5,6,7,8,9].map(n => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            // Culture is now optional - allow empty culture
                            setEditTrialCultureList(prev => [...prev, {
                              id: Date.now(),
                              culture: editTrialCulture || 'Not specified',
                              replications: editTrialReplications
                            }]);
                            setEditTrialCulture('');
                          }}
                          className="btn-primary px-6"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Observations Section - Right Side */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-md font-semibold text-green-900 mb-3">Observations</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Observations
                          {isLoadingObservations && (
                            <span className="ml-2 inline-flex items-center">
                              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            </span>
                          )}
                          {!isLoadingObservations && dynamicObservations.length > 0 && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">API</span>
                          )}
                          {!isLoadingObservations && dynamicObservations.length === 0 && (
                            <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Fallback</span>
                          )}
                        </label>
                        <div className="relative" ref={observationDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setIsObservationDropdownOpen((v) => !v)}
                            className="w-full input-field flex items-center justify-between"
                          >
                            <span className="truncate">
                              {editTrialObservations.length === 0
                                ? 'Select Observations'
                                : editTrialObservations.join(', ')}
                            </span>
                            <svg className={`w-4 h-4 ml-2 transition-transform ${isObservationDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                            </svg>
                          </button>
                          {isObservationDropdownOpen && (
                            <div className="absolute left-0 z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
                              <div className="p-2">
                                <input
                                  type="text"
                                  value={observationQuery}
                                  onChange={(e) => setObservationQuery(e.target.value)}
                                  placeholder="Search observations..."
                                  className="input-field mb-2"
                                />
                                <div className="space-y-1">
                                  {filteredObservations.map((obs) => (
                                    <div key={obs} className="flex items-center px-2 py-1 hover:bg-gray-50 rounded">
                                      <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-primary focus:ring-primary mr-2"
                                        checked={editTrialObservations.includes(obs)}
                                        onChange={() => {
                                          setEditTrialObservations((prev) => {
                                            const next = prev.includes(obs)
                                              ? prev.filter((v) => v !== obs)
                                              : [...prev, obs];
                                            return next;
                                          });
                                        }}
                                      />
                                      <span className="text-sm text-gray-700 flex-1">{obs}</span>
                                      {editTrialObservations.includes(obs) && (
                                        <input
                                          type="text"
                                          value={editTrialObservationDays[obs] || ''}
                                          onChange={(e) => setEditTrialObservationDays(prev => ({
                                            ...prev,
                                            [obs]: e.target.value
                                          }))}
                                          placeholder="Days"
                                          className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary ml-2"
                                        />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>



                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            if (editTrialObservations.length === 0) {
                              toast.error('Please select observations');
                              return;
                            }
                            
                            // Check if all selected observations have days
                            const missingDays = editTrialObservations.filter(obs => !editTrialObservationDays[obs] || !editTrialObservationDays[obs].trim());
                            if (missingDays.length > 0) {
                              toast.error(`Please enter days for: ${missingDays.join(', ')}`);
                              return;
                            }

                            // Create observation entries with individual days
                            const observationEntries = editTrialObservations.map(obs => ({
                              id: Date.now() + Math.random(),
                              observation: obs,
                              days: editTrialObservationDays[obs]
                            }));

                            setEditTrialObservationsList(prev => [...prev, ...observationEntries]);
                            setEditTrialObservations([]);
                            setEditTrialObservationDays({});
                          }}
                          className="btn-primary px-6"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Row: Culture and Observations Tables - Hidden for create trial, shown for edit */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ display: editingTrial ? 'grid' : 'none' }}>
                  {/* Culture Entries Table - Left Side */}
                  {editTrialCultureList.length > 0 && (
                    <div className="bg-white rounded-md border border-blue-100 overflow-x-auto">
                      <table className="min-w-[300px] w-full text-sm">
                        <thead className="bg-blue-100/40">
                          <tr>
                            <th className="px-4 py-2 text-left text-gray-700">Culture/Treatment</th>
                            <th className="px-4 py-2 text-left text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editTrialCultureList.map((row, idx) => (
                            <tr key={row.id} className="border-t">
                              <td className="px-4 py-2">{row.culture}</td>
                              <td className="px-4 py-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditTrialCultureList(prev => prev.filter((_, index) => index !== idx));
                                  }}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Observations Entries Table - Right Side */}
                  {editTrialObservationsList.length > 0 && (
                    <div className="bg-white rounded-md border border-green-100 overflow-x-auto">
                      <table className="min-w-[400px] w-full text-sm">
                        <thead className="bg-green-100/40">
                          <tr>
                            <th className="px-4 py-2 text-left text-gray-700">Observation</th>
                            <th className="px-4 py-2 text-left text-gray-700">Days</th>
                            <th className="px-4 py-2 text-left text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editTrialObservationsList.map((row, idx) => (
                            <tr key={row.id} className="border-t">
                              <td className="px-4 py-2">{row.observation}</td>
                              <td className="px-4 py-2">{row.days || '-'}</td>
                              <td className="px-4 py-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditTrialObservationsList(prev => prev.filter((_, index) => index !== idx));
                                  }}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingTrial(null);
                      setEditTrialCultureList([]);
                      setEditTrialCulture('');
                      setEditTrialObservations([]);
                      setEditTrialObservationsList([]);
                      setEditTrialObservationDays({});
                      setTrialInchargeValidityFrom('');
                      setTrialInchargeValidityTo('');
                      setTrialInchargeValidityList([]);
                      reset();
                    }}
                    className="btn-secondary px-6 py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary px-6 py-2"
                  >
                    {isLoading ? 'Saving...' : (editingTrial ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
                 </div>
       )}

       {/* Trial Assignment Modal */}
       {showAssignmentModal && selectedTrial && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
             <div className="p-6">
               <h2 className="text-lg font-semibold text-gray-900 mb-4">
                 Add Centre to Trial: {selectedTrial.name}
               </h2>
               <form className="space-y-6">
                 {/* Trial Details Section */}
                 <div className="bg-blue-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-blue-900 mb-3">Trial Details</h3>
                   <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                        <div className="md:col-span-2">
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Centre</label>
                              <select
                                value={assignmentCentre}
                                onChange={(e) => setAssignmentCentre(e.target.value)}
                                className="input-field"
                              >
                                <option value="">Select Centre</option>
                                {centres.map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Centre Incharge</label>
                              <select
                                value={assignmentCentreIncharge}
                                onChange={(e) => setAssignmentCentreIncharge(e.target.value)}
                                className="input-field"
                              >
                                <option value="">
                                  {isLoadingOfficers ? 'Loading officers...' : 'Select Centre Incharge'}
                                </option>
                                {dynamicOfficers.length > 0 ? (
                                  dynamicOfficers.map((officer) => (
                                    <option key={officer.id || officer} value={officer.id || officer}>
                                      {officer.name || officer}
                                    </option>
                                  ))
                                ) : (
                                  officers.map((officer, index) => (
                                    <option key={officer} value={index}>{officer}</option>
                                  ))
                                )}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Culture/Treatment</label>
                              <input
                                type="text"
                                value={assignmentCulture || ''}
                                onChange={(e) => setAssignmentCulture(e.target.value)}
                                placeholder="e.g., Hybrid Rice Variety"
                                className="input-field"
                              />
                            </div>
                          </div>
                        </div>
                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">Replications</label>
                         <select
                           value={assignmentReplications}
                           onChange={(e) => setAssignmentReplications(Number(e.target.value))}
                           className="input-field"
                         >
                           {[1,2,3,4,5,6,7,8,9].map(n => (
                             <option key={n} value={n}>{n}</option>
                           ))}
                         </select>
                       </div>
                        <div className="md:col-span-2 relative" ref={observationDropdownRef}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Observations
                            {isLoadingObservations && (
                              <span className="ml-2 inline-flex items-center">
                                <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                              </span>
                            )}
                            {!isLoadingObservations && dynamicObservations.length > 0 && (
                              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">API</span>
                            )}
                            {!isLoadingObservations && dynamicObservations.length === 0 && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Fallback</span>
                            )}
                          </label>
                          <button
                            type="button"
                            onClick={() => setIsObservationDropdownOpen((v) => !v)}
                            className="w-full input-field flex items-center justify-between"
                          >
                            <span className="truncate">
                              {assignmentObservations.length === 0
                                ? 'Select Observations'
                                : formatObservationsWithDays(assignmentObservations)}
                            </span>
                            <svg className={`w-4 h-4 ml-2 transition-transform ${isObservationDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                            </svg>
                          </button>
                          {isObservationDropdownOpen && (
                            <div className="absolute left-0 z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
                              <div className="p-2">
                                <input
                                  type="text"
                                  value={observationQuery}
                                  onChange={(e) => setObservationQuery(e.target.value)}
                                  placeholder="Search observations..."
                                  className="input-field mb-2"
                                />
                                <div className="space-y-1">
                                  {filteredObservations.map((obs) => (
                                    <div key={obs} className="flex items-center px-2 py-1 hover:bg-gray-50 rounded">
                                      <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-primary focus:ring-primary mr-2"
                                        checked={assignmentObservations.includes(obs)}
                                        onChange={() => toggleObservationSelection(obs)}
                                      />
                                      <span className="text-sm text-gray-700 flex-1">{obs}</span>
                                      {assignmentObservations.includes(obs) && (
                                        <div className="flex items-center space-x-1">
                                          <input
                                            type="number"
                                            min="0"
                                            className="w-20 input-field"
                                            placeholder="days"
                                            value={assignmentObservationDays?.[obs] || ''}
                                            onChange={(e) => setAssignmentObservationDays((d) => ({ ...(d || {}), [obs]: e.target.value }))}
                                          />
                                          <span className="text-xs text-gray-500">days</span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                     </div>
                     <div className="flex justify-end">
                       <button
                         type="button"
                         onClick={handleAddCultureReplication}
                         className="btn-primary"
                       >
                         Add
                       </button>
                     </div>
                      {cultureReplicationList.length > 0 && (
                        <div className="bg-white rounded-md border border-blue-100 overflow-x-auto">
                          <table className="min-w-[500px] w-full text-sm">
                           <thead className="bg-blue-100/40">
                             <tr>
                               <th className="px-4 py-2 text-left text-gray-700">Culture/Treatment</th>
                               <th className="px-4 py-2 text-left text-gray-700">Replications</th>
                                <th className="px-4 py-2 text-left text-gray-700">Observations</th>
                             </tr>
                           </thead>
                           <tbody>
                             {cultureReplicationList.map((row, idx) => (
                               <tr key={idx} className="border-t">
                                 <td className="px-4 py-2">{row.culture}</td>
                                 <td className="px-4 py-2">{cultureReplicationList[0]?.replications ?? assignmentReplications}</td>
                                 <td className="px-4 py-2 whitespace-pre-wrap break-words">{formatObservationsWithDays(cultureReplicationList[0]?.observations || assignmentObservations || [])}</td>
                               </tr>
                             ))}
                           </tbody>
                         </table>
                       </div>
                     )}
                     
                   </div>
                 </div>

                 {/* Location Details Section (collapsible) */}
                 <div className="bg-green-50 p-4 rounded-lg">
                   <div className="flex items-center justify-between mb-3">
                     <h3 className="text-md font-semibold text-green-900">Location Details</h3>
                     <button
                       type="button"
                       onClick={() => setShowLocationDetails(prev => !prev)}
                       className="p-2 rounded hover:bg-green-100"
                       aria-label="Toggle location details"
                     >
                       {showLocationDetails ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                     </button>
                   </div>
                   {showLocationDetails && (
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Location Code</label>
                       <input
                         type="text"
                         value={generateLocationCode(selectedTrial.code)}
                         className="input-field bg-gray-50 font-mono"
                         readOnly
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                       <input
                         type="text"
                         placeholder="e.g., Coimbatore Research Station"
                         className="input-field"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                       <input
                         type="text"
                         placeholder="11.0168"
                         className="input-field"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                       <input
                         type="text"
                         placeholder="76.9558"
                         className="input-field"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Altitude (m)</label>
                       <input
                         type="text"
                         placeholder="411"
                         className="input-field"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">SF Number</label>
                       <input
                         type="text"
                         placeholder="SF001"
                         className="input-field"
                       />
                     </div>
                     <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-1">Location Address</label>
                       <textarea
                         placeholder="Complete address of the location"
                         rows="2"
                         className="input-field"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Assign Scientist</label>
                       <select className="input-field">
                         <option value="">Select Scientist</option>
                         {scientists.map(scientist => (
                           <option key={scientist} value={scientist}>{scientist}</option>
                         ))}
                       </select>
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Assign Plot Officer</label>
                       <select className="input-field">
                         <option value="">Select Plot Officer</option>
                         {dynamicOfficers.map(officer => (
                           <option key={officer.id || officer} value={officer.id || officer}>{officer.name || officer}</option>
                         ))}
                       </select>
                     </div>
                   </div>
                   )}
                 </div>

                  {/* Individual Location Trial Details (collapsible) */}
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-md font-semibold text-orange-900">Individual Location Trial Details</h3>
                      <button
                        type="button"
                        onClick={() => setShowIndividualDetails(prev => !prev)}
                        className="p-2 rounded hover:bg-orange-100"
                        aria-label="Toggle individual location details"
                      >
                        {showIndividualDetails ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </button>
                    </div>
                    {showIndividualDetails && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Experimental Area (ha)</label>
                        <input type="number" className="input-field" placeholder="e.g., 2.5" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plot Size (m²)</label>
                        <input type="number" className="input-field" placeholder="e.g., 25" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Soil Type</label>
                        <select className="input-field">
                          <option value="">Select Soil Type</option>
                          {soilTypes.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Soil Test Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Soil Test Details</label>
                        <textarea rows="2" className="input-field" placeholder="pH, NPK values, etc." />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Irrigation Source</label>
                        <select className="input-field">
                          <option value="">Select Irrigation Source</option>
                          {irrigationSources.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Previous Crop</label>
                        <input type="text" className="input-field" placeholder="e.g., Paddy" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Seed Treatment</label>
                        <input type="text" className="input-field" placeholder="e.g., Thiram @ 2g/kg seed" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ploughing</label>
                        <input type="text" className="input-field" placeholder="e.g., 2 times disc ploughing" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sowing Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Planting Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic Name</label>
                        <input type="text" className="input-field" placeholder="e.g., FYM" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic Quantity (kg/ha)</label>
                        <input type="number" className="input-field" placeholder="e.g., 5000" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Name</label>
                        <input type="text" className="input-field" placeholder="e.g., NPK 20:20:20" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Quantity (kg/ha)</label>
                        <input type="number" className="input-field" placeholder="e.g., 120" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Name</label>
                        <input type="text" className="input-field" placeholder="e.g., Urea" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Quantity (kg/ha)</label>
                        <input type="number" className="input-field" placeholder="e.g., 60" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Weed Management Details</label>
                        <textarea rows="2" className="input-field" placeholder="Hand weeding / chemical quantity and date" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plant Protection Details</label>
                        <textarea rows="2" className="input-field" placeholder="Chemical, dose & date" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Harvest Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Monitoring Team Visit Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Monitoring Comments</label>
                        <textarea rows="2" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plot Officer Visit Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plot Officer Comments</label>
                        <textarea rows="2" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Field Day Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Officer Visit Date</label>
                        <input type="date" className="input-field" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Comments</label>
                        <textarea rows="2" className="input-field" />
                      </div>
                    </div>
                    )}
                  </div>



                 <div className="flex justify-end space-x-3 pt-4">
                   <button
                     type="button"
                     onClick={() => setShowAssignmentModal(false)}
                     className="btn-secondary"
                   >
                     Cancel
                   </button>
                   <button
                     type="button"
                     onClick={handleSaveAssignments}
                     className="btn-primary"
                   >
                     Add Centre
                   </button>
                 </div>
               </form>
             </div>
           </div>
         </div>
       )}

       {/* View Trial Assignments Modal */}
       {showViewModal && selectedTrial && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
             <div className="p-6">
               <h2 className="text-lg font-semibold text-gray-900 mb-4">
                 Trial Assignments: {selectedTrial.name}
               </h2>
               <div className="space-y-4">
                 {selectedTrial.id && getTrialAssignments(selectedTrial.id).map((assignment) => (
                   <div key={assignment.id} className="border border-gray-200 rounded-lg p-4">
                     <div className="flex justify-between items-start mb-4">
                       <div>
                         <h3 className="text-lg font-semibold text-gray-900">{assignment.locationName}</h3>
                         <p className="text-sm text-gray-600">Location Code: {assignment.locationCode}</p>
                       </div>
                       <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                         Active
                       </span>
                     </div>
                     
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                       <div>
                         <p className="text-sm font-medium text-gray-700">Assigned Officer</p>
                         <p className="text-sm text-gray-900">{assignment.assignedOfficer}</p>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-gray-700">Culture/Treatment</p>
                         <p className="text-sm text-gray-900">{assignment.culture} - {assignment.treatment}</p>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-gray-700">Replications</p>
                         <p className="text-sm text-gray-900">{assignment.replications}</p>
                       </div>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                       <div>
                         <p className="text-sm font-medium text-gray-700">Location</p>
                         <p className="text-sm text-gray-900">
                           {assignment.latitude}, {assignment.longitude} (Alt: {assignment.altitude}m)
                         </p>
                         <p className="text-sm text-gray-600">{assignment.locationAddress}</p>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-gray-700">Experimental Area</p>
                         <p className="text-sm text-gray-900">{assignment.experimentalArea} ha, Plot Size: {assignment.plotSize} m²</p>
                       </div>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       <div>
                         <p className="text-sm font-medium text-gray-700">Soil Type</p>
                         <p className="text-sm text-gray-900">{assignment.soilType}</p>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-gray-700">Irrigation Source</p>
                         <p className="text-sm text-gray-900">{assignment.irrigationSource}</p>
                       </div>
                     </div>

                                           <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => openAssignmentDetailModal(assignment)}
                            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                          >
                            View Details
                          </button>
                          <button 
                            onClick={() => openEditAssignmentModal(assignment)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit Assignment
                          </button>
                        </div>
                      </div>
                   </div>
                 ))}
                 
                 {selectedTrial.id && getTrialAssignments(selectedTrial.id).length === 0 && (
                   <div className="text-center py-8">
                     <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                     <p className="text-gray-600">No locations assigned to this trial yet.</p>
                     <button
                       onClick={() => {
                         setShowViewModal(false);
                         openAssignmentModal(selectedTrial);
                       }}
                       className="btn-primary mt-4"
                     >
                       Assign First Location
                     </button>
                   </div>
                 )}
               </div>

               <div className="flex justify-end pt-4">
                 <button
                   onClick={() => setShowViewModal(false)}
                   className="btn-secondary"
                 >
                   Close
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Assignment Detail View Modal */}
       {showAssignmentDetailModal && selectedAssignment && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
             <div className="p-6">
               <h2 className="text-lg font-semibold text-gray-900 mb-4">
                 Assignment Details: {selectedAssignment.locationName}
               </h2>
               <div className="space-y-6">
                 {/* Trial Information */}
                 <div className="bg-blue-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-blue-900 mb-3">Trial Information</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <p className="text-sm font-medium text-gray-700">Trial Code</p>
                       <p className="text-sm text-gray-900 font-mono">{selectedAssignment.locationCode.substring(0, selectedAssignment.locationCode.length - 3)}</p>
                     </div>
                     <div>
                       <p className="text-sm font-medium text-gray-700">Location Code</p>
                       <p className="text-sm text-gray-900 font-mono">{selectedAssignment.locationCode}</p>
                     </div>
                     <div>
                       <p className="text-sm font-medium text-gray-700">Culture/Treatment</p>
                       <p className="text-sm text-gray-900">{selectedAssignment.culture} - {selectedAssignment.treatment}</p>
                     </div>
                     <div>
                       <p className="text-sm font-medium text-gray-700">Number of Replications</p>
                       <p className="text-sm text-gray-900">{selectedAssignment.replications}</p>
                     </div>
                     <div className="md:col-span-2">
                       <p className="text-sm font-medium text-gray-700">Observations</p>
                       <p className="text-sm text-gray-900">{Array.isArray(selectedAssignment.observations) ? selectedAssignment.observations.join(', ') : 'N/A'}</p>
                     </div>
                   </div>
                 </div>

                 {/* Location Details */}
                 <div className="bg-green-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-green-900 mb-3">Location Details</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <p className="text-sm font-medium text-gray-700">Location Name</p>
                       <p className="text-sm text-gray-900">{selectedAssignment.locationName}</p>
                     </div>
                     <div>
                       <p className="text-sm font-medium text-gray-700">Assigned Officer</p>
                       <p className="text-sm text-gray-900">{selectedAssignment.assignedOfficer}</p>
                     </div>
                     <div>
                       <p className="text-sm font-medium text-gray-700">Coordinates</p>
                       <p className="text-sm text-gray-900">{selectedAssignment.latitude}, {selectedAssignment.longitude}</p>
                     </div>
                     <div>
                       <p className="text-sm font-medium text-gray-700">Altitude</p>
                       <p className="text-sm text-gray-900">{selectedAssignment.altitude} m</p>
                     </div>
                     <div>
                       <p className="text-sm font-medium text-gray-700">SF Number</p>
                       <p className="text-sm text-gray-900">{selectedAssignment.sfNumber}</p>
                     </div>
                     <div className="md:col-span-2">
                       <p className="text-sm font-medium text-gray-700">Location Address</p>
                       <p className="text-sm text-gray-900">{selectedAssignment.locationAddress}</p>
                     </div>
                   </div>
                 </div>

                                  {/* Plot Officer Data Entries */}
                 <div className="bg-purple-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-purple-900 mb-3">Plot Officer Data Entries</h3>
                   <div className="space-y-4">
                     {/* Mock data entries - in real app, this would come from the backend */}
                     <div className="border border-gray-200 rounded-lg p-4 bg-white">
                       <div className="flex justify-between items-start mb-3">
                         <div>
                           <h4 className="text-sm font-semibold text-gray-900">Entry #1 - Germination %</h4>
                           <p className="text-xs text-gray-600">Date: 2024-09-15 | Submitted by: Dr. Rajesh Kumar</p>
                         </div>
                         <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                           Completed
                         </span>
                       </div>
                       
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                         <div>
                           <p className="text-xs font-medium text-gray-700">Culture</p>
                           <p className="text-sm text-gray-900">Hybrid Rice Variety</p>
                         </div>
                         <div>
                           <p className="text-xs font-medium text-gray-700">Observation</p>
                           <p className="text-sm text-gray-900">Germination %</p>
                         </div>
                       </div>

                       {/* Individual Location Trial Details */}
                       <div className="mb-4">
                         <h5 className="text-xs font-semibold text-gray-700 mb-2">Individual Location Trial Details</h5>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                           <div>
                             <p className="font-medium text-gray-600">Experimental Area</p>
                             <p className="text-gray-900">2.5 ha</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Plot Size</p>
                             <p className="text-gray-900">25 m²</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Soil Type</p>
                             <p className="text-gray-900">Clay Loam</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Soil Test Date</p>
                             <p className="text-gray-900">2024-08-15</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Soil Test Details</p>
                             <p className="text-gray-900">pH: 6.8, N: 280 kg/ha, P: 45 kg/ha, K: 320 kg/ha</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Irrigation Source</p>
                             <p className="text-gray-900">Canal Irrigation</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Previous Crop</p>
                             <p className="text-gray-900">Paddy</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Seed Treatment</p>
                             <p className="text-gray-900">Carbendazim 2g/kg seed</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Ploughing</p>
                             <p className="text-gray-900">2 times disc ploughing</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Sowing Date</p>
                             <p className="text-gray-900">2024-06-15</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Planting Date</p>
                             <p className="text-gray-900">2024-06-20</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Basal Organic</p>
                             <p className="text-gray-900">FYM 5000 kg/ha (2024-06-10)</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Basal Fertilizer</p>
                             <p className="text-gray-900">NPK 20:20:20 120 kg/ha (2024-06-12)</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Top Dressing</p>
                             <p className="text-gray-900">Urea 60 kg/ha (2024-07-15)</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Weed Management</p>
                             <p className="text-gray-900">Hand weeding on 2024-07-20</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Plant Protection</p>
                             <p className="text-gray-900">Chlorpyriphos 2ml/lit on 2024-08-10</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Harvest Date</p>
                             <p className="text-gray-900">2024-10-15</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Monitoring Visit</p>
                             <p className="text-gray-900">2024-09-20 - Good progress observed</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Plot Officer Visit</p>
                             <p className="text-gray-900">2024-09-25 - Field conditions excellent</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Field Day</p>
                             <p className="text-gray-900">2024-10-10</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Inspection Visit</p>
                             <p className="text-gray-900">2024-09-28 - Trial meeting standards</p>
                           </div>
                         </div>
                       </div>
         
                       <div className="mb-3">
                         <p className="text-xs font-medium text-gray-700 mb-2">Replication Data</p>
                         <div className="grid grid-cols-3 gap-2 text-xs">
                           <div className="bg-gray-50 p-2 rounded">
                             <p className="font-medium">R1</p>
                             <p>V1: 85.5</p>
                             <p>V2: 87.2</p>
                             <p>V3: 86.8</p>
                           </div>
                           <div className="bg-gray-50 p-2 rounded">
                             <p className="font-medium">R2</p>
                             <p>V1: 88.1</p>
                             <p>V2: 86.9</p>
                             <p>V3: 87.5</p>
                           </div>
                           <div className="bg-gray-50 p-2 rounded">
                             <p className="font-medium">R3</p>
                             <p>V1: 86.3</p>
                             <p>V2: 88.7</p>
                             <p>V3: 87.1</p>
                           </div>
                         </div>
                       </div>
         
                       <div className="mb-3">
                         <p className="text-xs font-medium text-gray-700 mb-2">Field Photos</p>
                         <div className="flex space-x-2">
                           <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                             <span className="text-xs text-gray-500">Photo 1</span>
                           </div>
                           <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                             <span className="text-xs text-gray-500">Photo 2</span>
                           </div>
                         </div>
                       </div>
         
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                         <div>
                           <p className="text-xs font-medium text-gray-700 mb-1">Farmer Feedback</p>
                           <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                             Crop is showing good germination. Plants are healthy and uniform. No major issues observed.
                           </p>
                         </div>
                         <div>
                           <p className="text-xs font-medium text-gray-700 mb-1">Officer Assessment</p>
                           <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                             Germination percentage is excellent across all replications. Field conditions are optimal. Monitoring for any pest incidence.
                           </p>
                         </div>
                       </div>
                     </div>
         
                     <div className="border border-gray-200 rounded-lg p-4 bg-white">
                       <div className="flex justify-between items-start mb-3">
                         <div>
                           <h4 className="text-sm font-semibold text-gray-900">Entry #2 - Plant Height</h4>
                           <p className="text-xs text-gray-600">Date: 2024-09-30 | Submitted by: Dr. Rajesh Kumar</p>
                         </div>
                         <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                           Completed
                         </span>
                       </div>
                       
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                         <div>
                           <p className="text-xs font-medium text-gray-700">Culture</p>
                           <p className="text-sm text-gray-900">Hybrid Rice Variety</p>
                         </div>
                         <div>
                           <p className="text-xs font-medium text-gray-700">Observation</p>
                           <p className="text-sm text-gray-900">Plant Height</p>
                         </div>
                       </div>

                       {/* Individual Location Trial Details */}
                       <div className="mb-4">
                         <h5 className="text-xs font-semibold text-gray-700 mb-2">Individual Location Trial Details</h5>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                           <div>
                             <p className="font-medium text-gray-600">Experimental Area</p>
                             <p className="text-gray-900">2.5 ha</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Plot Size</p>
                             <p className="text-gray-900">25 m²</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Soil Type</p>
                             <p className="text-gray-900">Clay Loam</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Soil Test Date</p>
                             <p className="text-gray-900">2024-08-15</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Soil Test Details</p>
                             <p className="text-gray-900">pH: 6.8, N: 280 kg/ha, P: 45 kg/ha, K: 320 kg/ha</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Irrigation Source</p>
                             <p className="text-gray-900">Canal Irrigation</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Previous Crop</p>
                             <p className="text-gray-900">Paddy</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Seed Treatment</p>
                             <p className="text-gray-900">Carbendazim 2g/kg seed</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Ploughing</p>
                             <p className="text-gray-900">2 times disc ploughing</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Sowing Date</p>
                             <p className="text-gray-900">2024-06-15</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Planting Date</p>
                             <p className="text-gray-900">2024-06-20</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Basal Organic</p>
                             <p className="text-gray-900">FYM 5000 kg/ha (2024-06-10)</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Basal Fertilizer</p>
                             <p className="text-gray-900">NPK 20:20:20 120 kg/ha (2024-06-12)</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Top Dressing</p>
                             <p className="text-gray-900">Urea 60 kg/ha (2024-07-15)</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Weed Management</p>
                             <p className="text-gray-900">Hand weeding on 2024-07-20</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Plant Protection</p>
                             <p className="text-gray-900">Chlorpyriphos 2ml/lit on 2024-08-10</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Harvest Date</p>
                             <p className="text-gray-900">2024-10-15</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Monitoring Visit</p>
                             <p className="text-gray-900">2024-09-20 - Good progress observed</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Plot Officer Visit</p>
                             <p className="text-gray-900">2024-09-25 - Field conditions excellent</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Field Day</p>
                             <p className="text-gray-900">2024-10-10</p>
                           </div>
                           <div>
                             <p className="font-medium text-gray-600">Inspection Visit</p>
                             <p className="text-gray-900">2024-09-28 - Trial meeting standards</p>
                           </div>
                         </div>
                       </div>
         
                       <div className="mb-3">
                         <p className="text-xs font-medium text-gray-700 mb-2">Replication Data</p>
                         <div className="grid grid-cols-3 gap-2 text-xs">
                           <div className="bg-gray-50 p-2 rounded">
                             <p className="font-medium">R1</p>
                             <p>V1: 45.2</p>
                             <p>V2: 46.8</p>
                             <p>V3: 44.9</p>
                           </div>
                           <div className="bg-gray-50 p-2 rounded">
                             <p className="font-medium">R2</p>
                             <p>V1: 47.1</p>
                             <p>V2: 45.6</p>
                             <p>V3: 46.3</p>
                           </div>
                           <div className="bg-gray-50 p-2 rounded">
                             <p className="font-medium">R3</p>
                             <p>V1: 44.8</p>
                             <p>V2: 47.2</p>
                             <p>V3: 45.7</p>
                           </div>
                         </div>
                       </div>
         
                       <div className="mb-3">
                         <p className="text-xs font-medium text-gray-700 mb-2">Field Photos</p>
                         <div className="flex space-x-2">
                           <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                             <span className="text-xs text-gray-500">Photo 1</span>
                           </div>
                           <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                             <span className="text-xs text-gray-500">Photo 2</span>
                           </div>
                         </div>
                       </div>
         
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                         <div>
                           <p className="text-xs font-medium text-gray-700 mb-1">Farmer Feedback</p>
                           <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                             Plants are growing well with good height. Uniform growth observed across the field.
                           </p>
                         </div>
                         <div>
                           <p className="text-xs font-medium text-gray-700 mb-1">Officer Assessment</p>
                           <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                             Plant height measurements show consistent growth. No lodging observed. Continue monitoring for flowering stage.
                           </p>
                         </div>
                       </div>
                     </div>
         
                     <div className="text-center py-4">
                       <p className="text-sm text-gray-600">Total Entries: 2 | Last Updated: 2024-09-30</p>
                     </div>
                   </div>
                 </div>

               </div>

               <div className="flex justify-end pt-4">
                 <button
                   onClick={() => setShowAssignmentDetailModal(false)}
                   className="btn-secondary"
                 >
                   Close
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Edit Assignment Modal */}
       {showEditAssignmentModal && editingAssignment && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
             <div className="p-6">
               <h2 className="text-lg font-semibold text-gray-900 mb-4">
                 Edit Assignment: {editingAssignment.locationName}
               </h2>
               
               <form onSubmit={handleSubmit(handleEditAssignment)} className="space-y-6">
                 {/* Trial Details */}
                 <div className="bg-blue-50 p-4 rounded-lg">
                   <div className="flex items-center justify-between mb-3">
                     <h3 className="text-md font-semibold text-blue-900">Trial Details</h3>
                     <div className="flex space-x-2">
                       <button
                         type="button"
                        onClick={loadCrops}
                        disabled={isLoadingCrops}
                        className="p-1 text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                        title="Refresh crops"
                      >
                        <div className={`w-4 h-4 ${isLoadingCrops ? 'animate-spin' : ''}`}>
                          {isLoadingCrops ? (
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          ) : (
                            <Sprout className="w-4 h-4" />
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={loadSeasons}
                        disabled={isLoadingSeasons}
                        className="p-1 text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                        title="Refresh seasons"
                      >
                        <div className={`w-4 h-4 ${isLoadingSeasons ? 'animate-spin' : ''}`}>
                          {isLoadingSeasons ? (
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          ) : (
                            <Calendar className="w-4 h-4" />
                          )}
                        </div>
                      </button>
                    </div>
                  </div>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Culture/Treatment (Optional)</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.culture}
                         {...register('culture')}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.treatment}
                         {...register('treatment', { required: 'Treatment is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.treatment && <p className="text-red-500 text-xs mt-1">{errors.treatment.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Number of Replications</label>
                       <input
                         type="number"
                         min="1"
                         defaultValue={editingAssignment.replications}
                         {...register('replications', { required: 'Number of replications is required', min: { value: 1, message: 'Must be at least 1' } })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.replications && <p className="text-red-500 text-xs mt-1">{errors.replications.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Observations</label>
                       <select
                         multiple
                         defaultValue={editingAssignment.observations}
                         {...register('observations', { required: 'At least one observation is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       >
                         <option value="Germination %">Germination %</option>
                         <option value="Germination Vigour">Germination Vigour</option>
                         <option value="Population">Population</option>
                         <option value="Vigour">Vigour</option>
                         <option value="Germination DAS">Germination DAS</option>
                         <option value="Plant height">Plant height</option>
                         <option value="Plant width">Plant width</option>
                         <option value="No. of branches">No. of branches</option>
                       </select>
                       {errors.observations && <p className="text-red-500 text-xs mt-1">{errors.observations.message}</p>}
                     </div>
                   </div>
                 </div>

                 {/* Location Details */}
                 <div className="bg-green-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-green-900 mb-3">Location Details</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.locationName}
                         {...register('locationName', { required: 'Location name is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.locationName && <p className="text-red-500 text-xs mt-1">{errors.locationName.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Officer</label>
                       <select
                         defaultValue={editingAssignment.assignedOfficer}
                         {...register('assignedOfficer', { required: 'Assigned officer is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       >
                         <option value="">Select Officer</option>
                         {dynamicOfficers.map(officer => (
                           <option key={officer.id || officer} value={officer.id || officer}>{officer.name || officer}</option>
                         ))}
                       </select>
                       {errors.assignedOfficer && <p className="text-red-500 text-xs mt-1">{errors.assignedOfficer.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.latitude}
                         {...register('latitude', { required: 'Latitude is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.latitude && <p className="text-red-500 text-xs mt-1">{errors.latitude.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.longitude}
                         {...register('longitude', { required: 'Longitude is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.longitude && <p className="text-red-500 text-xs mt-1">{errors.longitude.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Altitude (m)</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.altitude}
                         {...register('altitude', { required: 'Altitude is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.altitude && <p className="text-red-500 text-xs mt-1">{errors.altitude.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">SF Number</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.sfNumber}
                         {...register('sfNumber', { required: 'SF Number is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.sfNumber && <p className="text-red-500 text-xs mt-1">{errors.sfNumber.message}</p>}
                     </div>
                     <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-1">Location Address</label>
                       <textarea
                         defaultValue={editingAssignment.locationAddress}
                         {...register('locationAddress', { required: 'Location address is required' })}
                         rows="3"
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.locationAddress && <p className="text-red-500 text-xs mt-1">{errors.locationAddress.message}</p>}
                     </div>
                   </div>
                 </div>



                 {/* Field Operations */}
                 <div className="bg-orange-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-orange-900 mb-3">Field Operations</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Seed Treatment Details</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.seedTreatment}
                         {...register('seedTreatment', { required: 'Seed treatment details are required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.seedTreatment && <p className="text-red-500 text-xs mt-1">{errors.seedTreatment.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Ploughing</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.ploughing}
                         {...register('ploughing', { required: 'Ploughing details are required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.ploughing && <p className="text-red-500 text-xs mt-1">{errors.ploughing.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Date of Sowing</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.sowingDate}
                         {...register('sowingDate', { required: 'Sowing date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.sowingDate && <p className="text-red-500 text-xs mt-1">{errors.sowingDate.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Date of Planting</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.plantingDate}
                         {...register('plantingDate', { required: 'Planting date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.plantingDate && <p className="text-red-500 text-xs mt-1">{errors.plantingDate.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Date of Harvest</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.harvestDate}
                         {...register('harvestDate', { required: 'Harvest date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.harvestDate && <p className="text-red-500 text-xs mt-1">{errors.harvestDate.message}</p>}
                     </div>
                   </div>
                 </div>

                 {/* Fertilizer Management */}
                 <div className="bg-purple-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-purple-900 mb-3">Fertilizer Management</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic Name</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.basalOrganic}
                         {...register('basalOrganic', { required: 'Basal organic name is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.basalOrganic && <p className="text-red-500 text-xs mt-1">{errors.basalOrganic.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic Date</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.basalOrganicDate}
                         {...register('basalOrganicDate', { required: 'Basal organic date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.basalOrganicDate && <p className="text-red-500 text-xs mt-1">{errors.basalOrganicDate.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Basal Organic Quantity (kg/ha)</label>
                       <input
                         type="number"
                         defaultValue={editingAssignment.basalOrganicQuantity}
                         {...register('basalOrganicQuantity', { required: 'Basal organic quantity is required', min: { value: 0, message: 'Must be at least 0' } })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.basalOrganicQuantity && <p className="text-red-500 text-xs mt-1">{errors.basalOrganicQuantity.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Date</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.basalFertilizerDate}
                         {...register('basalFertilizerDate', { required: 'Basal fertilizer date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.basalFertilizerDate && <p className="text-red-500 text-xs mt-1">{errors.basalFertilizerDate.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Name</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.basalFertilizerName}
                         {...register('basalFertilizerName', { required: 'Basal fertilizer name is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.basalFertilizerName && <p className="text-red-500 text-xs mt-1">{errors.basalFertilizerName.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Quantity (kg/ha)</label>
                       <input
                         type="number"
                         defaultValue={editingAssignment.basalFertilizerQuantity}
                         {...register('basalFertilizerQuantity', { required: 'Basal fertilizer quantity is required', min: { value: 0, message: 'Must be at least 0' } })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.basalFertilizerQuantity && <p className="text-red-500 text-xs mt-1">{errors.basalFertilizerQuantity.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Date</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.topDressingDate}
                         {...register('topDressingDate', { required: 'Top dressing date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.topDressingDate && <p className="text-red-500 text-xs mt-1">{errors.topDressingDate.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Name</label>
                       <input
                         type="text"
                         defaultValue={editingAssignment.topDressingName}
                         {...register('topDressingName', { required: 'Top dressing name is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.topDressingName && <p className="text-red-500 text-xs mt-1">{errors.topDressingName.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Top Dressing Quantity (kg/ha)</label>
                       <input
                         type="number"
                         defaultValue={editingAssignment.topDressingQuantity}
                         {...register('topDressingQuantity', { required: 'Top dressing quantity is required', min: { value: 0, message: 'Must be at least 0' } })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.topDressingQuantity && <p className="text-red-500 text-xs mt-1">{errors.topDressingQuantity.message}</p>}
                     </div>
                   </div>
                 </div>

                 {/* Crop Protection */}
                 <div className="bg-red-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-red-900 mb-3">Crop Protection</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-1">Weed Management Details</label>
                       <textarea
                         defaultValue={editingAssignment.weedManagement}
                         {...register('weedManagement', { required: 'Weed management details are required' })}
                         rows="3"
                         placeholder="Hand weeding / chemical quantity and date"
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.weedManagement && <p className="text-red-500 text-xs mt-1">{errors.weedManagement.message}</p>}
                     </div>
                     <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-1">Plant Protection Details</label>
                       <textarea
                         defaultValue={editingAssignment.plantProtection}
                         {...register('plantProtection', { required: 'Plant protection details are required' })}
                         rows="3"
                         placeholder="Chemical, dose & date"
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.plantProtection && <p className="text-red-500 text-xs mt-1">{errors.plantProtection.message}</p>}
                     </div>
                   </div>
                 </div>

                 {/* Monitoring & Visits */}
                 <div className="bg-indigo-50 p-4 rounded-lg">
                   <h3 className="text-md font-semibold text-indigo-900 mb-3">Monitoring & Visits</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Monitoring Team Visit Date</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.monitoringVisitDate}
                         {...register('monitoringVisitDate', { required: 'Monitoring visit date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.monitoringVisitDate && <p className="text-red-500 text-xs mt-1">{errors.monitoringVisitDate.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Monitoring Comments</label>
                       <textarea
                         defaultValue={editingAssignment.monitoringComments}
                         {...register('monitoringComments', { required: 'Monitoring comments are required' })}
                         rows="2"
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.monitoringComments && <p className="text-red-500 text-xs mt-1">{errors.monitoringComments.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Plot Officer Visit Date</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.plotOfficerVisitDate}
                         {...register('plotOfficerVisitDate', { required: 'Plot officer visit date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.plotOfficerVisitDate && <p className="text-red-500 text-xs mt-1">{errors.plotOfficerVisitDate.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Plot Officer Comments</label>
                       <textarea
                         defaultValue={editingAssignment.plotOfficerComments}
                         {...register('plotOfficerComments', { required: 'Plot officer comments are required' })}
                         rows="2"
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.plotOfficerComments && <p className="text-red-500 text-xs mt-1">{errors.plotOfficerComments.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Officer Visit Date</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.inspectionVisitDate}
                         {...register('inspectionVisitDate', { required: 'Inspection visit date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.inspectionVisitDate && <p className="text-red-500 text-xs mt-1">{errors.inspectionVisitDate.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Comments</label>
                       <textarea
                         defaultValue={editingAssignment.inspectionComments}
                         {...register('inspectionComments', { required: 'Inspection comments are required' })}
                         rows="2"
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.inspectionComments && <p className="text-red-500 text-xs mt-1">{errors.inspectionComments.message}</p>}
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Field Day Date</label>
                       <input
                         type="date"
                         defaultValue={editingAssignment.fieldDayDate}
                         {...register('fieldDayDate', { required: 'Field day date is required' })}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.fieldDayDate && <p className="text-red-500 text-xs mt-1">{errors.fieldDayDate.message}</p>}
                     </div>
                     <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-1">Farmers Feedback</label>
                       <textarea
                         defaultValue={editingAssignment.farmersFeedback}
                         {...register('farmersFeedback', { required: 'Farmers feedback is required' })}
                         rows="3"
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                       />
                       {errors.farmersFeedback && <p className="text-red-500 text-xs mt-1">{errors.farmersFeedback.message}</p>}
                     </div>
                   </div>
                 </div>

                 <div className="flex justify-end space-x-3 pt-4">
                   <button
                     type="button"
                     onClick={() => {
                       setShowEditAssignmentModal(false);
                       setEditingAssignment(null);
                       reset();
                     }}
                     className="btn-secondary"
                   >
                     Cancel
                   </button>
                   <button
                     type="submit"
                     disabled={isLoading}
                     className="btn-primary"
                   >
                     {isLoading ? 'Updating...' : 'Update Assignment'}
                   </button>
                 </div>
               </form>
             </div>
           </div>
         </div>
       )}

      {/* Add Location Modal */}
      {showAddLocationModal && addLocationContext.trial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Add Location to Centre: {addLocationContext.centre}
              </h2>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="text-md font-semibold text-blue-900 mb-3">Trial Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Trial Code</p>
                    <p className="font-mono text-sm">{addLocationContext.trial.code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Trial Type</p>
                    <p className="text-sm">{addLocationContext.trial.trial_type || addLocationContext.trial.type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Crop</p>
                    <p className="text-sm">{addLocationContext.trial.crop_name || addLocationContext.trial.crop}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Centre</p>
                    <p className="text-sm">{addLocationContext.centre}</p>
                  </div>
                  {centreAssignmentForAddLocation && (
                    <>
                      <div>
                        <p className="text-sm text-gray-500">Replications</p>
                        <p className="text-sm">{centreAssignmentForAddLocation.replications}</p>
                      </div>
                      <div className="lg:col-span-3">
                        <p className="text-sm text-gray-500">Observations</p>
                        <p className="text-sm">{formatObservationsWithDays(centreAssignmentForAddLocation.observations || [])}</p>
                      </div>
                    </>
                  )}
                </div>
                {centreAssignmentForAddLocation && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-500">Culture/Treatment</p>
                    <p className="text-sm">{centreAssignmentForAddLocation.culture || '-'}</p>
                  </div>
                )}
              </div>

              {/* Collapsible Location Details */}
              <div className="bg-green-50 p-4 rounded-lg mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-md font-semibold text-green-900">Location Details</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddLocLocationDetails(prev => !prev)}
                    className="p-2 rounded hover:bg-green-100"
                    aria-label="Toggle location details"
                  >
                    {showAddLocLocationDetails ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </button>
                </div>
                {showAddLocLocationDetails && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Location Name *</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="e.g., Research Station"
                        value={newLocationData.locationName}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, locationName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Location Incharge *</label>
                      <select
                        value={newLocationData.locationIncharge}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, locationIncharge: e.target.value }))}
                        className="input-field"
                      >
                        <option value="">Select Location Incharge</option>
                        {officers.map((officer) => (
                          <option key={officer} value={officer}>{officer}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">SF Number</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="SF001"
                        value={newLocationData.sfNumber}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, sfNumber: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Latitude</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="11.0168"
                        value={newLocationData.latitude}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, latitude: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Longitude</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="76.9558"
                        value={newLocationData.longitude}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, longitude: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Location Address</label>
                      <textarea 
                        rows="2" 
                        className="input-field" 
                        placeholder="Complete address of the location"
                        value={newLocationData.locationAddress}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, locationAddress: e.target.value }))}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Collapsible Individual Location Details */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-md font-semibold text-orange-900">Individual Location Trial Details</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddLocIndividualDetails(prev => !prev)}
                    className="p-2 rounded hover:bg-orange-100"
                    aria-label="Toggle individual location details"
                  >
                    {showAddLocIndividualDetails ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </button>
                </div>
                {showAddLocIndividualDetails && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Experimental Area (ha)</label>
                      <input 
                        type="number" 
                        className="input-field"
                        placeholder="e.g., 2.5"
                        value={newLocationData.experimentalArea}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, experimentalArea: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Plot Size (m²)</label>
                      <input 
                        type="number" 
                        className="input-field" 
                        placeholder="e.g., 25"
                        value={newLocationData.plotSize}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, plotSize: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Soil Type</label>
                      <select 
                        className="input-field"
                        value={newLocationData.soilType}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, soilType: e.target.value }))}
                      >
                        <option value="">Select Soil Type</option>
                        {soilTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Soil Test Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.soilTestDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, soilTestDate: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Soil Test Details</label>
                      <textarea 
                        rows="2" 
                        className="input-field" 
                        placeholder="pH, NPK values, etc."
                        value={newLocationData.soilTestDetails}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, soilTestDetails: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Irrigation Source</label>
                      <select 
                        className="input-field"
                        value={newLocationData.irrigationSource}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, irrigationSource: e.target.value }))}
                      >
                        <option value="">Select Irrigation Source</option>
                        {irrigationSources.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Previous Crop</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="e.g., Paddy"
                        value={newLocationData.previousCrop}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, previousCrop: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Seed Treatment</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="e.g., Thiram @ 2g/kg seed"
                        value={newLocationData.seedTreatment}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, seedTreatment: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Ploughing</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="e.g., 2 times disc ploughing"
                        value={newLocationData.ploughing}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, ploughing: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Sowing Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.sowingDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, sowingDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Planting Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.plantingDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, plantingDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Basal Organic Name</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="e.g., FYM"
                        value={newLocationData.basalOrganic}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, basalOrganic: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Basal Organic Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.basalOrganicDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, basalOrganicDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Basal Organic Quantity (kg/ha)</label>
                      <input 
                        type="number" 
                        className="input-field" 
                        placeholder="e.g., 5000"
                        value={newLocationData.basalOrganicQuantity}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, basalOrganicQuantity: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.basalFertilizerDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, basalFertilizerDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Name</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="e.g., NPK 20:20:20"
                        value={newLocationData.basalFertilizerName}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, basalFertilizerName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Basal Fertilizer Quantity (kg/ha)</label>
                      <input 
                        type="number" 
                        className="input-field" 
                        placeholder="e.g., 120"
                        value={newLocationData.basalFertilizerQuantity}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, basalFertilizerQuantity: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Top Dressing Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.topDressingDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, topDressingDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Top Dressing Name</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="e.g., Urea"
                        value={newLocationData.topDressingName}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, topDressingName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Top Dressing Quantity (kg/ha)</label>
                      <input 
                        type="number" 
                        className="input-field" 
                        placeholder="e.g., 60"
                        value={newLocationData.topDressingQuantity}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, topDressingQuantity: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Weed Management Details</label>
                      <textarea 
                        rows="2" 
                        className="input-field" 
                        placeholder="Hand weeding / chemical quantity and date"
                        value={newLocationData.weedManagement}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, weedManagement: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Plant Protection Details</label>
                      <textarea 
                        rows="2" 
                        className="input-field" 
                        placeholder="Chemical, dose & date"
                        value={newLocationData.plantProtection}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, plantProtection: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Harvest Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.harvestDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, harvestDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Monitoring Team Visit Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.monitoringVisitDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, monitoringVisitDate: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Monitoring Comments</label>
                      <textarea 
                        rows="2" 
                        className="input-field"
                        value={newLocationData.monitoringComments}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, monitoringComments: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Plot Officer Visit Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.plotOfficerVisitDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, plotOfficerVisitDate: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Plot Officer Comments</label>
                      <textarea 
                        rows="2" 
                        className="input-field"
                        value={newLocationData.plotOfficerComments}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, plotOfficerComments: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Inspection Officer Visit Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.inspectionVisitDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, inspectionVisitDate: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Inspection Comments</label>
                      <textarea 
                        rows="2" 
                        className="input-field"
                        value={newLocationData.inspectionComments}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, inspectionComments: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1">Field Day Date</label>
                      <input 
                        type="date" 
                        className="input-field"
                        value={newLocationData.fieldDayDate}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, fieldDayDate: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Farmers Feedback</label>
                      <textarea 
                        rows="2" 
                        className="input-field"
                        value={newLocationData.farmersFeedback}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, farmersFeedback: e.target.value }))}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4 space-x-3">
                <button onClick={() => setShowAddLocationModal(false)} className="btn-secondary">Close</button>
                <button onClick={handleSaveLocation} className="btn-primary">Save Location</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Centre Modal */}
      {showEditCentreModal && selectedCentre && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 flex-shrink-0">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Edit Centre: {selectedCentre.locationName}
                </h2>
                <button
                  onClick={() => setShowEditCentreModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
              
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              <div className="space-y-6">
                {/* Centre Name - Display Only */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Centre Name</label>
                  <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900">
                    {selectedCentre.locationName || selectedCentre.centre_name || 'N/A'}
                  </div>
                </div>

                {/* Centre Incharge Validity Details */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="text-md font-medium text-blue-900 mb-4 flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Centre Incharge
                  </h4>
                  
                  {/* Add New Validity Period */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        Centre Incharge
                        {isLoadingOfficers && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            Loading...
                          </span>
                        )}
                      </label>
                      <select
                        value={selectedCentre.centreInchargeId || selectedCentre.centreIncharge || selectedCentre.centre_incharge || ''}
                        onChange={(e) => {
                          console.log('🔄 Centre incharge changed to:', e.target.value);
                          // Find the officer name from the ID
                          const selectedOfficer = dynamicOfficers.find(officer => officer.id == e.target.value);
                          const officerName = selectedOfficer ? selectedOfficer.name : e.target.value;
                          
                          setSelectedCentre(prev => ({ 
                            ...prev, 
                            centreIncharge: officerName,
                            centre_incharge: officerName,
                            centreInchargeId: e.target.value
                          }));
                        }}
                        className="input-field border-2 focus:border-blue-500"
                        disabled={isLoadingOfficers}
                      >
                        <option value="">
                          {isLoadingOfficers ? 'Loading officers...' : 'Select Centre Incharge'}
                        </option>
                        {dynamicOfficers.length > 0 ? (
                          dynamicOfficers.map((officer) => (
                            <option key={officer.id || officer} value={officer.id || officer}>
                              {officer.name || officer}
                            </option>
                          ))
                        ) : (
                          officers.map((officer, index) => (
                            <option key={officer} value={officer}>{officer}</option>
                          ))
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                      <input
                        type="date"
                        value={centreInchargeValidityFrom}
                        onChange={(e) => setCentreInchargeValidityFrom(e.target.value)}
                        className="input-field border-2 focus:border-blue-500"
                        placeholder="Start date"
                      />
                    </div>
                  </div>
                  
                  {/* Add Validity Period Button */}
                  <div className="flex justify-end mb-4">
                    <button
                      type="button"
                      onClick={handleAddCentreInchargeAssignment}
                      className="btn-primary px-6"
                    >
                      Add
                    </button>
                  </div>
                  
                  {/* Centre Incharge Assignments Table */}
                  {isLoadingCentreInchargeAssignments ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-600 mt-2">Loading assignments...</p>
                    </div>
                  ) : centreInchargeAssignments.length > 0 ? (
                    <div className="bg-white rounded-md border border-blue-200 overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-blue-100">
                          <tr>
                            <th className="px-4 py-2 text-left text-gray-700">Centre Incharge</th>
                            <th className="px-4 py-2 text-left text-gray-700">Assignment Date</th>
                            <th className="px-4 py-2 text-left text-gray-700">Status</th>
                            <th className="px-4 py-2 text-left text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {centreInchargeAssignments.map((assignment) => (
                            <tr key={assignment.id} className="border-t">
                              <td className="px-4 py-2">{assignment.centre_incharge || assignment.assigned_officer || 'N/A'}</td>
                              <td className="px-4 py-2">
                                {assignment.assignment_date ? new Date(assignment.assignment_date).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  assignment.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {assignment.status === 'active' ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() => handleDeleteCentreInchargeAssignment(assignment.id)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                  title="Delete Assignment"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No centre incharge assignments found</p>
                    </div>
                  )}
                </div>
              </div>

              </div>
              
              {/* Action Buttons */}
            <div className="flex-shrink-0 p-6 pt-0">
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowEditCentreModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      
                      // Prepare update data
                      const updateData = {
                        centre_name: selectedCentre.centre_name || selectedCentre.locationName,
                        centre_code: selectedCentre.centre_code,
                        centre_incharge: selectedCentre.centre_incharge,
                        from_date: selectedCentre.from_date,
                        status: selectedCentre.status || 'active'
                      };
                      
                      // Call API to update trial centre
                      const response = await trialCentresService.updateTrialCentre(selectedCentre.id, updateData);
                      
                      if (response.success) {
                        toast.success('Centre updated successfully!');
                        
                        // Refresh trial centres data
                        await loadTrialCentres();
                        
                        // Close modal
                        setShowEditCentreModal(false);
                        setSelectedCentre(null);
                      } else {
                        throw new Error(response.message || 'Failed to update centre');
                      }
                    } catch (error) {
                      console.error('Error updating centre:', error);
                      toast.error(error.message || 'Failed to update centre');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Updating...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trial Details Modal */}
      {showTrialDetailsModal && selectedLocationForDetails && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Location Trial Details: {selectedLocationForDetails.location_name}
                    </h3>
                    <button
                      onClick={closeTrialDetailsModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                <div className="p-6">
                  {isLoadingTrialDetails ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      <span className="ml-2 text-gray-600">Loading trial details...</span>
                    </div>
                  ) : (
                    <>
                      {/* Two Column Layout */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column - Agricultural Practices */}
                      <div className="space-y-6">
                        <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                          Agricultural Practices
                        </h4>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Experimental Area (ha)
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter experimental area in hectares"
                              value={trialDetailsFormData.experimental_area_ha}
                              onChange={(e) => handleTrialDetailsChange('experimental_area_ha', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Plot Size (sq.m)
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter plot size in square meters"
                              value={trialDetailsFormData.plot_size_sqm}
                              onChange={(e) => handleTrialDetailsChange('plot_size_sqm', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Soil Type
                            </label>
                            <select 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              value={trialDetailsFormData.soil_type}
                              onChange={(e) => handleTrialDetailsChange('soil_type', e.target.value)}
                            >
                              <option value="">Select Soil Type</option>
                              <option value="Clay">Clay</option>
                              <option value="Sandy">Sandy</option>
                              <option value="Loamy">Loamy</option>
                              <option value="Silty">Silty</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Soil Test Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="dd-mm-yyyy"
                                value={trialDetailsFormData.soil_test_date}
                                onChange={(e) => handleTrialDetailsChange('soil_test_date', e.target.value)}
                              />
                              <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Soil Test Details
                            </label>
                            <textarea
                              rows="3"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter soil test details (pH, N, P, K values, etc.)"
                              value={trialDetailsFormData.soil_test_details}
                              onChange={(e) => handleTrialDetailsChange('soil_test_details', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Irrigation Source
                            </label>
                            <select 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              value={trialDetailsFormData.irrigation_source}
                              onChange={(e) => handleTrialDetailsChange('irrigation_source', e.target.value)}
                            >
                              <option value="">Select Irrigation Source</option>
                              <option value="Borewell">Borewell</option>
                              <option value="Canal">Canal</option>
                              <option value="Tank">Tank</option>
                              <option value="Rainfed">Rainfed</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Previous Crop
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter previous crop name"
                              value={trialDetailsFormData.previous_crop}
                              onChange={(e) => handleTrialDetailsChange('previous_crop', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right Column - Crop Management */}
                      <div className="space-y-6">
                        <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                          Crop Management
                        </h4>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Seed Treatment
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter seed treatment details"
                              value={trialDetailsFormData.seed_treatment}
                              onChange={(e) => handleTrialDetailsChange('seed_treatment', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Ploughing
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter ploughing details"
                              value={trialDetailsFormData.ploughing}
                              onChange={(e) => handleTrialDetailsChange('ploughing', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Sowing Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="dd-mm-yyyy"
                                value={trialDetailsFormData.sowing_date}
                                onChange={(e) => handleTrialDetailsChange('sowing_date', e.target.value)}
                              />
                              <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Planting Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="dd-mm-yyyy"
                                value={trialDetailsFormData.planting_date}
                                onChange={(e) => handleTrialDetailsChange('planting_date', e.target.value)}
                              />
                              <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Basal Organic
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter basal organic details"
                              value={trialDetailsFormData.basal_organic}
                              onChange={(e) => handleTrialDetailsChange('basal_organic', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Basal Organic Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="dd-mm-yyyy"
                                value={trialDetailsFormData.basal_organic_date}
                                onChange={(e) => handleTrialDetailsChange('basal_organic_date', e.target.value)}
                              />
                              <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Basal Organic Quantity (kg/ha)
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter quantity in kg/ha"
                              value={trialDetailsFormData.basal_organic_quantity_kg_ha}
                              onChange={(e) => handleTrialDetailsChange('basal_organic_quantity_kg_ha', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Second Row - Fertilizer Management and Crop Protection */}
                    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column - Fertilizer Management */}
                      <div className="space-y-6">
                        <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                          Fertilizer Management
                        </h4>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Basal Fertilizer Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="dd-mm-yyyy"
                                value={trialDetailsFormData.basal_fertilizer_date}
                                onChange={(e) => handleTrialDetailsChange('basal_fertilizer_date', e.target.value)}
                              />
                              <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Basal Fertilizer Name
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter fertilizer name"
                              value={trialDetailsFormData.basal_fertilizer_name}
                              onChange={(e) => handleTrialDetailsChange('basal_fertilizer_name', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Basal Fertilizer Quantity (kg/ha)
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter quantity in kg/ha"
                              value={trialDetailsFormData.basal_fertilizer_quantity_kg_ha}
                              onChange={(e) => handleTrialDetailsChange('basal_fertilizer_quantity_kg_ha', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Top Dressing Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="dd-mm-yyyy"
                                value={trialDetailsFormData.top_dressing_date}
                                onChange={(e) => handleTrialDetailsChange('top_dressing_date', e.target.value)}
                              />
                              <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Top Dressing Name
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter top dressing name"
                              value={trialDetailsFormData.top_dressing_name}
                              onChange={(e) => handleTrialDetailsChange('top_dressing_name', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Top Dressing Quantity (kg/ha)
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter quantity in kg/ha"
                              value={trialDetailsFormData.top_dressing_quantity_kg_ha}
                              onChange={(e) => handleTrialDetailsChange('top_dressing_quantity_kg_ha', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right Column - Crop Protection & Monitoring */}
                      <div className="space-y-6">
                        <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                          Crop Protection & Monitoring
                        </h4>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Weed Management
                            </label>
                            <textarea
                              rows="3"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter weed management details"
                              value={trialDetailsFormData.weed_management}
                              onChange={(e) => handleTrialDetailsChange('weed_management', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Plant Protection
                            </label>
                            <textarea
                              rows="3"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter plant protection details"
                              value={trialDetailsFormData.plant_protection}
                              onChange={(e) => handleTrialDetailsChange('plant_protection', e.target.value)}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Harvest Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="dd-mm-yyyy"
                                value={trialDetailsFormData.harvest_date}
                                onChange={(e) => handleTrialDetailsChange('harvest_date', e.target.value)}
                              />
                              <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Monitoring Visit Date
                            </label>
                            <div className="relative">
                              <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="dd-mm-yyyy"
                                value={trialDetailsFormData.monitoring_visit_date}
                                onChange={(e) => handleTrialDetailsChange('monitoring_visit_date', e.target.value)}
                              />
                              <Calendar className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Monitoring Comments
                            </label>
                            <textarea
                              rows="3"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Enter monitoring comments"
                              value={trialDetailsFormData.monitoring_comments}
                              onChange={(e) => handleTrialDetailsChange('monitoring_comments', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                      {/* Modal Actions */}
                      <div className="mt-8 flex justify-end space-x-4 pt-6 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={closeTrialDetailsModal}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={saveTrialDetails}
                          disabled={isSavingTrialDetails}
                          className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSavingTrialDetails ? 'Saving...' : 'Save Trial Details'}
                        </button>
                      </div>
                    </>
                  )}
                  </div>
                </div>
              </div>
            )}
    </div>
  );
};

export default TrialCreation; 